/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Imports API
 * Scrivio API for bulk data imports.  Currently supports validation of the ZPM GGZ import workbook: the caller uploads the filled-in `.xlsx` template and receives a validation report. Validation is read-only — nothing is written to any business table.  ## Authentication All endpoints require a Firebase Authentication JWT token in the `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * The EPD-dump run a reopened import screen should pick up: which delivery it is, where it has got to, and since when.  Deliberately without counts. A dump run\'s progress is what its *preview* describes, per entity type, and the only thing this endpoint has to answer is which run to go and look at — a second set of numbers here would be a second definition of how far an import is. 
 */
export interface ActiveDumpImportRun {
    /**
     * The run, as the initiate endpoint returned it to whoever started the upload
     */
    'runId': string;
    /**
     * Name of the uploaded archive, so the screen can say which delivery this is
     */
    'fileName': string;
    'state': ImportRunState;
    /**
     * When the run was opened - that is, when the upload was initiated
     */
    'startedAt': string;
}


/**
 * A single validation finding, attributed to a sheet and (where known) a row and column
 */
export interface AgendaFinding {
    /**
     * Sheet the finding applies to
     */
    'sheet': string;
    /**
     * Excel row number (1-based), or null for sheet-level findings
     */
    'row'?: number | null;
    /**
     * Column name, or null when the finding is not tied to one column
     */
    'column'?: string | null;
    'severity': ImportFindingSeverity;
    'code': AgendaFindingCode;
    /**
     * Human-readable explanation in Dutch
     */
    'message': string;
    /**
     * The offending cell value, or null when not applicable
     */
    'value'?: string | null;
}


/**
 * Machine-readable finding code. Grouped by rule family: structural (workbook shape), cell (per-value), resolution (koppeling aan Scrivio) and context (state of the agenda the appointments land in).  Whether a code blocks the import follows one rule: does the imported result differ from what the workbook says? Resolution failures, unreadable cells and a session whose participants only appear on one of the two sheets all do, and are ERROR. Everything under \"context\" describes the surroundings of a correctly imported appointment and is WARNING. 
 */

export const AgendaFindingCode = {
    SheetMissing: 'SHEET_MISSING',
    ColumnMissing: 'COLUMN_MISSING',
    ColumnOrderChanged: 'COLUMN_ORDER_CHANGED',
    RequiredFieldMissing: 'REQUIRED_FIELD_MISSING',
    InvalidDate: 'INVALID_DATE',
    InvalidTime: 'INVALID_TIME',
    EindVoorStart: 'EIND_VOOR_START',
    DagKomtNietOvereen: 'DAG_KOMT_NIET_OVEREEN',
    SessieOnvolledig: 'SESSIE_ONVOLLEDIG',
    BehandelaarNietGevonden: 'BEHANDELAAR_NIET_GEVONDEN',
    BehandelaarAmbigu: 'BEHANDELAAR_AMBIGU',
    BehandelaarZonderRol: 'BEHANDELAAR_ZONDER_ROL',
    ClientNietGevonden: 'CLIENT_NIET_GEVONDEN',
    ClientAmbigu: 'CLIENT_AMBIGU',
    AfspraaktypeNietGevonden: 'AFSPRAAKTYPE_NIET_GEVONDEN',
    DubbeleAfspraak: 'DUBBELE_AFSPRAAK',
    OverlappendeAfspraak: 'OVERLAPPENDE_AFSPRAAK',
    ClientGearchiveerd: 'CLIENT_GEARCHIVEERD',
    ClientZonderZorgtraject: 'CLIENT_ZONDER_ZORGTRAJECT',
    DuurWijktAf: 'DUUR_WIJKT_AF',
    GroepssamenstellingWijktAf: 'GROEPSSAMENSTELLING_WIJKT_AF'
} as const;

export type AgendaFindingCode = typeof AgendaFindingCode[keyof typeof AgendaFindingCode];


/**
 * Result of committing an agenda planning workbook: the run its appointments were staged under, and how many were staged, broken down per type as the workbook names it.  Staged is not yet created, and not yet approved either — the run waits for its preview to be approved, and only then does the scheduled worker turn the staged rows into appointments (ADR-039). `runId` is what a caller previews, decides, and then follows to see how far the import got. 
 */
export interface AgendaImportResult {
    /**
     * The import run the appointments were staged under
     */
    'runId': string;
    /**
     * Name of the committed workbook
     */
    'fileName': string;
    /**
     * Per-type counts, one entry per appointment type in the workbook
     */
    'types': Array<AgendaImportTypeResult>;
    /**
     * Total number of appointments staged, awaiting approval of the run
     */
    'totalStaged': number;
    /**
     * Total number of appointments skipped because an identical appointment from an earlier run of this import already exists. 
     */
    'totalSkipped': number;
}
/**
 * One appointment a revert would leave standing
 */
export interface AgendaImportRevertBlockedAppointment {
    /**
     * Title of the appointment as it stands in the agenda
     */
    'title': string;
    /**
     * Start time of the appointment (milliseconds since epoch)
     */
    'startTime': number;
}
/**
 * One reason a revert leaves appointments standing, how many carry it, and a few of them by name
 */
export interface AgendaImportRevertBlockedGroup {
    /**
     * Dutch sentence naming the work that blocks removal, fit to show a practice
     */
    'reason': string;
    /**
     * Appointments blocked by this reason
     */
    'count': number;
    /**
     * Some of the blocked appointments, oldest first. Capped: a run whose every appointment blocks must not hand a screen thousands of rows, and `count` stays the real total. 
     */
    'examples': Array<AgendaImportRevertBlockedAppointment>;
}
/**
 * What reverting one run would do, counted before anything is started. Every appointment the run created falls into exactly one of `removable`, `blocked` and `alreadyRemoved`; `pending` is the rows that were never created and would be withdrawn. 
 */
export interface AgendaImportRevertPreflight {
    /**
     * The run the numbers are about
     */
    'runId': string;
    /**
     * Appointments the revert would remove
     */
    'removable': number;
    /**
     * Appointments the revert would leave standing because they carry work the import did not create. The real total, whatever the examples below list. 
     */
    'blocked': number;
    /**
     * Appointments the run created that have since been removed by hand; the revert has nothing to do about these
     */
    'alreadyRemoved': number;
    /**
     * Rows still queued to be created, which the revert would withdraw instead
     */
    'pending': number;
    /**
     * The blocked appointments grouped by what stops them, largest group first
     */
    'blockedByReason': Array<AgendaImportRevertBlockedGroup>;
}
/**
 * One staged appointment the worker could not create, and why
 */
export interface AgendaImportRunFailure {
    /**
     * Title the appointment would have been created with
     */
    'title': string;
    /**
     * Start time of the planned appointment (milliseconds since epoch)
     */
    'startTime': number;
    /**
     * Why the appointment could not be created, as the write refused it
     */
    'reason': string;
}
/**
 * How far an agenda import run got: what it staged, what the worker has created, and what it could not create.  A run is over once `finishedAt` is set, whatever its `state` — a run that finished with rows it could not apply did create the rest, which is why `FAILED` still carries an `applied` count. The same shape follows a revert: `state` says it is being taken back, and `reverted` / `revertBlocked` say how far that got. 
 */
export interface AgendaImportRunStatus {
    /**
     * The run, as the commit endpoint returned it
     */
    'runId': string;
    /**
     * Name of the committed workbook
     */
    'fileName': string;
    'state': ImportRunState;
    /**
     * When the workbook was committed
     */
    'startedAt': string;
    /**
     * When the last row was applied; absent while the run is still being applied
     */
    'finishedAt'?: string | null;
    /**
     * Appointments the approval released to the worker; fixed for the life of the run. `applied + failed` counts up to exactly this, which is what makes it the denominator the progress bar reads. 
     */
    'total': number;
    /**
     * Staged appointments the worker has created so far
     */
    'applied': number;
    /**
     * Staged appointments the worker could not create; each carries its own reason
     */
    'failed': number;
    /**
     * Appointments a revert has removed, or found already gone; 0 for a run never reverted
     */
    'reverted': number;
    /**
     * Appointments a revert refused to remove because they carry work the import did not create. A reverted run is not necessarily one that left nothing behind. 
     */
    'revertBlocked': number;
    /**
     * The rows that could not be created, oldest appointment first. Capped, so a run that failed wholesale does not answer with thousands of rows: `failed` is the real total and is what the caller counts on. 
     */
    'failures': Array<AgendaImportRunFailure>;
}


/**
 * One agenda import run as the history lists it: what was uploaded, when, how it ended and what a revert has since done to it.  Deliberately without the per-row failures the run endpoint carries — a list of runs would otherwise answer with every failed row of every import the practice ever did. 
 */
export interface AgendaImportRunSummary {
    /**
     * The run, to follow it or to take it back
     */
    'runId': string;
    /**
     * Name of the committed workbook
     */
    'fileName': string;
    'state': ImportRunState;
    /**
     * When the workbook was committed
     */
    'startedAt': string;
    /**
     * When the last row was applied; absent while the run is still being applied
     */
    'finishedAt'?: string | null;
    /**
     * Appointments the approval released to the worker; fixed for the life of the run. `applied + failed` counts up to exactly this, which is what makes it the denominator the progress bar reads. 
     */
    'total': number;
    /**
     * Appointments the run created. Survives a revert: how many this import wrote stays true however many of them were taken back afterwards. 
     */
    'applied': number;
    /**
     * Staged appointments the worker could not create
     */
    'failed': number;
    /**
     * Appointments a revert has removed, or found already gone; 0 for a run never reverted
     */
    'reverted': number;
    /**
     * Appointments a revert refused to remove because they carry work the import did not create. A reverted run is not necessarily one that left nothing behind. 
     */
    'revertBlocked': number;
}


/**
 * Staged/skipped counts for a single appointment type
 */
export interface AgendaImportTypeResult {
    /**
     * Type as it appears in the workbook
     */
    'type': string;
    /**
     * Number of appointments staged for this type
     */
    'staged': number;
    /**
     * Number of appointments skipped for this type
     */
    'skipped': number;
}
/**
 * Row and finding counts for a single sheet
 */
export interface AgendaSheetSummary {
    /**
     * Sheet name as it appears in the workbook
     */
    'sheet': string;
    /**
     * Number of data rows read
     */
    'rows': number;
    /**
     * Number of ERROR findings on this sheet
     */
    'errors': number;
    /**
     * Number of WARNING findings on this sheet
     */
    'warnings': number;
}
/**
 * Result of validating an agenda planning workbook
 */
export interface AgendaValidationReport {
    /**
     * Name of the uploaded workbook
     */
    'fileName': string;
    'summary': AgendaValidationSummary;
    /**
     * All findings, ordered by sheet and row
     */
    'findings': Array<AgendaFinding>;
}
/**
 * Aggregated counts over the whole workbook
 */
export interface AgendaValidationSummary {
    /**
     * Per-sheet counts, one entry per validated sheet
     */
    'sheets': Array<AgendaSheetSummary>;
    /**
     * How many WARNING findings each code accounts for, most frequent first; codes without a warning are absent. A revised planning lands next to an agenda that already holds the appointments it revises, so hundreds of warnings over 1.807 appointments is the normal case rather than the exception — this breakdown is what makes that surveyable before reading the list itself. 
     */
    'warningsByCode': Array<AgendaWarningCount>;
    /**
     * Number of distinct appointments the two sheets merge into. Lower than the sum of the sheet row counts: a group session appears once per co-therapist on one sheet and once per attendee on the other. 
     */
    'appointments': number;
    /**
     * Total number of ERROR findings across all sheets
     */
    'totalErrors': number;
    /**
     * Total number of WARNING findings across all sheets
     */
    'totalWarnings': number;
    /**
     * True when the workbook has no ERROR findings. WARNING findings do not affect this: a duplicate or overlapping booking still imports. 
     */
    'readyToImport': boolean;
}
/**
 * The number of WARNING findings carrying one finding code
 */
export interface AgendaWarningCount {
    'code': AgendaFindingCode;
    /**
     * Number of WARNING findings with this code
     */
    'count': number;
}


/**
 * A screenful of answers, applied together. 
 */
export interface AnswerImportFieldsRequest {
    /**
     * The answers. Capped because this is one screen\'s worth: a request larger than the page it was composed from is a request nobody read before sending. 
     */
    'answers': Array<ImportFieldAnswerInput>;
}
/**
 * The one thing an approval can say beyond \"go ahead\": that the practice has read the preview and accepts that the rows carrying a blocking finding will not be imported (#4051).  Stating `skipBlockedRows` **is** the acceptance. Leaving it out leaves the default refusal exactly as it was — a run with any blocking finding answers `422`.  The property is deliberately **not** required, and the body itself is optional, so the two spellings of \"this approval says nothing about the blocked rows\" — an absent body and a `{}` — mean the same thing. They have to: a generated client whose serialiser materialises an omitted body as `{}` cannot express an absent one, which made every approval from this frontend a `400` (#4098). 
 */
export interface ApproveImportRunRequest {
    /**
     * How many staged rows the caller accepts will not be imported: the sum of `blockedRows` over the preview\'s entities, as it was shown to them.  A number rather than a flag, and it must match what the run now holds or the approval answers `409`. A flag would let a screen approve a preview nobody has read; this is a statement about a delivery, and one that stops agreeing the moment the delivery does.  `0` is a real answer: a run whose blocking findings all describe records that could not be staged at all has no row to skip, and accepting that they will not arrive is still a decision the practice takes deliberately. Absent or `null` is the opposite answer — the approval accepts nothing, and a blocking finding still refuses it. 
     */
    'skipBlockedRows'?: number;
}
/**
 * Billing category defining how an appointment type is billed: - INSURED: Billed through VECOZO to health insurers (requires consultType) - PRIVATE: Billed directly to the client (requires privatePriceEuro) - NON_BILLABLE: Not billed at all (e.g., internal meetings, admin time) 
 */

export const BillingCategory = {
    Insured: 'INSURED',
    Private: 'PRIVATE',
    NonBillable: 'NON_BILLABLE'
} as const;

export type BillingCategory = typeof BillingCategory[keyof typeof BillingCategory];


/**
 * What loading one insurer tariff sheet onto one contract would do, before any tariff moves.  Every number is derived **live**, by comparing the run\'s staged lines with the contract\'s price rules as they now stand — nothing about the comparison is stored. That is what makes one shape answer two questions: before the run is approved it says what will happen, and after it has been applied it says what did, with every line the run wrote reporting `unchanged`. Re-uploading the same sheet reports the same way, which is how a practice tells a correction from a repeat. 
 */
export interface ContractTariffImportPreview {
    /**
     * The staged run - what `GET /api/v1/imports/{runId}/preview` and `POST .../approve` name
     */
    'runId': string;
    /**
     * The contract these tariffs were staged for
     */
    'contractId': string;
    /**
     * The uploaded sheet, as the practice knows it
     */
    'fileName': string;
    'status': ImportRunState;
    'startedAt': string;
    /**
     * Lines below the header naming a prestatiecode - the number a practice checks against the row count of their own sheet. It is the sum of the five outcomes below. 
     */
    'linesRead': number;
    /**
     * Lines pricing a prestatiecode the contract does not price yet
     */
    'created': number;
    /**
     * Lines pricing a prestatiecode the contract prices at another amount
     */
    'updated': number;
    /**
     * Lines the contract already prices at exactly this amount. Applying such a line writes nothing at all, which is what makes applying the same sheet twice idempotent. 
     */
    'unchanged': number;
    /**
     * Lines naming a prestatiecode Scrivio\'s catalogue does not carry yet - the NZa codetabel has more families than `service_codes` was seeded from (#4049). Staged all the same, each with a Dutch reason naming the code and which kind of prestatie it is, so the preview can say which - a row dropped during parsing never reaches a preview at all. 
     */
    'rejected': number;
    /**
     * Lines the reader could make no row of - an unreadable or non-positive tariff, or a prestatiecode the sheet names twice. Each raises a warning naming its row. 
     */
    'skipped': number;
    /**
     * Prestatiecodes the contract already prices that this sheet does not mention. Left exactly as they are - counted because a silent omission is how half a tariff list gets loaded. 
     */
    'notMentioned': number;
    /**
     * Lines whose own validity falls outside the contract\'s `validFrom`/`validUntil`. The price rule carries no period of its own, so this is the only place a 2026 sheet loaded onto a contract that ran to 2025 is ever said out loud. 
     */
    'outsideContractValidity': number;
    /**
     * WARNING findings on this run; read them through the preview drilldown
     */
    'warnings': number;
}


/**
 * Records of one entity type a revert would leave standing for one reason
 */
export interface DumpImportRevertBlockedGroup {
    'entityType': ImportEntityType;
    /**
     * The Dutch sentence the revert would store on these rows
     */
    'reason': string;
    /**
     * How many records stay for this reason; the real total, whatever `examples` holds
     */
    'count': number;
    /**
     * The records themselves as the drilldown describes them, led by the cliënt; at most 100
     */
    'examples': Array<string>;
}


/**
 * What a revert would take back of one entity type
 */
export interface DumpImportRevertEntityCount {
    'entityType': ImportEntityType;
    /**
     * Rows of this type the revert would try to take back; 0 when the run applied none
     */
    'applied': number;
    /**
     * Rows of this type that were never applied and would be withdrawn instead
     */
    'unapplied': number;
}


/**
 * How much of an EPD delivery a revert would take back, counted before anything is started.  A staged row is in exactly one of the two counts, or in neither: `applied` is what the revert would try to take back, `unapplied` is what it would withdraw without ever having created it, and a row that is already reverted, withdrawn or failed is in neither because a revert has nothing to do about it.  Whether a record can really be removed is decided per row by the removers. For a delivery small enough to ask them in one request `blockedAssessed` is true and `blocked` names what would stay. For the rest it is false and `blocked` is empty: what the revert left is then read off the run while it proceeds — the drilldown\'s `REVERT_BLOCKED` rows, each with its own reason. A second, cheaper estimate could disagree with the verdict acted on, so there is none. 
 */
export interface DumpImportRevertPreflight {
    /**
     * The run the numbers are about
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * Zorgtrajecten of the practice\'s own that this import closed and **the revert will not reopen** (#4356).  The one thing a revert cannot take back. Reopening is not merely declined: in the case that matters the imported zorgtraject is blocked from removal because it holds the cliënt\'s re-parented afspraken, so both would be ACTIEF and `uk_client_location_active_zorgtraject` would refuse it. The practice answered `SUPERSEDE` about each of these before the import ran, having been told exactly this — and stating the number here is what keeps a revert from reporting a clean undo over records it is leaving changed.  Counted on the zorgtrajecten rather than on the rows, and excluding the ones this run created itself: two imported zorgtrajecten of one cliënt close one existing record between them, and the second closes the first — which the revert does take back. 
     */
    'supersededNotReopened': number;
    /**
     * Rows over every entity type the revert would try to take back. The real total, and what a practice is deciding on. 
     */
    'applied': number;
    /**
     * Rows over every entity type that were never applied and would be withdrawn instead
     */
    'unapplied': number;
    /**
     * The same two counts per entity type, in apply order. Every type is present, including the ones this run staged nothing for — an absent type would be indistinguishable from one the reader forgot to look for. 
     */
    'entities': Array<DumpImportRevertEntityCount>;
    /**
     * Whether `blocked` was answered for this run, rather than left to the revert itself
     */
    'blockedAssessed': boolean;
    /**
     * What the revert would leave standing, per entity type and reason, latest type first
     */
    'blocked': Array<DumpImportRevertBlockedGroup>;
}


/**
 * One EPD-dump run as the history lists it: which archive was delivered, when, how it ended and what a revert has since done to it.  Deliberately without per-row failures. A list of runs that carried them would answer with every failed row of every delivery the practice ever made, and a dump stages some 40.000 rows; what a run staged is described per entity type by its preview (`GET /api/v1/imports/{runId}/preview/{entityType}`), which is where a failed row is read. 
 */
export interface DumpImportRunSummary {
    /**
     * The run, to read its preview, to follow it or to take it back
     */
    'runId': string;
    /**
     * Name of the uploaded archive, so the practice can say which delivery this is
     */
    'fileName': string;
    'state': ImportRunState;
    /**
     * When the run was opened - that is, when the upload was initiated
     */
    'startedAt': string;
    /**
     * When the last row was applied; absent while the run is still being applied
     */
    'finishedAt'?: string | null;
    /**
     * Rows the approval released to the worker, over every entity type; fixed for the life of the run. Not what the delivery *staged*: an activity type or a practitioner name skipped from the preview withdraws staged rows, and `applied + failed` could never reach a total that counted those. A rejected run carries the rows it would have released. 
     */
    'total': number;
    /**
     * Rows the run wrote. Survives a revert: how many this delivery wrote stays true however many of them were taken back afterwards. 
     */
    'applied': number;
    /**
     * Staged rows the worker could not write
     */
    'failed': number;
    /**
     * Records a revert has removed, or found already gone; 0 for a run never reverted
     */
    'reverted': number;
    /**
     * Records a revert refused to remove because they carry work the import did not create. A reverted run is not necessarily one that left nothing behind. 
     */
    'revertBlocked': number;
}


/**
 * One appointment type an activity type may be mapped to. 
 */
export interface ImportAppointmentTypeCandidate {
    /**
     * The Scrivio appointment type
     */
    'id': string;
    /**
     * How the practice sees it
     */
    'name': string;
    'billingCategory': BillingCategory;
    /**
     * Whether the organization still offers this type for new appointments. Inactive types are offered anyway: a practice migrating years of history routinely has a type it has stopped using and still needs for the old sessions. 
     */
    'isActive': boolean;
}


/**
 * What a practice answers about one activity type of a delivery.  There is a third state and it is deliberately not a value here: **no answer at all**, which is what an activity type nobody has reached yet is in, and what refuses an approval. It is reported as `UNRESOLVED` on `ImportAppointmentTypeState` and cannot be *sent* — you cannot un-decide a type, you re-decide it. 
 */

export const ImportAppointmentTypeDecision = {
    Mapped: 'MAPPED',
    Skipped: 'SKIPPED'
} as const;

export type ImportAppointmentTypeDecision = typeof ImportAppointmentTypeDecision[keyof typeof ImportAppointmentTypeDecision];


/**
 * One activity type of the delivery, with what it accounts for and what has been answered. 
 */
export interface ImportAppointmentTypeEntry {
    /**
     * The activity type exactly as the source spells it
     */
    'sourceType': string;
    /**
     * Appointments of the delivery carrying this type, counted when the inventory was derived — appointments and not source rows, and a property of the delivery, so the number does not move as the practice answers. 
     */
    'appointments': number;
    'state': ImportAppointmentTypeState;
    /**
     * The appointment type this was mapped to; absent unless the state is `MAPPED`
     */
    'appointmentTypeId'?: string | null;
    /**
     * That type\'s name, so the list reads without a second request
     */
    'appointmentTypeName'?: string | null;
    /**
     * What appointments of this type will be worth — the mapped appointment type\'s own category. Absent unless the state is `MAPPED`, which is the whole reason an unanswered type blocks: there is no category to fall back on. 
     */
    'billingCategory'?: BillingCategory | null;
    /**
     * The organization\'s appointment type whose name is exactly this one, when there is one. An offer, not an answer. 
     */
    'suggestedAppointmentTypeId'?: string | null;
    /**
     * That type\'s name, which is this entry\'s `sourceType` up to case
     */
    'suggestedAppointmentTypeName'?: string | null;
    /**
     * What the suggestion would make these appointments worth. Shown because a name that matches says nothing about the category behind it, and the category is the expensive half to get wrong. 
     */
    'suggestedBillingCategory'?: BillingCategory | null;
    /**
     * The beroepen the mapped appointment type allows to hold a session of it. **Empty means no restriction**, which is also what an unanswered or skipped type answers. 
     */
    'allowedProfessionCodes': Array<ProfessionCode>;
    /**
     * Practitioners this delivery files afspraken of this activity type under whose beroep `allowedProfessionCodes` does not include (#4357). Largest first.  A **warning, not a refusal**: unlike an unanswered type, this has no answer that is obviously right — the beroepscode is a fact about a colleague and the allowed list a fact about an afspraaktype, so correcting either is a change to the practice\'s own configuration rather than an answer about the delivery. What is offered instead is the count, before approval, once — rather than a `400` per row long after it.  Empty for a type that is unanswered, skipped, or mapped to one that restricts nothing. 
     */
    'professionCodeConflicts': Array<ImportProfessionCodeConflict>;
}


/**
 * Every activity type of one run, and how far the practice has got with them. 
 */
export interface ImportAppointmentTypeReport {
    /**
     * The run these activity types belong to
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * The activity types, largest first, so the answer that settles most of the delivery is the one at the top. Ties fall back to the name, so the order is stable between reads. 
     */
    'types': Array<ImportAppointmentTypeEntry>;
    /**
     * The appointment types an activity type may be mapped to — the organization\'s own, each with its billing category. Answered here rather than left to `GET /api/v1/appointment-types` for the same reasons the practitioner picker is: that endpoint takes a permission the import permissions do not imply, and it is paginated. This is exactly the set `resolve` accepts, so the picker cannot offer a type the write would refuse. 
     */
    'candidates': Array<ImportAppointmentTypeCandidate>;
    /**
     * How many activity types nobody has answered for. **While this is above zero the run cannot be approved** — no billing category is ever inferred. 
     */
    'undecided': number;
    /**
     * Appointments the skips answered so far will drop
     */
    'skippedAppointments': number;
}


/**
 * Where one activity type stands: nobody has answered for it yet (`UNRESOLVED`, which blocks the run\'s approval), it is a Scrivio appointment type, or its sessions are not imported. 
 */

export const ImportAppointmentTypeState = {
    Unresolved: 'UNRESOLVED',
    Mapped: 'MAPPED',
    Skipped: 'SKIPPED'
} as const;

export type ImportAppointmentTypeState = typeof ImportAppointmentTypeState[keyof typeof ImportAppointmentTypeState];


/**
 * The password that opens a delivery\'s protected archive.
 */
export interface ImportArchivePasswordRequest {
    /**
     * As the EPD vendor supplied it. Stored encrypted with Cloud KMS only until the unpack it is entered for ends, and never logged. 
     */
    'password': string;
}
/**
 * What reconsidering an applied delivery\'s unplaced declarations came to.  The three counts are one reading of the same set, so they add up: `reconsidered` is every declaration the run left without a session, `resolved` is how many of those a single answer existed for, and `unresolved` is the rest — still exactly where they were. Nothing already applied is counted, because nothing already applied is looked at.  Why each of the rest stays unplaced is on the findings of the run, per declaration, which is where it has always been. 
 */
export interface ImportBillingLineReresolution {
    /**
     * Declarations of this run that reached no session and could not be written
     */
    'reconsidered': number;
    /**
     * How many of those are now placed on an afspraak and a behandelaar, and queued for the apply worker
     */
    'resolved': number;
    /**
     * How many stay unplaced, unchanged
     */
    'unresolved': number;
}
/**
 * Which kind of record a staged import row will write. One value per staged table, and the axis the preview splits on — \"how many cliënten, how many afspraken\" is the first thing a practice asks of a run before approving it. 
 */

export const ImportEntityType = {
    Client: 'CLIENT',
    InsurancePolicy: 'INSURANCE_POLICY',
    ClientFinancing: 'CLIENT_FINANCING',
    VerantwoordelijkeGemeente: 'VERANTWOORDELIJKE_GEMEENTE',
    Zorgvraag: 'ZORGVRAAG',
    CareTrajectory: 'CARE_TRAJECTORY',
    Toewijzing: 'TOEWIJZING',
    Appointment: 'APPOINTMENT',
    Registratie: 'REGISTRATIE',
    BillingLine: 'BILLING_LINE',
    Note: 'NOTE',
    Questionnaire: 'QUESTIONNAIRE',
    Document: 'DOCUMENT',
    IjwBericht: 'IJW_BERICHT',
    ContractPriceRule: 'CONTRACT_PRICE_RULE'
} as const;

export type ImportEntityType = typeof ImportEntityType[keyof typeof ImportEntityType];


/**
 * Which EPD the practice says produced an uploaded delivery.  **Declared, not detected.** With one vendor supported the recogniser could answer \"what is this\" by reading the delivery, because there was only one answer it could give; a second vendor turns that into detection between layouts, and the practice migrating away from an EPD already knows which one it is.  It is deliberately not `ImportRunSource`. A vendor is what was *claimed*; the source is what a recogniser read and accepted. The declaration decides which recogniser is offered the delivery — that recogniser still checks the layout and narrows the source itself, which is what makes `ZORGADMIN_DUMP` mean \"read as a ZorgAdmin export and accepted\" rather than \"someone said it was one\".  - ZORGADMIN: ZorgAdmin (Prosoftware) standaarddump - MEDICORE: Medicore CSV export, anchored on its own `pakbon.csv` - JAMES: James Software delivery, anchored on its own `Export.xlsx` 
 */

export const ImportEpdVendor = {
    Zorgadmin: 'ZORGADMIN',
    Medicore: 'MEDICORE',
    James: 'JAMES'
} as const;

export type ImportEpdVendor = typeof ImportEpdVendor[keyof typeof ImportEpdVendor];


/**
 * One answer: this row, this field, and either the value or the decision not to import it. 
 */
export interface ImportFieldAnswerInput {
    /**
     * The staged row being answered, exactly as the report names it
     */
    'stagedRowId': string;
    'field': ImportRequiredField;
    /**
     * Which answer this is. Absent means `SUPPLIED`, which is the only answer that existed before #4055 and therefore what a tab left open across that deploy sends.  A `SKIPPED` answer carries no `date`, `userId` or `verwijzerType`, and one that does is refused with `400` — a skip that also named a value would leave the two saying opposite things about the same row. 
     */
    'decision'?: ImportFieldDecision | null;
    /**
     * The value, for a `SUPPLIED` answer to a `DATE` field. Required then, and refused for a `USER` field — an answer carrying the wrong kind of value is a value that would be silently dropped. 
     */
    'date'?: string | null;
    /**
     * The value, for a `SUPPLIED` answer to a `USER` field. Required then, and refused for a `DATE` field. Must be an active member of the caller\'s organization. 
     */
    'userId'?: string | null;
    /**
     * The value, for a `SUPPLIED` answer to a `VERWIJZER_TYPE` field. Required then, and refused for any other field. 
     */
    'verwijzerType'?: VerwijzerType | null;
}


/**
 * What a bulk answer did, and what is left. 
 */
export interface ImportFieldAnswerOutcome {
    /**
     * Rows this request supplied a value for
     */
    'answered': number;
    /**
     * Rows this request took out of the run instead, because there is no value to give (#4055). Counted apart from `answered` because the two do opposite things: one makes a record land, the other makes sure it does not. 
     */
    'skipped': number;
    /**
     * What is left of the gate, over every group — the same number, counted the same way, as `ImportMissingFieldReport.unansweredBlocking`. Zero is what opens the approval gate, as far as this question is concerned.  Named for the subset rather than `unanswered` because it *is* the subset: a gap on a row a partial approval would skip (#4051) is not counted here, and one field meaning two things in one contract is how a consumer comes to refuse an approval the server allows. 
     */
    'unansweredBlocking': number;
}
/**
 * What a practice answered about one required field a delivery left empty.  `SUPPLIED` is a value they gave; `SKIPPED` says there is no value to give and the row should not be imported — a zorgtraject that has not started yet has no startdatum anywhere, and `care_trajectories.startdatum` is NOT NULL, so neither answer nor re-delivery can produce one (#4055).  There is a third state and it is deliberately not a value here: **no answer at all**, which is what a gap the practice has not reached yet is in, and what refuses an approval. It is the absence of an answer rather than a value, exactly as it is on `ImportPractitionerDecision` — you cannot un-answer a field, you re-answer it. 
 */

export const ImportFieldDecision = {
    Supplied: 'SUPPLIED',
    Skipped: 'SKIPPED'
} as const;

export type ImportFieldDecision = typeof ImportFieldDecision[keyof typeof ImportFieldDecision];


/**
 * One field of a merge: what the import would do to it, and the two values it was decided between.  The decision is the write path\'s own — the same expression decides what is written and what is reported here — so a preview cannot promise something the apply worker will not do. 
 */
export interface ImportFieldMerge {
    'field': ImportMergeField;
    'outcome': ImportFieldOutcome;
    /**
     * What Scrivio holds today, as a practice reads it — a name, a date, a regiebehandelaar\'s name. Absent when the stored record carries no value for the field, which is exactly when the import can fill it. 
     */
    'storedValue'?: string | null;
    /**
     * What the source carries for the field; absent when it carries nothing
     */
    'importedValue'?: string | null;
}


/**
 * What an import would do to one field of a record it matched: - FILLED: Scrivio holds no value and the import carries one — it will be written - IDENTICAL: the two agree (both absent included) — nothing is written - KEPT: they disagree and the stored value wins — nothing is written, and this is the   answer a practice is reading the preview for - REPLACED: they disagree and the import\'s value is written over the stored one  The default policy is fill-blanks-only, so REPLACED is not reachable for a staged import: a value the practice has corrected in Scrivio outranks an older export\'s. 
 */

export const ImportFieldOutcome = {
    Filled: 'FILLED',
    Identical: 'IDENTICAL',
    Kept: 'KEPT',
    Replaced: 'REPLACED'
} as const;

export type ImportFieldOutcome = typeof ImportFieldOutcome[keyof typeof ImportFieldOutcome];


/**
 * What kind of answer a required field takes, so one screen can render every group: a calendar date, one of the organization\'s members, or a `VerwijzerType`. 
 */

export const ImportFieldValueType = {
    Date: 'DATE',
    User: 'USER',
    VerwijzerType: 'VERWIJZER_TYPE'
} as const;

export type ImportFieldValueType = typeof ImportFieldValueType[keyof typeof ImportFieldValueType];


/**
 * A cliënt a finding is about, as the delivery staged them — never the Scrivio dossier, which a finding on an unapplied run has not written yet. 
 */
export interface ImportFindingClient {
    /**
     * The cliënt\'s name as the delivery spells it
     */
    'name': string;
    /**
     * The cliënt\'s number in the source system — what the practice and the EPD vendor both search on. Absent when the delivery carries none. 
     */
    'patientNumber'?: string | null;
    /**
     * The staged cliënt row\'s own key in the source (`patient.csv id 78`) — what locates the cliënt in the delivery when the patiëntnummer does not 
     */
    'sourceRef': string;
}
/**
 * Machine-readable finding code raised by the ZPM GGZ rules. Grouped by rule family: structural (source shape), cell (per-value), reference (reference data) and cross-sheet (referential integrity and ZPM invariants). 
 */

export const ImportFindingCode = {
    SheetMissing: 'SHEET_MISSING',
    ColumnMissing: 'COLUMN_MISSING',
    ColumnOrderChanged: 'COLUMN_ORDER_CHANGED',
    RequiredFieldMissing: 'REQUIRED_FIELD_MISSING',
    InvalidDate: 'INVALID_DATE',
    InvalidBsn: 'INVALID_BSN',
    BsnUnknown: 'BSN_UNKNOWN',
    InvalidEnumValue: 'INVALID_ENUM_VALUE',
    InvalidUuid: 'INVALID_UUID',
    UnknownUzovi: 'UNKNOWN_UZOVI',
    UnknownDiagnoseCode: 'UNKNOWN_DIAGNOSE_CODE',
    UnknownSettingCode: 'UNKNOWN_SETTING_CODE',
    UnknownZorgvraagtypeCode: 'UNKNOWN_ZORGVRAAGTYPE_CODE',
    ClientNotFound: 'CLIENT_NOT_FOUND',
    ZorgtrajectNotFound: 'ZORGTRAJECT_NOT_FOUND',
    TrajectStartBeforeZpm: 'TRAJECT_START_BEFORE_ZPM',
    MultiplePrimaryDiagnoses: 'MULTIPLE_PRIMARY_DIAGNOSES',
    NoPrimaryDiagnosis: 'NO_PRIMARY_DIAGNOSIS',
    OverlappingRegiebehandelaar: 'OVERLAPPING_REGIEBEHANDELAAR',
    OverlappingSetting: 'OVERLAPPING_SETTING',
    OverlappingZorgvraagtypering: 'OVERLAPPING_ZORGVRAAGTYPERING',
    TrajectZonderRegiebehandelaar: 'TRAJECT_ZONDER_REGIEBEHANDELAAR',
    MedewerkerNietGevonden: 'MEDEWERKER_NIET_GEVONDEN',
    MedewerkerAmbigu: 'MEDEWERKER_AMBIGU',
    ClientBestaatAlMetActiefZorgtraject: 'CLIENT_BESTAAT_AL_MET_ACTIEF_ZORGTRAJECT',
    ClientBestaatAl: 'CLIENT_BESTAAT_AL'
} as const;

export type ImportFindingCode = typeof ImportFindingCode[keyof typeof ImportFindingCode];


/**
 * One finding, stated in source terms *and* in the practice\'s: where in the delivery it was raised, and which cliënt it is about. 
 */
export interface ImportFindingDetail {
    'severity': ImportFindingSeverity;
    /**
     * The rule\'s own code
     */
    'code': string;
    /**
     * Human-readable explanation in Dutch, as the practice reads it
     */
    'message': string;
    /**
     * Where in the delivery — a sheet name, `patient.csv`, `202400001_GDS801.xml`
     */
    'sourcePart': string;
    /**
     * The 1-based row within `sourcePart`, where the part has rows at all
     */
    'sourceRow'?: number | null;
    /**
     * The column or element the finding is about, where it is tied to one
     */
    'sourceField'?: string | null;
    /**
     * The offending value, echoed back so the practice can find it in the source
     */
    'value'?: string | null;
    'entityType'?: ImportEntityType;
    /**
     * The cliënten this finding is about. Usually one; a group afspraak names every attendee, and a finding about the delivery itself — or about a record that names no cliënt, such as a contracttarief — names none. 
     */
    'clients': Array<ImportFindingClient>;
    /**
     * The zorgtrajectnummer of the trajectory this finding\'s row belongs to, where the row has one; absent otherwise 
     */
    'zorgtrajectNummer'?: string | null;
}


/**
 * One cause, and how many findings it accounts for. 
 */
export interface ImportFindingGroup {
    /**
     * The rule\'s own code, from whichever vocabulary the import speaks
     */
    'code': string;
    'severity': ImportFindingSeverity;
    'entityType'?: ImportEntityType;
    /**
     * One of the sentences this group\'s findings carry. A rule that names a practitioner or a prestatiecode phrases each finding differently, so this is an example rather than the group\'s message — the findings themselves carry their own. 
     */
    'exampleMessage': string;
    /**
     * Findings this cause accounts for
     */
    'total': number;
}


/**
 * One page of a run\'s findings, in the order the source reads
 */
export interface ImportFindingPage {
    /**
     * The findings of this page
     */
    'content': Array<ImportFindingDetail>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Findings matching the filters, over the whole run
     */
    'totalElements': number;
    /**
     * Number of pages the filtered findings come to
     */
    'totalPages': number;
}
/**
 * Severity of a finding, shared by every import. `ERROR` blocks import; `WARNING` is informational and does not block. 
 */

export const ImportFindingSeverity = {
    Error: 'ERROR',
    Warning: 'WARNING'
} as const;

export type ImportFindingSeverity = typeof ImportFindingSeverity[keyof typeof ImportFindingSeverity];


/**
 * A run\'s findings, one line per cause rather than one per finding. 
 */
export interface ImportFindingSummary {
    /**
     * The run these findings belong to
     */
    'runId': string;
    /**
     * Name of the delivery, as the practice knows it
     */
    'fileName': string;
    /**
     * ERROR findings over the whole run — what refuses an approval
     */
    'blockingFindings': number;
    /**
     * WARNING findings over the whole run
     */
    'warningFindings': number;
    /**
     * One line per (code, severity, kind of record), most frequent first. Empty for a run whose rules found nothing. 
     */
    'groups': Array<ImportFindingGroup>;
}
/**
 * A field an import merge decides. One vocabulary over every record type that has a merge, each of which produces only its own subset: the first fifteen are a client\'s identity core, the last three a care trajectory\'s.  The keys a match is deduped on are deliberately absent — a client\'s BSN and a trajectory\'s zorgtrajectnummer are what made it a match, so they cannot disagree — and so is the behandelsetting, which cannot change after a trajectory is created. 
 */

export const ImportMergeField = {
    FirstName: 'FIRST_NAME',
    LastName: 'LAST_NAME',
    Initials: 'INITIALS',
    Prefix: 'PREFIX',
    Email: 'EMAIL',
    Phone: 'PHONE',
    AddressStreet: 'ADDRESS_STREET',
    AddressHouseNumber: 'ADDRESS_HOUSE_NUMBER',
    AddressHouseNumberSuffix: 'ADDRESS_HOUSE_NUMBER_SUFFIX',
    AddressPostalCode: 'ADDRESS_POSTAL_CODE',
    AddressCity: 'ADDRESS_CITY',
    DateOfBirth: 'DATE_OF_BIRTH',
    Gender: 'GENDER',
    IntakeDate: 'INTAKE_DATE',
    ExternalReference: 'EXTERNAL_REFERENCE',
    Startdatum: 'STARTDATUM',
    Hoofdbehandelaar: 'HOOFDBEHANDELAAR',
    HoofdbehandelaarStart: 'HOOFDBEHANDELAAR_START'
} as const;

export type ImportMergeField = typeof ImportMergeField[keyof typeof ImportMergeField];


/**
 * One staged row\'s gap in one required field — or the answer that has since filled it. 
 */
export interface ImportMissingFieldEntry {
    /**
     * The staged row, which is what an answer names
     */
    'stagedRowId': string;
    'field': ImportRequiredField;
    'entityType': ImportEntityType;
    /**
     * The row\'s natural key in the source, so a practice can find it there
     */
    'sourceRef': string;
    /**
     * What the row is, in the practice\'s terms
     */
    'description': string;
    /**
     * What the parse said about the gap, in Dutch, where it raised a finding for it. Absent for a gap no finding names — a zorgtraject that was never declared has no regiebehandelaar for the same reason it has no startdatum, and only one finding is raised for the pair. 
     */
    'reason'?: string | null;
    /**
     * A date the delivery implies, offered rather than applied. Only for a `DATE` field, and only where something in the delivery answers it. 
     */
    'suggestedDate'?: string | null;
    /**
     * A member the delivery implies, offered rather than applied; only for a `USER` field
     */
    'suggestedUserId'?: string | null;
    /**
     * That member\'s name, so the offer reads without a second request
     */
    'suggestedUserName'?: string | null;
    /**
     * Where the suggestion comes from, in Dutch — a value offered without saying what it was derived from is a guess wearing a confirmation. 
     */
    'suggestion'?: string | null;
    /**
     * The date the practice supplied, for an answered `DATE` field
     */
    'answeredDate'?: string | null;
    /**
     * The member the practice supplied, for an answered `USER` field
     */
    'answeredUserId'?: string | null;
    /**
     * The type the practice supplied, for an answered `VERWIJZER_TYPE` field
     */
    'answeredVerwijzerType'?: VerwijzerType | null;
    /**
     * That member\'s name
     */
    'answeredUserName'?: string | null;
    /**
     * When the answer was given; absent while the gap stands
     */
    'answeredAt'?: string | null;
    /**
     * Who supplied it. A migration\'s provenance is why these rows are staged at all, so an answer is recorded as an answer rather than folded into the delivery. 
     */
    'answeredByName'?: string | null;
    /**
     * Which answer was given, or absent while the gap stands. A `SKIPPED` row carries this and none of the three `answered*` value fields — it is an answer with no value, which is precisely what makes it different from a gap nobody has reached (#4055). 
     */
    'answeredDecision'?: ImportFieldDecision | null;
    /**
     * What skipping this row would take with it, per kind of record — **before** the practice confirms, rather than discovered at apply time (#4055). Empty for a row nothing hangs off, which is every vragenlijst and most zorgtrajecten in a delivery like FIER\'s. 
     */
    'skipImpact'?: Array<ImportSkipImpact>;
}


/**
 * One required field across the whole run: how many rows are still missing it, and how many have been answered. 
 */
export interface ImportMissingFieldGroup {
    'field': ImportRequiredField;
    'entityType': ImportEntityType;
    'valueType': ImportFieldValueType;
    /**
     * Staged rows still missing this field — every gap there is to answer, which is what makes this the count a tab carries. Deliberately **not** the number that refuses the approval: a gap on a row a partial approval would skip (#4051) is listed here and left out of `ImportMissingFieldReport.unansweredBlocking`, which is what the gate reads.  A row the practice has skipped (#4055) has left the run, so it is no longer a gap and is counted under `skipped` instead. 
     */
    'unanswered': number;
    /**
     * Rows of this run whose gap the practice has filled with a value
     */
    'answered': number;
    /**
     * Rows of this run the practice answered by saying there is no value and the row should not be imported (#4055). Beside `answered` rather than folded into it: both are answers, and only one of them puts a record in the dossier. 
     */
    'skipped': number;
}


/**
 * The required fields one run\'s delivery left empty, grouped, with one page of one group. 
 */
export interface ImportMissingFieldReport {
    /**
     * The run these gaps belong to
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * Every required field, including the ones this delivery filled in throughout. A field absent from the answer is indistinguishable from one the reader did not think to look for, which is the whole reason this report exists. 
     */
    'groups': Array<ImportMissingFieldGroup>;
    /**
     * Rows still missing a required field, over every group — every gap there is to answer, which is what the tabs offer. 
     */
    'unanswered': number;
    /**
     * The subset of `unanswered` that refuses the approval **whatever the practice decides about its blocked rows**, and the exact number the approval endpoint gates on.  A gap on a row carrying a blocking finding is left out, because a partial approval (#4051) skips that row and a gap on a row leaving the run is not a gap the run will hit. What is counted is a gap with no blocking finding behind it — the row a skip does not reach, and the one that would otherwise arrive at the apply worker and fail. So a screen reading this can never offer an approval the server refuses, nor hide the partial approval that is the only way a delivery like FIER\'s gets in. 
     */
    'unansweredBlocking': number;
    'field': ImportRequiredField;
    /**
     * The members a `USER` field may be answered with — the organization\'s active members, by name. Answered here for the reason `ImportPractitionerReport.candidates` is: it is exactly the set the write accepts, and it does not take `users.read`. 
     */
    'candidates': Array<ImportPractitionerCandidate>;
    /**
     * One page of `field`\'s rows — the gaps and the ones already answered together, in the source\'s own key order, so a row does not move under the practice as they answer it
     */
    'entries': Array<ImportMissingFieldEntry>;
    /**
     * Zero-indexed page number
     */
    'page': number;
    /**
     * Rows per page
     */
    'size': number;
    /**
     * Rows in this group, gaps and answers together
     */
    'totalElements': number;
    /**
     * Pages at this size
     */
    'totalPages': number;
}


/**
 * One user a name may be mapped to. 
 */
export interface ImportPractitionerCandidate {
    /**
     * The Scrivio user
     */
    'id': string;
    /**
     * How the practice sees them
     */
    'name': string;
}
/**
 * What a practice answers about one unresolved practitioner name.  There is a third state and it is deliberately not a value here: **no answer at all**, which is what a name the practice has not reached yet is in, and what refuses an approval. It is reported as `UNRESOLVED` on `ImportPractitionerNameState` and cannot be *sent* — you cannot un-decide a name, you re-decide it. 
 */

export const ImportPractitionerDecision = {
    Mapped: 'MAPPED',
    Skipped: 'SKIPPED'
} as const;

export type ImportPractitionerDecision = typeof ImportPractitionerDecision[keyof typeof ImportPractitionerDecision];


/**
 * One practitioner name an import could not resolve, with what hangs off it. 
 */
export interface ImportPractitionerName {
    /**
     * The practitioner exactly as the source spells it
     */
    'sourceName': string;
    /**
     * Staged afspraken carrying this name, whatever state they are in — so the number does not shrink under the reader the moment they answer 
     */
    'appointments': number;
    /**
     * Staged notities filed against those afspraken. This is what a skip additionally costs: withdrawing an afspraak withdraws the notities on it. 
     */
    'notes': number;
    'state': ImportPractitionerNameState;
    /**
     * The Scrivio user this name was mapped to; absent unless the state is `MAPPED`
     */
    'userId'?: string | null;
    /**
     * That user\'s name, so the list reads without a second request
     */
    'userName'?: string | null;
}


/**
 * Where one practitioner name stands: nobody has answered for it yet (`UNRESOLVED`, which blocks the run\'s approval), it is a Scrivio user, or it is to be skipped. 
 */

export const ImportPractitionerNameState = {
    Unresolved: 'UNRESOLVED',
    Mapped: 'MAPPED',
    Skipped: 'SKIPPED'
} as const;

export type ImportPractitionerNameState = typeof ImportPractitionerNameState[keyof typeof ImportPractitionerNameState];


/**
 * Every unresolved practitioner name of one run, and how far the practice has got with them. 
 */
export interface ImportPractitionerReport {
    /**
     * The run these names belong to
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * The names, most-used first, so the answer that resolves most of the delivery is the one at the top. Ties fall back to the name itself, so the order is stable between reads. 
     */
    'names': Array<ImportPractitionerName>;
    /**
     * The users a name may be mapped to — the organization\'s active members, by name. It is answered here rather than left to `GET /api/v1/users` for two reasons: that endpoint takes `users.read`, which the permission that commits an import does not imply, and it is paginated, so a picker built from it would silently offer the first page of a large practice. This is exactly the set `resolve` accepts, so the picker cannot offer a user the write would refuse. 
     */
    'candidates': Array<ImportPractitionerCandidate>;
    /**
     * How many names nobody has answered for. **While this is above zero the run cannot be approved** — no name is silently guessed. 
     */
    'undecided': number;
    /**
     * Afspraken the skips answered so far will drop
     */
    'skippedAppointments': number;
    /**
     * Notities that go with them
     */
    'skippedNotes': number;
}


/**
 * What an import run will do, before any of it exists: the run\'s own state, how far the delivery has been read, and a summary per staged entity type. 
 */
export interface ImportPreview {
    /**
     * The run being previewed
     */
    'runId': string;
    /**
     * Name of the delivery, as the practice knows it
     */
    'fileName': string;
    'source': ImportRunSource;
    /**
     * Which EPD the practice declared this delivery came from. Beside `source` rather than folded into it: this is what was claimed at upload, and `source` is what a recogniser read and accepted — which is exactly the pair a practice needs to see when a delivery was refused. Absent for an import that carries no archive. 
     */
    'vendor'?: ImportEpdVendor | null;
    'state': ImportRunState;
    'phase': ImportPreviewPhase;
    /**
     * When the delivery was staged
     */
    'startedAt': string;
    /**
     * When the last row was applied; absent while the run has not been applied
     */
    'finishedAt'?: string | null;
    /**
     * One summary per entity type, in a fixed order. Every type is present, including the ones this run staged nothing for — an absent type would be indistinguishable from one the reader forgot to look for. 
     */
    'entities': Array<ImportPreviewEntitySummary>;
    /**
     * ERROR findings over the whole run. Not the sum of the per-entity counts: a finding about the delivery as a whole belongs to no entity type and is counted only here. 
     */
    'blockingFindings': number;
    /**
     * WARNING findings over the whole run, on the same basis as `blockingFindings`
     */
    'warningFindings': number;
    /**
     * Rows this run\'s approval knowingly skipped over a blocking finding — what the practice read and accepted would not land. `0` while the run has not been decided, on a run approved in full, and on a rejected one.  Which rows they were is the drilldown under `status=SKIPPED`; this is the run\'s own record of how many there were. 
     */
    'skippedRows': number;
    /**
     * Parts of the delivery the parse gave up on and never read, in the order a run is read (#4632). Giving up on one part stops the read, so this is that part and every one after it. Empty when the delivery was read whole, and while it is still being read.  None of these is in `entities`: an unread part staged no rows, so its summary reads `0` exactly as a part the delivery did not contain does — this is what tells the two apart. A run with any cannot be approved, `skipBlockedRows` included; it is re-parsed or rejected. How many records such a part held is not known, because reading it is what failed. 
     */
    'unreadEntityTypes': Array<ImportEntityType>;
    /**
     * Whether the delivery\'s archive is password-protected and waits for the practice to enter the password — its unpack ended on the protection, or on a password that did not open it, and the run has not been decided since. Exactly when `POST /api/v1/imports/runs/{runId}/archive-password` accepts one. `false` for every archive without a password, which is never asked for one. 
     */
    'archivePasswordRequested': boolean;
    'unpack'?: ImportUnpackProgress;
}


/**
 * What one entity type of a run comes to.  `newRows` and `matchedRows` split the staged rows by whether the row already names a Scrivio record — a client matched on BSN, a trajectory matched on zorgtrajectnummer. That split answers new-versus-existing while the run is awaiting approval, which is when a preview is read. Once a run has been applied every row names the record it wrote or merged onto, so `matchedRows` then counts rows that name a record rather than rows that were matched to one. 
 */
export interface ImportPreviewEntitySummary {
    'entityType': ImportEntityType;
    /**
     * Rows the run staged for this entity type; 0 when it staged none
     */
    'total': number;
    /**
     * Of those, the rows that name no Scrivio record — what the run will create
     */
    'newRows': number;
    /**
     * Of those, the rows that name a Scrivio record — what the run will merge onto
     */
    'matchedRows': number;
    /**
     * ERROR findings the rules raised about rows of this entity type
     */
    'blockingFindings': number;
    /**
     * WARNING findings the rules raised about rows of this entity type
     */
    'warningFindings': number;
    /**
     * Rows of this type that are still awaiting the decision and carry a blocking finding — the rows a partial approval will not import. Counted in rows rather than in findings, because one row can carry several and because a finding about the delivery itself describes no row at all.  Summed over the entity types this is exactly the number `POST /approve` demands as `skipBlockedRows`: the practice accepts the number it was shown. `0` once the run has been decided — what a decision actually skipped is `skippedRows` on the run, and the rows themselves keep their findings under `status=SKIPPED`. 
     */
    'blockedRows': number;
}


/**
 * How far the delivery has been read, which `state` cannot say.  A dump reaches `AWAITING_APPROVAL` the moment its explode finishes and keeps it through recognition *and* parsing, because that status is what \"previewable\" means and the phases after it select on it. So the state a run reports while it is still being read is the one that means \"waiting for you\", and only the unpack is distinguishable from the outside (#3785, #3945).  Derived on every read from `status`, `exploded_at`, `source` and `parsed_at` — never stored, because a column recording the same fact is one that can disagree with them.  - AWAITING_UNPACK: the archive is in storage and no explode worker has claimed it yet - UNPACKING: the archive is being streamed apart into per-entry objects - RECOGNISING: the entries are out and nothing has yet decided which EPD produced them - READING: the delivery was recognised and its records are being staged - PREVIEWABLE: nothing further will be read, so the preview describes the whole run. An   import that stages inside the request that uploads it is always here, and so is a run   that ended before it could be read — `state` is what says which 
 */

export const ImportPreviewPhase = {
    AwaitingUnpack: 'AWAITING_UNPACK',
    Unpacking: 'UNPACKING',
    Recognising: 'RECOGNISING',
    Reading: 'READING',
    Previewable: 'PREVIEWABLE'
} as const;

export type ImportPreviewPhase = typeof ImportPreviewPhase[keyof typeof ImportPreviewPhase];


/**
 * One staged row: what the source called it, what it will write, and what the rules found about it. 
 */
export interface ImportPreviewRow {
    /**
     * The staged row itself — what a finding about it refers to
     */
    'id': string;
    /**
     * The row\'s natural key in the source (`patient.csv id 78`, `agendaid 11624`, `202400001_GDS801.xml`) — what a practice recognises it by, and what answers \"where did this come from?\" months later 
     */
    'sourceRef': string;
    /**
     * What this row will write, in the practice\'s own terms — a name, a zorgtrajectnummer, an appointment\'s title. Derived from the staged row itself; the source key alone does not tell a reader what they are approving. 
     */
    'description': string;
    'state': ImportStagedRowState;
    /**
     * The Scrivio record this row merges onto or created — a client, a zorgtraject, an afspraak, whichever this entity type writes. Absent while the run will create it. 
     */
    'targetId'?: string | null;
    /**
     * Why this row could not be applied — or, while it is still STAGED, why it cannot be applied as it stands (a practitioner the source named and nothing resolved) 
     */
    'failureReason'?: string | null;
    /**
     * Why the revert did not remove what this row wrote, which is therefore still standing
     */
    'revertReason'?: string | null;
    /**
     * What the rules raised about this row, in source order. Empty for a row nothing was found about — the ordinary case. 
     */
    'findings': Array<ImportRowFinding>;
    /**
     * What applying this row would do to the record it matched, per field — what a practice reads before approving a merge onto clients they have been working with since the previous import.  Empty for a row that names no Scrivio record (it will be created, so there is nothing to merge onto), for an entity type nothing merges (an afspraak, a notitie and a document are written, never merged with), and for a row whose matched record can no longer be read. 
     */
    'fieldMerges': Array<ImportFieldMerge>;
}


/**
 * One page of a run\'s staged rows for a single entity type
 */
export interface ImportPreviewRowPage {
    'entityType': ImportEntityType;
    /**
     * The rows of this page, in the requested order
     */
    'content': Array<ImportPreviewRow>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Rows matching the filters, over the whole run
     */
    'totalElements': number;
    /**
     * Number of pages the filtered rows come to
     */
    'totalPages': number;
}


/**
 * One practitioner whose beroep the appointment type an activity type is mapped to does not allow — a pairing the apply worker will refuse, an afspraak at a time (#4357). 
 */
export interface ImportProfessionCodeConflict {
    /**
     * The Scrivio user the afspraken are filed under
     */
    'userId': string;
    /**
     * As the practice sees their name
     */
    'userName': string;
    /**
     * What that user holds. **Absent means they hold none**, which the appointment type refuses just as firmly as the wrong one — the two are different problems with different answers, so they are not collapsed into one. 
     */
    'professionCode'?: ProfessionCode | null;
    /**
     * Staged afspraken of this activity type filed under this practitioner, every one of which would fail. Counted over the rows the approval will **release**, so it falls to zero once the practice has acted — a skipped activiteitsoort or practitioner reports no conflict, because those rows are not imported. 
     */
    'appointments': number;
}


/**
 * A column a staged row cannot be written without, and which a delivery can leave empty.  One value per NOT NULL column an import writes and an EPD export may not carry, plus the verwijzer type of a zorgtraject that claims a verwijzing, which `VerwijzingValidator` refuses it without. It is deliberately not \"every field a row has\": this exists so a gap the source cannot fill can be answered by the practice, not so an import can be edited on its way in. 
 */

export const ImportRequiredField = {
    CareTrajectoryStartdatum: 'CARE_TRAJECTORY_STARTDATUM',
    CareTrajectoryHoofdbehandelaar: 'CARE_TRAJECTORY_HOOFDBEHANDELAAR',
    CareTrajectoryVerwijzerType: 'CARE_TRAJECTORY_VERWIJZER_TYPE',
    AppointmentBehandelaar: 'APPOINTMENT_BEHANDELAAR',
    QuestionnaireFilledInAt: 'QUESTIONNAIRE_FILLED_IN_AT'
} as const;

export type ImportRequiredField = typeof ImportRequiredField[keyof typeof ImportRequiredField];


/**
 * One thing an import\'s rules found about a staged row, stated in source terms so it can be looked up in the delivery. 
 */
export interface ImportRowFinding {
    'severity': ImportFindingSeverity;
    /**
     * The rule\'s own code. A plain string rather than an enum: the imports do not share one code vocabulary — the ZPM rules, the agenda rules and an EPD dump each speak their own. 
     */
    'code': string;
    /**
     * Human-readable explanation in Dutch, as the practice reads it
     */
    'message': string;
    /**
     * Where in the delivery — a sheet name, `patient.csv`, `202400001_GDS801.xml`
     */
    'sourcePart': string;
    /**
     * The 1-based row within `sourcePart`, where the part has rows at all
     */
    'sourceRow'?: number | null;
    /**
     * The column or element the finding is about, where it is tied to one
     */
    'sourceField'?: string | null;
    /**
     * The offending value, echoed back so the practice can find it in the source
     */
    'value'?: string | null;
}


/**
 * Which import produced the run. The run header is generic (ADR-039), so a second import joins it by adding a value here rather than a table of its own.  A delivery **changes source over its own lifetime**: an uploaded archive is `EPD_DUMP` until the recogniser its declared vendor selects has read it, and the vendor-shaped value afterwards. Anything branching on this therefore asks \"is this a dump\" rather than naming one value.  A vendor-shaped value means the delivery was **read and accepted** as that export, never that someone said it was one — which is what the practice\'s own `vendor` declaration says (`ImportEpdVendor`). The two are published side by side because a refused delivery is only explained by the pair.  - AGENDA_PLANNING: the agenda planning workbook, parsed inside the request that uploads it - CONTRACT_TARIFFS: an insurer\'s per-prestatiecode tariff sheet, for one contract. Which   contract is on the staged rows rather than on the run, because the header is generic - EPD_DUMP: a full EPD export uploaded as one archive, not yet recognised - ZORGADMIN_DUMP: an EPD export read and accepted as a ZorgAdmin (Prosoftware) standaarddump - MEDICORE_DUMP: an EPD export read and accepted as a Medicore gegevensexport — its   `pakbon.csv` located, the parts it names present, and their record counts matched - JAMES_DUMP: an EPD export read and accepted as a James Software delivery — its   `Export.xlsx` read and its parts located. A count that disagrees with what arrived is a   warning on the run, because such a delivery still imports 
 */

export const ImportRunSource = {
    AgendaPlanning: 'AGENDA_PLANNING',
    ContractTariffs: 'CONTRACT_TARIFFS',
    EpdDump: 'EPD_DUMP',
    ZorgadminDump: 'ZORGADMIN_DUMP',
    MedicoreDump: 'MEDICORE_DUMP',
    JamesDump: 'JAMES_DUMP'
} as const;

export type ImportRunSource = typeof ImportRunSource[keyof typeof ImportRunSource];


/**
 * Lifecycle of an import run, whatever the run imports: - EXPLODING: the uploaded archive is being streamed apart into per-file objects. Only an   import whose file arrives through Cloud Storage reaches this; a workbook is parsed in the   request that uploads it, so the agenda import never reports it (#3772) - AWAITING_APPROVAL: staged and waiting to be approved; no row is claimable yet. The agenda   import has no approval step and stages straight to PENDING, so it does not report this   today — it is the run header\'s counterpart of a staged row (#3767) - REJECTED: the preview was turned down. Terminal, and the only terminal state a run   reaches without a worker having touched it: it applied nothing and never will (#3776) - PENDING: staged, the worker has not applied any row yet - IN_PROGRESS: the worker is applying the run\'s rows - COMPLETED: every staged row was applied - COMPLETED_WITH_FAILURES: the run is over, it applied rows, and some of its rows it could   not apply. Partial application is what the staging design is for — a failing row is   claimed in a transaction of its own so it does not take the batch with it (ADR-039) — so   a run that landed most of a delivery says so rather than reporting as FAILED (#4359) - FAILED: the run is over and produced nothing. Either the delivery never became rows (the   unpack was stopped short, the recognition refused it, a parse phase gave up) or every row   it staged failed to apply - REVERTING: the run is being taken back; the worker has records of it left to remove - REVERTED: the revert is over. It may still have left records behind — ones carrying   work the import did not create are refused rather than removed 
 */

export const ImportRunState = {
    Exploding: 'EXPLODING',
    AwaitingApproval: 'AWAITING_APPROVAL',
    Rejected: 'REJECTED',
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Completed: 'COMPLETED',
    CompletedWithFailures: 'COMPLETED_WITH_FAILURES',
    Failed: 'FAILED',
    Reverting: 'REVERTING',
    Reverted: 'REVERTED'
} as const;

export type ImportRunState = typeof ImportRunState[keyof typeof ImportRunState];


/**
 * One behandelsetting a setting code may be mapped to.
 */
export interface ImportSettingCandidate {
    /**
     * The Scrivio behandelsetting
     */
    'id': string;
    /**
     * Its stable code
     */
    'code': string;
    /**
     * How the practice sees it
     */
    'name': string;
}
/**
 * One setting code of the delivery, with what it accounts for and what has been answered.
 */
export interface ImportSettingEntry {
    /**
     * Staged zorgtrajecten carrying this code, counted when the inventory was derived
     */
    'trajectories': number;
    'state': ImportSettingState;
    /**
     * The settingcode exactly as the delivery spells it. **Absent for the zorgtrajecten whose delivery names no setting** — that is one question of its own. 
     */
    'sourceCode'?: string | null;
    /**
     * The behandelsetting this code is; absent while `UNRESOLVED`
     */
    'settingId'?: string | null;
    /**
     * That behandelsetting\'s name, so the list reads without a second request
     */
    'settingName'?: string | null;
}


/**
 * Every setting code of one run, and how far the practice has got with them.
 */
export interface ImportSettingReport {
    /**
     * The run these setting codes belong to
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * The setting codes, largest first; ties fall back to the code.
     */
    'settings': Array<ImportSettingEntry>;
    /**
     * Scrivio\'s behandelsettingen, in display order — exactly the set `resolve` accepts. 
     */
    'candidates': Array<ImportSettingCandidate>;
    /**
     * How many setting codes nobody has answered for. **While this is above zero the run cannot be approved** — no behandelsetting is ever defaulted. 
     */
    'undecided': number;
}


/**
 * Where one setting code stands: nobody has answered for it yet (`UNRESOLVED`, which blocks the run\'s approval), its behandelsetting is confirmed and was filled in without asking (`AUTOMATIC`), or the practice chose it (`DECIDED`). 
 */

export const ImportSettingState = {
    Unresolved: 'UNRESOLVED',
    Automatic: 'AUTOMATIC',
    Decided: 'DECIDED'
} as const;

export type ImportSettingState = typeof ImportSettingState[keyof typeof ImportSettingState];


/**
 * What skipping one staged row does to the rows hanging off it (#4055).  `SKIPPED` — the row goes with it, because it cannot be written without the row being skipped: a dossiernotitie and a declaratieregel both name the afspraak they belong to, and neither has anywhere else to be filed.  `ORPHANED` — the row is still imported, but without the link. A zorgtraject that could not be created never took its afspraken, vragenlijsten or declaratieregels with it (#3880): an afspraak names no zorgtraject of its own, a session that happened is still a session, and losing two years of history over a link the schema does not require would cost the practice more than the link is worth. 
 */

export const ImportSkipEffect = {
    Skipped: 'SKIPPED',
    Orphaned: 'ORPHANED'
} as const;

export type ImportSkipEffect = typeof ImportSkipEffect[keyof typeof ImportSkipEffect];


/**
 * What one skip would take with it, for one kind of record. 
 */
export interface ImportSkipImpact {
    'entityType': ImportEntityType;
    'effect': ImportSkipEffect;
    /**
     * How many rows of that kind hang off the row being skipped. Counted over the rows the run can still write *and* the ones an earlier skip already took, so the number reads the same before and after the skip — a count that emptied itself on the press would take the evidence away exactly when the practice looks for it. 
     */
    'rows': number;
}


/**
 * Lifecycle of one staged import row, whatever it stages: - STAGED: resolved and waiting for the run to be approved; no worker will claim it - PENDING: approved, not claimed by the apply worker yet - APPLIED: the record was written; `targetId` names it, unless it has since been deleted - FAILED: the record could not be written; `failureReason` says why - REVERT_PENDING: applied, and queued for removal - REVERTED: what it wrote is gone — removed, or already absent when the revert reached it - REVERT_BLOCKED: the revert refused to remove what it wrote; `revertReason` says why - WITHDRAWN: staged and no longer applicable — taken off the apply queue by a revert,   withdrawn by a rejection, or classified at parse time as something this import does not   file - SKIPPED: the approval knowingly left it behind over a blocking finding. Never attempted,   which is why it is not FAILED — a practice checking whether every verslag came in reads   `FAILED` as \"the worker tried and could not\" 
 */

export const ImportStagedRowState = {
    Staged: 'STAGED',
    Pending: 'PENDING',
    Applied: 'APPLIED',
    Failed: 'FAILED',
    RevertPending: 'REVERT_PENDING',
    Reverted: 'REVERTED',
    RevertBlocked: 'REVERT_BLOCKED',
    Withdrawn: 'WITHDRAWN',
    Skipped: 'SKIPPED'
} as const;

export type ImportStagedRowState = typeof ImportStagedRowState[keyof typeof ImportStagedRowState];


/**
 * What a practice answers about one imported zorgtraject that would close a cliënt\'s existing active one.  - `SUPERSEDE` — import it, and close the existing zorgtraject on the imported one\'s start   date. Its afspraken re-resolve onto the imported zorgtraject, and **a revert undoes   neither of those**. - `SKIP` — do not import it. The practice\'s own zorgtraject is left exactly as it is, and   the staged row leaves the run with a Dutch reason on it.  There is a third state and it is deliberately not a value here: **no answer at all**, which is what an overlap nobody has reached is in, and what refuses an approval. It is reported as `UNANSWERED` on `ImportTrajectoryOverlapState` and cannot be *sent* — you cannot un-answer an overlap, you re-answer it. 
 */

export const ImportSupersedeDecision = {
    Supersede: 'SUPERSEDE',
    Skip: 'SKIP'
} as const;

export type ImportSupersedeDecision = typeof ImportSupersedeDecision[keyof typeof ImportSupersedeDecision];


/**
 * One imported zorgtraject that would close a zorgtraject the practice already has open, with both sides of the overlap and the answer given about it. 
 */
export interface ImportTrajectoryOverlapEntry {
    /**
     * The staged zorgtraject being answered about — what `resolve` names it by
     */
    'stagedTrajectoryId': string;
    /**
     * How the delivery identifies the imported zorgtraject
     */
    'sourceRef': string;
    /**
     * The cliënt whose zorgtraject would be closed, as the delivery\'s staged cliënt row names them. This is a decision about one person\'s dossier, so it is never presented as a row id. 
     */
    'clientName': string;
    /**
     * The imported zorgtraject\'s number in the source, where it carries one
     */
    'incomingZorgtrajectNummer'?: string | null;
    /**
     * The imported zorgtraject\'s start date — the date the existing zorgtraject would be closed on. Null when the delivery states none, which is a row that cannot be applied at all until the required field is answered. 
     */
    'incomingStartdatum'?: string | null;
    /**
     * The practice\'s own zorgtraject that would be closed. Null once the row has been applied and closed nothing — a practice that closed it themselves in the meantime — which is what keeps this the record of what happened rather than of what was expected. 
     */
    'existingCareTrajectoryId'?: string | null;
    /**
     * That zorgtraject\'s number, so the list reads without a second request
     */
    'existingZorgtrajectNummer'?: string | null;
    /**
     * When the existing zorgtraject started
     */
    'existingStartdatum'?: string | null;
    'state': ImportTrajectoryOverlapState;
    /**
     * Whether the existing zorgtraject\'s number has already gone out (#4633): a declaratieregel on it sits in a batch that has been exported or submitted, or a regel of it is on an issued nota. An imported zorgtraject merges onto an existing one when the zorgtrajectnummers match, so an overlap is two different numbers and the answer decides which survives: `SUPERSEDE` leaves the imported number, `SKIP` the existing one. While this is false that is cosmetic; once it is true, `SKIP` keeps a number the verzekeraar may not know and `SUPERSEDE` changes the number the notas already issued were about. 
     */
    'existingDeclared': boolean;
    /**
     * The issued, non-credit notas carrying a regel of the existing zorgtraject, by invoice number and sorted — the ones to reissue when a `SUPERSEDE` changes the number they name. Empty when there are none. 
     */
    'existingInvoiceNumbers': Array<string>;
    /**
     * Whether the existing zorgtraject holds a verwijsbrief (#4672). A `SUPERSEDE` carries that letter onto the imported zorgtraject — no delivery links one of its own — and the closed zorgtraject keeps it too. Without the carry the imported zorgtraject would be declared as \"verwijsbrief ontbreekt\". 
     */
    'existingCarriesVerwijsbrief': boolean;
    /**
     * Where the verwijzing recorded on the existing zorgtraject differs from the one the delivery brings, per part (#4672). Empty when they agree, or when the existing zorgtraject records no verwijzing. What a `SUPERSEDE` declares is the delivery\'s verwijzing — so where the existing zorgtraject also carries a verwijsbrief, a difference here is a letter that may not match what is declared beside it. 
     */
    'verwijzingDifferences': Array<ImportVerwijzingDifference>;
    /**
     * Whether the existing zorgtraject is a heraanmelding rather than the same episode: the imported zorgtraject starts more than 31 days before it, and the existing one records a verwijzing the delivery does not share — a verwijsdatum more than 31 days from the delivery\'s (or none in the delivery), or another verwijzer AGB-code. A `SUPERSEDE` would close the practice\'s new episode in favour of the old one and declare its consults with the old verwijzing; `SKIP` keeps it. `resolve-remaining` never answers such a row — it stays unanswered until it is answered on its own. 
     */
    'heraanmelding': boolean;
    /**
     * When the answer was given; absent while the overlap is unanswered
     */
    'answeredAt'?: string | null;
    /**
     * Who answered. Recorded because the answer alters a clinical record the import never owned and a revert does not reopen it. 
     */
    'answeredByName'?: string | null;
}


/**
 * Every overlap of one run, and how far the practice has got with them. 
 */
export interface ImportTrajectoryOverlapReport {
    /**
     * The run these overlaps belong to
     */
    'runId': string;
    'state': ImportRunState;
    /**
     * The overlaps, in the delivery\'s own order, so a list read twice reads the same way. 
     */
    'overlaps': Array<ImportTrajectoryOverlapEntry>;
    /**
     * How many overlaps nobody has answered. **While this is above zero the run cannot be approved** — closing a cliënt\'s active zorgtraject is never done by default. 
     */
    'unanswered': number;
    /**
     * How many existing zorgtrajecten the answers so far will close
     */
    'superseding': number;
    /**
     * How many imported zorgtrajecten the answers so far leave out of the run
     */
    'skipped': number;
}


/**
 * Where one overlap stands: nobody has answered it yet (`UNANSWERED`, which blocks the run\'s approval), the existing zorgtraject will be closed, or the imported zorgtraject is not imported at all. 
 */

export const ImportTrajectoryOverlapState = {
    Unanswered: 'UNANSWERED',
    Supersede: 'SUPERSEDE',
    Skip: 'SKIP'
} as const;

export type ImportTrajectoryOverlapState = typeof ImportTrajectoryOverlapState[keyof typeof ImportTrajectoryOverlapState];


/**
 * How far the archive of this delivery has been taken apart, and when that last moved (#4050).  Absent for a run whose archive nothing has started on, and for an import that carries no archive at all. Present from the moment the explode is claimed — including afterwards, where it describes what the delivery turned out to hold.  **There is no percentage, and there cannot be one.** A zip read as a stream is sequential-only, so the number of entries an archive holds is not known until the last one has gone past — the count is the whole of what can honestly be reported while it runs. What answers \"is this still moving\" is `lastProgressAt` beside it. 
 */
export interface ImportUnpackProgress {
    /**
     * Entries taken out of the archive so far — staged, stepped over and refused alike.  Per attempt rather than cumulative: an explode interrupted by a recycled instance re-streams the archive from the beginning, because a sequential archive offers no way back to a middle entry. So this restarts at 0 on a resumed pass and climbs back quickly through what an earlier attempt already staged. 
     */
    'entries': number;
    /**
     * Uncompressed bytes read out of the archive so far. Moves while a single large entry streams, which is what tells a large file apart from a read that has stopped — the second half of a 2 GB delivery is a handful of entries at a few tens of MB a minute. 
     */
    'bytes': number;
    /**
     * When the explode last reported. While the phase is `UNPACKING` this is what says the pass is alive: it is written as the bytes go past, so a value that has stopped moving is a stalled explode rather than a slow one. Another instance takes the run over once it is older than the stale-claim window, and re-streams the archive from the beginning. 
     */
    'lastProgressAt': string;
}
/**
 * The run that was opened, and the upload that fills it
 */
export interface ImportUploadTicket {
    /**
     * The import run the archive belongs to, and how it is followed afterwards
     */
    'runId': string;
    /**
     * The object the archive lands on, within the import upload bucket. Derived from the run, so it is stated here rather than chosen by the caller. 
     */
    'objectPath': string;
    /**
     * Signed URL that **starts** the resumable upload - `POST`, empty body, with the header `x-goog-resumable: start`. The bytes go to the session URI that answers. 
     */
    'uploadUrl': string;
    /**
     * When `uploadUrl` stops being accepted. It bounds starting the upload, not finishing it: a session already opened is not cut off when this passes. 
     */
    'expiresAt': string;
}
/**
 * One part of a verwijzing on which the existing zorgtraject and the delivery disagree.
 */
export interface ImportVerwijzingDifference {
    'field': ImportVerwijzingField;
    /**
     * What the existing zorgtraject records. A `VERWIJSDATUM` is an ISO date (`yyyy-MM-dd`). Null when it records nothing for this part. 
     */
    'existing'?: string | null;
    /**
     * What the delivery brings, in the same form. Null when it brings nothing.
     */
    'incoming'?: string | null;
}


/**
 * A part of a verwijzing an overlap compares: who referred (`VERWIJZER`, the verwijzer\'s name), their AGB-code, and the verwijsdatum. 
 */

export const ImportVerwijzingField = {
    Verwijzer: 'VERWIJZER',
    AgbCode: 'AGB_CODE',
    Verwijsdatum: 'VERWIJSDATUM'
} as const;

export type ImportVerwijzingField = typeof ImportVerwijzingField[keyof typeof ImportVerwijzingField];


/**
 * Opens an import for an archive that is uploaded straight to storage.  The archive\'s *contents* are described by nothing the caller sends: its size is whatever actually arrives, and what is in it is decided by reading the bytes. What the caller does say is which EPD produced it — a declaration, which recognition then verifies. 
 */
export interface InitiateImportUploadRequest {
    /**
     * The archive as the practice knows it. Kept on the run so the import history names something recognisable; it deliberately does not decide where the object lands, so a path separator in it is refused rather than sanitised away. 
     */
    'fileName': string;
    'vendor': ImportEpdVendor;
}


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * NZA beroepscode. Defined here as well as on the users, contracts and appointment-types specs because a schema name is global across specs and a local `$ref` cannot cross one — the definitions must agree, and `openapi:schema-collisions` is what keeps them agreeing. 
 */

export const ProfessionCode = {
    ArtsBig3: 'ARTS_BIG3',
    ArtsBig14: 'ARTS_BIG14',
    GzPsych: 'GZ_PSYCH',
    KlinPsych: 'KLIN_PSYCH',
    Psychother: 'PSYCHOTHER',
    Verpl: 'VERPL',
    VerplSpec: 'VERPL_SPEC',
    Overig: 'OVERIG'
} as const;

export type ProfessionCode = typeof ProfessionCode[keyof typeof ProfessionCode];


/**
 * One answer about one activity type. Sending it again for the same type replaces the previous answer. 
 */
export interface ResolveImportAppointmentTypeRequest {
    /**
     * The activity type being answered, exactly as the report spells it
     */
    'sourceType': string;
    'decision': ImportAppointmentTypeDecision;
    /**
     * The Scrivio appointment type, for a `MAPPED` decision — and with it the billing category these appointments will carry. Required then, and refused with `400` for a `SKIPPED` one. 
     */
    'appointmentTypeId'?: string | null;
}


/**
 * One answer about one name. Sending it again for the same name replaces the previous answer. 
 */
export interface ResolveImportPractitionerRequest {
    /**
     * The name being answered, exactly as the report spells it
     */
    'sourceName': string;
    'decision': ImportPractitionerDecision;
    /**
     * The Scrivio user, for a `MAPPED` decision. Required then, and refused with `400` for a `SKIPPED` one — a skip carrying a user reads as a mapping the import silently ignored. 
     */
    'userId'?: string | null;
}


/**
 * One answer about one setting code. Sending it again for the same code replaces the previous answer. 
 */
export interface ResolveImportSettingRequest {
    /**
     * The setting code being answered, exactly as the report spells it; absent or null for the zorgtrajecten whose delivery names no setting. 
     */
    'sourceCode'?: string | null;
    /**
     * The behandelsetting these zorgtrajecten are created under
     */
    'settingId': string;
}
/**
 * One answer about one overlapping zorgtraject. Sending it again for the same zorgtraject replaces the previous answer. 
 */
export interface ResolveImportTrajectoryOverlapRequest {
    /**
     * The staged zorgtraject being answered, as the report names it
     */
    'stagedTrajectoryId': string;
    'decision': ImportSupersedeDecision;
}


/**
 * One answer, given to every overlap that is still unanswered and is not a heraanmelding. Answers already given are left as they are. 
 */
export interface ResolveRemainingImportTrajectoryOverlapsRequest {
    'decision': ImportSupersedeDecision;
}


/**
 * Type verwijzer; the same schema as `openapi-zorgtrajecten.yaml` declares.
 */

export const VerwijzerType = {
    Huisarts: 'HUISARTS',
    Specialist: 'SPECIALIST',
    Bedrijfsarts: 'BEDRIJFSARTS',
    Other: 'OTHER'
} as const;

export type VerwijzerType = typeof VerwijzerType[keyof typeof VerwijzerType];


/**
 * A single validation finding, attributed to a sheet and (where known) a row and column
 */
export interface ZpmFinding {
    /**
     * Sheet the finding applies to
     */
    'sheet': string;
    /**
     * Excel row number (1-based), or null for sheet-level findings
     */
    'row'?: number | null;
    /**
     * Column name, or null when the finding is not tied to one column
     */
    'column'?: string | null;
    'severity': ImportFindingSeverity;
    'code': ImportFindingCode;
    /**
     * Human-readable explanation in Dutch
     */
    'message': string;
    /**
     * The offending cell value, or null when not applicable
     */
    'value'?: string | null;
}


/**
 * Created/updated/skipped counts for a single master-data entity
 */
export interface ZpmImportEntityResult {
    /**
     * Sheet name of the entity as it appears in the workbook
     */
    'entity': string;
    /**
     * Number of records created for this entity
     */
    'created': number;
    /**
     * Number of records updated for this entity
     */
    'updated': number;
    /**
     * Number of records skipped for this entity
     */
    'skipped': number;
}
/**
 * Result of committing a ZPM GGZ import workbook: how many records were created, updated or skipped, broken down per master-data entity. 
 */
export interface ZpmImportResult {
    /**
     * Name of the committed workbook
     */
    'fileName': string;
    /**
     * Per-entity counts, one entry per master-data sheet
     */
    'entities': Array<ZpmImportEntityResult>;
    /**
     * Total number of records created across all entities
     */
    'totalCreated': number;
    /**
     * Total number of records updated across all entities
     */
    'totalUpdated': number;
    /**
     * Total number of records skipped across all entities
     */
    'totalSkipped': number;
}
/**
 * Row and finding counts for a single sheet
 */
export interface ZpmSheetSummary {
    /**
     * Sheet name as it appears in the workbook
     */
    'sheet': string;
    /**
     * Number of data rows read (spec rows and the example row excluded)
     */
    'rows': number;
    /**
     * Number of ERROR findings on this sheet
     */
    'errors': number;
    /**
     * Number of WARNING findings on this sheet
     */
    'warnings': number;
}
/**
 * Result of validating a ZPM GGZ import workbook
 */
export interface ZpmValidationReport {
    /**
     * Name of the uploaded workbook
     */
    'fileName': string;
    'summary': ZpmValidationSummary;
    /**
     * All findings, ordered by sheet and row
     */
    'findings': Array<ZpmFinding>;
}
/**
 * Aggregated counts over the whole workbook
 */
export interface ZpmValidationSummary {
    /**
     * Per-sheet counts, one entry per validated sheet
     */
    'sheets': Array<ZpmSheetSummary>;
    /**
     * Total number of ERROR findings across all sheets
     */
    'totalErrors': number;
    /**
     * Total number of WARNING findings across all sheets
     */
    'totalWarnings': number;
    /**
     * True when the workbook has no ERROR findings
     */
    'readyToImport': boolean;
}

/**
 * AgendaImportsApi - axios parameter creator
 */
export const AgendaImportsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and stages its appointments for the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. WARNING findings — duplicate bookings, overlaps — never block the commit.  The commit **stages** the appointments and returns as soon as they are written: no appointment exists in the agenda when this responds, and none will until the run is approved. The response names the run they were staged under, which is what `GET /api/v1/imports/{runId}/preview` describes and `POST .../approve` releases; a scheduled worker then creates them a batch at a time (ADR-039), and the agenda fills gradually afterwards. Rejecting the run instead leaves no appointment at all.  Staging is all-or-nothing: a workbook with an ERROR stages nothing.  Requires the `imports.agenda.commit` permission. 
         * @summary Commit an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitAgendaImport: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('commitAgendaImport', 'file', file)
            const localVarPath = `/api/v1/imports/agenda/commit`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the caller\'s organization\'s agenda import run that has not finished yet, so a screen reopened while a run is in flight picks that run up instead of offering a fresh upload. The import runs on the server, not in the browser: closing the page does not interrupt it.  A run whose preview has not been decided is not over either, so it is answered here too — that is what sends someone who closed the tab mid-decision back to their preview.  Answers `204` when no such run exists — a finished run is not \"active\" and is followed by id instead.  Requires the `imports.agenda` permission. 
         * @summary The agenda import run that is not over yet, if there is one
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveAgendaImportRun: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/imports/agenda/runs/active`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * What reverting this run would come to, **before** anything is started: how many appointments would be removed, how many would stay put because they carry work the import did not create, how many are already gone, and how many are still queued and would simply not be created.  Read-only — nothing about the run or its appointments changes. A practice about to remove some 1.800 appointments irreversibly gets to see the numbers first.  Appointments that stay put are grouped by the reason that blocks them, each with a capped list of examples: a run whose every appointment blocks must not hand a screen thousands of rows, and the counts stay the real totals.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so.  Requires the `imports.agenda` permission. 
         * @summary Wat het terugdraaien van deze import zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaImportRevertPreflight: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getAgendaImportRevertPreflight', 'runId', runId)
            const localVarPath = `/api/v1/imports/agenda/runs/{runId}/revert-preflight`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * How far the worker got with the run: how many appointments it staged, how many it has created, and how many it could not create with the reason for each.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Requires the `imports.agenda` permission. 
         * @summary Progress of one agenda import run
         * @param {string} runId The run returned by the commit endpoint
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getAgendaImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/agenda/runs/{runId}`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The caller\'s organization\'s agenda import runs, newest first — the history a practice picks an earlier import from, for instance to take it back.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.agenda` permission. 
         * @summary Eerdere agenda-imports van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaImportRuns: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/imports/agenda/runs`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Starts taking the run back and returns immediately, exactly as the commit does: the appointments are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the agenda empties gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the import worker no longer creates them, and the run moves to `REVERTING`. Follow it with the run endpoint, as an import is followed.  An appointment carrying work the import did not create — a dossiernotitie, transcript, opname, formulier or declaratieregel — is refused rather than removed, and stays in the agenda. Use `revert-preflight` to see how many that is before starting.  Reverting a run that is already being reverted, or has been, answers `409`.  Requires the `imports.agenda.commit` permission — the permission that put these appointments in the agenda in the first place. 
         * @summary Draai een eerdere agenda-import terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertAgendaImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('revertAgendaImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/agenda/runs/{runId}/revert`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no appointment is written. The report is scoped to the caller\'s own organization.  Requires the `imports.agenda` permission. 
         * @summary Validate an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        validateAgendaImport: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('validateAgendaImport', 'file', file)
            const localVarPath = `/api/v1/imports/agenda/validate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AgendaImportsApi - functional programming interface
 */
export const AgendaImportsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AgendaImportsApiAxiosParamCreator(configuration)
    return {
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and stages its appointments for the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. WARNING findings — duplicate bookings, overlaps — never block the commit.  The commit **stages** the appointments and returns as soon as they are written: no appointment exists in the agenda when this responds, and none will until the run is approved. The response names the run they were staged under, which is what `GET /api/v1/imports/{runId}/preview` describes and `POST .../approve` releases; a scheduled worker then creates them a batch at a time (ADR-039), and the agenda fills gradually afterwards. Rejecting the run instead leaves no appointment at all.  Staging is all-or-nothing: a workbook with an ERROR stages nothing.  Requires the `imports.agenda.commit` permission. 
         * @summary Commit an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async commitAgendaImport(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaImportResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.commitAgendaImport(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.commitAgendaImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the caller\'s organization\'s agenda import run that has not finished yet, so a screen reopened while a run is in flight picks that run up instead of offering a fresh upload. The import runs on the server, not in the browser: closing the page does not interrupt it.  A run whose preview has not been decided is not over either, so it is answered here too — that is what sends someone who closed the tab mid-decision back to their preview.  Answers `204` when no such run exists — a finished run is not \"active\" and is followed by id instead.  Requires the `imports.agenda` permission. 
         * @summary The agenda import run that is not over yet, if there is one
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActiveAgendaImportRun(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaImportRunStatus>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActiveAgendaImportRun(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.getActiveAgendaImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * What reverting this run would come to, **before** anything is started: how many appointments would be removed, how many would stay put because they carry work the import did not create, how many are already gone, and how many are still queued and would simply not be created.  Read-only — nothing about the run or its appointments changes. A practice about to remove some 1.800 appointments irreversibly gets to see the numbers first.  Appointments that stay put are grouped by the reason that blocks them, each with a capped list of examples: a run whose every appointment blocks must not hand a screen thousands of rows, and the counts stay the real totals.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so.  Requires the `imports.agenda` permission. 
         * @summary Wat het terugdraaien van deze import zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAgendaImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaImportRevertPreflight>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAgendaImportRevertPreflight(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.getAgendaImportRevertPreflight']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * How far the worker got with the run: how many appointments it staged, how many it has created, and how many it could not create with the reason for each.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Requires the `imports.agenda` permission. 
         * @summary Progress of one agenda import run
         * @param {string} runId The run returned by the commit endpoint
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAgendaImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaImportRunStatus>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAgendaImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.getAgendaImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The caller\'s organization\'s agenda import runs, newest first — the history a practice picks an earlier import from, for instance to take it back.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.agenda` permission. 
         * @summary Eerdere agenda-imports van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAgendaImportRuns(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AgendaImportRunSummary>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAgendaImportRuns(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.listAgendaImportRuns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Starts taking the run back and returns immediately, exactly as the commit does: the appointments are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the agenda empties gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the import worker no longer creates them, and the run moves to `REVERTING`. Follow it with the run endpoint, as an import is followed.  An appointment carrying work the import did not create — a dossiernotitie, transcript, opname, formulier or declaratieregel — is refused rather than removed, and stays in the agenda. Use `revert-preflight` to see how many that is before starting.  Reverting a run that is already being reverted, or has been, answers `409`.  Requires the `imports.agenda.commit` permission — the permission that put these appointments in the agenda in the first place. 
         * @summary Draai een eerdere agenda-import terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revertAgendaImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaImportRunStatus>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revertAgendaImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.revertAgendaImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no appointment is written. The report is scoped to the caller\'s own organization.  Requires the `imports.agenda` permission. 
         * @summary Validate an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async validateAgendaImport(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaValidationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.validateAgendaImport(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaImportsApi.validateAgendaImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AgendaImportsApi - factory interface
 */
export const AgendaImportsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AgendaImportsApiFp(configuration)
    return {
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and stages its appointments for the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. WARNING findings — duplicate bookings, overlaps — never block the commit.  The commit **stages** the appointments and returns as soon as they are written: no appointment exists in the agenda when this responds, and none will until the run is approved. The response names the run they were staged under, which is what `GET /api/v1/imports/{runId}/preview` describes and `POST .../approve` releases; a scheduled worker then creates them a batch at a time (ADR-039), and the agenda fills gradually afterwards. Rejecting the run instead leaves no appointment at all.  Staging is all-or-nothing: a workbook with an ERROR stages nothing.  Requires the `imports.agenda.commit` permission. 
         * @summary Commit an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitAgendaImport(file: File, options?: RawAxiosRequestConfig): AxiosPromise<AgendaImportResult> {
            return localVarFp.commitAgendaImport(file, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the caller\'s organization\'s agenda import run that has not finished yet, so a screen reopened while a run is in flight picks that run up instead of offering a fresh upload. The import runs on the server, not in the browser: closing the page does not interrupt it.  A run whose preview has not been decided is not over either, so it is answered here too — that is what sends someone who closed the tab mid-decision back to their preview.  Answers `204` when no such run exists — a finished run is not \"active\" and is followed by id instead.  Requires the `imports.agenda` permission. 
         * @summary The agenda import run that is not over yet, if there is one
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveAgendaImportRun(options?: RawAxiosRequestConfig): AxiosPromise<AgendaImportRunStatus> {
            return localVarFp.getActiveAgendaImportRun(options).then((request) => request(axios, basePath));
        },
        /**
         * What reverting this run would come to, **before** anything is started: how many appointments would be removed, how many would stay put because they carry work the import did not create, how many are already gone, and how many are still queued and would simply not be created.  Read-only — nothing about the run or its appointments changes. A practice about to remove some 1.800 appointments irreversibly gets to see the numbers first.  Appointments that stay put are grouped by the reason that blocks them, each with a capped list of examples: a run whose every appointment blocks must not hand a screen thousands of rows, and the counts stay the real totals.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so.  Requires the `imports.agenda` permission. 
         * @summary Wat het terugdraaien van deze import zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<AgendaImportRevertPreflight> {
            return localVarFp.getAgendaImportRevertPreflight(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * How far the worker got with the run: how many appointments it staged, how many it has created, and how many it could not create with the reason for each.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Requires the `imports.agenda` permission. 
         * @summary Progress of one agenda import run
         * @param {string} runId The run returned by the commit endpoint
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<AgendaImportRunStatus> {
            return localVarFp.getAgendaImportRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * The caller\'s organization\'s agenda import runs, newest first — the history a practice picks an earlier import from, for instance to take it back.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.agenda` permission. 
         * @summary Eerdere agenda-imports van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaImportRuns(options?: RawAxiosRequestConfig): AxiosPromise<Array<AgendaImportRunSummary>> {
            return localVarFp.listAgendaImportRuns(options).then((request) => request(axios, basePath));
        },
        /**
         * Starts taking the run back and returns immediately, exactly as the commit does: the appointments are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the agenda empties gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the import worker no longer creates them, and the run moves to `REVERTING`. Follow it with the run endpoint, as an import is followed.  An appointment carrying work the import did not create — a dossiernotitie, transcript, opname, formulier or declaratieregel — is refused rather than removed, and stays in the agenda. Use `revert-preflight` to see how many that is before starting.  Reverting a run that is already being reverted, or has been, answers `409`.  Requires the `imports.agenda.commit` permission — the permission that put these appointments in the agenda in the first place. 
         * @summary Draai een eerdere agenda-import terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertAgendaImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<AgendaImportRunStatus> {
            return localVarFp.revertAgendaImportRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads the agenda planning workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no appointment is written. The report is scoped to the caller\'s own organization.  Requires the `imports.agenda` permission. 
         * @summary Validate an agenda planning workbook
         * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        validateAgendaImport(file: File, options?: RawAxiosRequestConfig): AxiosPromise<AgendaValidationReport> {
            return localVarFp.validateAgendaImport(file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AgendaImportsApi - object-oriented interface
 */
export class AgendaImportsApi extends BaseAPI {
    /**
     * Uploads the agenda planning workbook (`.xlsx`) and stages its appointments for the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. WARNING findings — duplicate bookings, overlaps — never block the commit.  The commit **stages** the appointments and returns as soon as they are written: no appointment exists in the agenda when this responds, and none will until the run is approved. The response names the run they were staged under, which is what `GET /api/v1/imports/{runId}/preview` describes and `POST .../approve` releases; a scheduled worker then creates them a batch at a time (ADR-039), and the agenda fills gradually afterwards. Rejecting the run instead leaves no appointment at all.  Staging is all-or-nothing: a workbook with an ERROR stages nothing.  Requires the `imports.agenda.commit` permission. 
     * @summary Commit an agenda planning workbook
     * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public commitAgendaImport(file: File, options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).commitAgendaImport(file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the caller\'s organization\'s agenda import run that has not finished yet, so a screen reopened while a run is in flight picks that run up instead of offering a fresh upload. The import runs on the server, not in the browser: closing the page does not interrupt it.  A run whose preview has not been decided is not over either, so it is answered here too — that is what sends someone who closed the tab mid-decision back to their preview.  Answers `204` when no such run exists — a finished run is not \"active\" and is followed by id instead.  Requires the `imports.agenda` permission. 
     * @summary The agenda import run that is not over yet, if there is one
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getActiveAgendaImportRun(options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).getActiveAgendaImportRun(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * What reverting this run would come to, **before** anything is started: how many appointments would be removed, how many would stay put because they carry work the import did not create, how many are already gone, and how many are still queued and would simply not be created.  Read-only — nothing about the run or its appointments changes. A practice about to remove some 1.800 appointments irreversibly gets to see the numbers first.  Appointments that stay put are grouped by the reason that blocks them, each with a capped list of examples: a run whose every appointment blocks must not hand a screen thousands of rows, and the counts stay the real totals.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so.  Requires the `imports.agenda` permission. 
     * @summary Wat het terugdraaien van deze import zou doen
     * @param {string} runId The run whose revert is being considered
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAgendaImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).getAgendaImportRevertPreflight(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * How far the worker got with the run: how many appointments it staged, how many it has created, and how many it could not create with the reason for each.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Requires the `imports.agenda` permission. 
     * @summary Progress of one agenda import run
     * @param {string} runId The run returned by the commit endpoint
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAgendaImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).getAgendaImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The caller\'s organization\'s agenda import runs, newest first — the history a practice picks an earlier import from, for instance to take it back.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.agenda` permission. 
     * @summary Eerdere agenda-imports van de organisatie
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAgendaImportRuns(options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).listAgendaImportRuns(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Starts taking the run back and returns immediately, exactly as the commit does: the appointments are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the agenda empties gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the import worker no longer creates them, and the run moves to `REVERTING`. Follow it with the run endpoint, as an import is followed.  An appointment carrying work the import did not create — a dossiernotitie, transcript, opname, formulier or declaratieregel — is refused rather than removed, and stays in the agenda. Use `revert-preflight` to see how many that is before starting.  Reverting a run that is already being reverted, or has been, answers `409`.  Requires the `imports.agenda.commit` permission — the permission that put these appointments in the agenda in the first place. 
     * @summary Draai een eerdere agenda-import terug
     * @param {string} runId The run to take back
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revertAgendaImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).revertAgendaImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads the agenda planning workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no appointment is written. The report is scoped to the caller\'s own organization.  Requires the `imports.agenda` permission. 
     * @summary Validate an agenda planning workbook
     * @param {File} file The agenda planning workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public validateAgendaImport(file: File, options?: RawAxiosRequestConfig) {
        return AgendaImportsApiFp(this.configuration).validateAgendaImport(file, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContractTariffImportsApi - axios parameter creator
 */
export const ContractTariffImportsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Uploads an insurer\'s per-prestatiecode tariff sheet (`.xlsx`) and **stages** its lines as price rules for one contract. No tariff changes when this responds, and none will until the run is approved (ADR-039) — which is the whole reason this is an import rather than a bulk write: a tariff is the amount a declaration carries to an insurer, so \"apply and see\" is not an option.  **There is no one layout.** The header row is found rather than assumed — the two sheets for 2026 put theirs on row 4 (behind a merged title) and on row 2, and spell the columns `Prestatie code` / `Gecontracteerd tarief` and `Prestatiecode` / `Tarief` respectively. Only a file in which no header naming both a code column and a tariff column can be found is refused, with a `422`; everything a single line can be wrong about is a warning beside that line.  The response is the preview: how many lines would be **created**, **updated**, left **unchanged** and **rejected**, how many the reader **skipped**, and how many prestatiecodes the contract already prices that this sheet does not mention — because a silent omission is how half a tariff list gets loaded. `GET /api/v1/imports/{runId}/preview` and its drilldown answer for the same run in the shape every import shares; this adds the four numbers a tariff list is read for.  Requires the `imports.contract_tariffs` permission, and the contract must belong to the caller\'s organization. 
         * @summary Laad de tarievenlijst van een verzekeraar op één contract
         * @param {string} contractId The contract these tariffs were agreed for
         * @param {File} file The insurer\\\&#39;s tariff sheet (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitContractTariffImport: async (contractId: string, file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('commitContractTariffImport', 'contractId', contractId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('commitContractTariffImport', 'file', file)
            const localVarPath = `/api/v1/imports/contract-tariffs/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The same preview the commit answered with, re-read for a screen that was reopened — and re-read **against the contract as it now stands**, which is what makes it the answer to \"did this actually land\": once the run is applied, every line it wrote reports `unchanged`.  Requires the `imports.contract_tariffs` permission. A run of another organization, or one that is not a contract tariff import, is a `404`. 
         * @summary De tariefimport van een contract, zoals hij er nu voor staat
         * @param {string} runId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractTariffImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getContractTariffImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/contract-tariffs/runs/{runId}`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContractTariffImportsApi - functional programming interface
 */
export const ContractTariffImportsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContractTariffImportsApiAxiosParamCreator(configuration)
    return {
        /**
         * Uploads an insurer\'s per-prestatiecode tariff sheet (`.xlsx`) and **stages** its lines as price rules for one contract. No tariff changes when this responds, and none will until the run is approved (ADR-039) — which is the whole reason this is an import rather than a bulk write: a tariff is the amount a declaration carries to an insurer, so \"apply and see\" is not an option.  **There is no one layout.** The header row is found rather than assumed — the two sheets for 2026 put theirs on row 4 (behind a merged title) and on row 2, and spell the columns `Prestatie code` / `Gecontracteerd tarief` and `Prestatiecode` / `Tarief` respectively. Only a file in which no header naming both a code column and a tariff column can be found is refused, with a `422`; everything a single line can be wrong about is a warning beside that line.  The response is the preview: how many lines would be **created**, **updated**, left **unchanged** and **rejected**, how many the reader **skipped**, and how many prestatiecodes the contract already prices that this sheet does not mention — because a silent omission is how half a tariff list gets loaded. `GET /api/v1/imports/{runId}/preview` and its drilldown answer for the same run in the shape every import shares; this adds the four numbers a tariff list is read for.  Requires the `imports.contract_tariffs` permission, and the contract must belong to the caller\'s organization. 
         * @summary Laad de tarievenlijst van een verzekeraar op één contract
         * @param {string} contractId The contract these tariffs were agreed for
         * @param {File} file The insurer\\\&#39;s tariff sheet (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async commitContractTariffImport(contractId: string, file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractTariffImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.commitContractTariffImport(contractId, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractTariffImportsApi.commitContractTariffImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The same preview the commit answered with, re-read for a screen that was reopened — and re-read **against the contract as it now stands**, which is what makes it the answer to \"did this actually land\": once the run is applied, every line it wrote reports `unchanged`.  Requires the `imports.contract_tariffs` permission. A run of another organization, or one that is not a contract tariff import, is a `404`. 
         * @summary De tariefimport van een contract, zoals hij er nu voor staat
         * @param {string} runId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContractTariffImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractTariffImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContractTariffImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractTariffImportsApi.getContractTariffImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContractTariffImportsApi - factory interface
 */
export const ContractTariffImportsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContractTariffImportsApiFp(configuration)
    return {
        /**
         * Uploads an insurer\'s per-prestatiecode tariff sheet (`.xlsx`) and **stages** its lines as price rules for one contract. No tariff changes when this responds, and none will until the run is approved (ADR-039) — which is the whole reason this is an import rather than a bulk write: a tariff is the amount a declaration carries to an insurer, so \"apply and see\" is not an option.  **There is no one layout.** The header row is found rather than assumed — the two sheets for 2026 put theirs on row 4 (behind a merged title) and on row 2, and spell the columns `Prestatie code` / `Gecontracteerd tarief` and `Prestatiecode` / `Tarief` respectively. Only a file in which no header naming both a code column and a tariff column can be found is refused, with a `422`; everything a single line can be wrong about is a warning beside that line.  The response is the preview: how many lines would be **created**, **updated**, left **unchanged** and **rejected**, how many the reader **skipped**, and how many prestatiecodes the contract already prices that this sheet does not mention — because a silent omission is how half a tariff list gets loaded. `GET /api/v1/imports/{runId}/preview` and its drilldown answer for the same run in the shape every import shares; this adds the four numbers a tariff list is read for.  Requires the `imports.contract_tariffs` permission, and the contract must belong to the caller\'s organization. 
         * @summary Laad de tarievenlijst van een verzekeraar op één contract
         * @param {string} contractId The contract these tariffs were agreed for
         * @param {File} file The insurer\\\&#39;s tariff sheet (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitContractTariffImport(contractId: string, file: File, options?: RawAxiosRequestConfig): AxiosPromise<ContractTariffImportPreview> {
            return localVarFp.commitContractTariffImport(contractId, file, options).then((request) => request(axios, basePath));
        },
        /**
         * The same preview the commit answered with, re-read for a screen that was reopened — and re-read **against the contract as it now stands**, which is what makes it the answer to \"did this actually land\": once the run is applied, every line it wrote reports `unchanged`.  Requires the `imports.contract_tariffs` permission. A run of another organization, or one that is not a contract tariff import, is a `404`. 
         * @summary De tariefimport van een contract, zoals hij er nu voor staat
         * @param {string} runId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractTariffImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ContractTariffImportPreview> {
            return localVarFp.getContractTariffImportRun(runId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContractTariffImportsApi - object-oriented interface
 */
export class ContractTariffImportsApi extends BaseAPI {
    /**
     * Uploads an insurer\'s per-prestatiecode tariff sheet (`.xlsx`) and **stages** its lines as price rules for one contract. No tariff changes when this responds, and none will until the run is approved (ADR-039) — which is the whole reason this is an import rather than a bulk write: a tariff is the amount a declaration carries to an insurer, so \"apply and see\" is not an option.  **There is no one layout.** The header row is found rather than assumed — the two sheets for 2026 put theirs on row 4 (behind a merged title) and on row 2, and spell the columns `Prestatie code` / `Gecontracteerd tarief` and `Prestatiecode` / `Tarief` respectively. Only a file in which no header naming both a code column and a tariff column can be found is refused, with a `422`; everything a single line can be wrong about is a warning beside that line.  The response is the preview: how many lines would be **created**, **updated**, left **unchanged** and **rejected**, how many the reader **skipped**, and how many prestatiecodes the contract already prices that this sheet does not mention — because a silent omission is how half a tariff list gets loaded. `GET /api/v1/imports/{runId}/preview` and its drilldown answer for the same run in the shape every import shares; this adds the four numbers a tariff list is read for.  Requires the `imports.contract_tariffs` permission, and the contract must belong to the caller\'s organization. 
     * @summary Laad de tarievenlijst van een verzekeraar op één contract
     * @param {string} contractId The contract these tariffs were agreed for
     * @param {File} file The insurer\\\&#39;s tariff sheet (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public commitContractTariffImport(contractId: string, file: File, options?: RawAxiosRequestConfig) {
        return ContractTariffImportsApiFp(this.configuration).commitContractTariffImport(contractId, file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The same preview the commit answered with, re-read for a screen that was reopened — and re-read **against the contract as it now stands**, which is what makes it the answer to \"did this actually land\": once the run is applied, every line it wrote reports `unchanged`.  Requires the `imports.contract_tariffs` permission. A run of another organization, or one that is not a contract tariff import, is a `404`. 
     * @summary De tariefimport van een contract, zoals hij er nu voor staat
     * @param {string} runId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getContractTariffImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return ContractTariffImportsApiFp(this.configuration).getContractTariffImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportAppointmentTypesApi - axios parameter creator
 */
export const ImportAppointmentTypesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every activity type this run\'s delivery uses, how many appointments came in under each, and which Scrivio appointment type the practice says it is.  The list is **derived from the delivery**, never from a list configured in advance: the layout of an EPD export is a property of the product, its vocabulary is a property of the practice. ZorgAdmin files a session\'s type and a treatment group\'s name in one column, so a group name is one of these entries too.  **The billing category is the point.** `billable_consult_v` only considers an appointment whose appointment type maps to `INSURED` or `PRIVATE`, so the type chosen here is what decides whether years of imported history arrive as *te declareren*. Every candidate carries its category, and so does every answer, so no mapping is made blind — and **no type is guessed**: one nobody has answered for refuses the run\'s approval.  Where the delivery\'s name is exactly one of the organization\'s own appointment type names, that type is offered as `suggested…`. A suggestion is not a decision: a matching name says nothing about the billing category behind it.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De activiteitsoorten van deze levering, en welk afspraaktype elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportAppointmentTypes: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportAppointmentTypes', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/appointment-types`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers one activity type, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak of that type the chosen appointment type\'s id — and with it the billing category everything downstream reads — and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them.  **Both directions are available for as long as the run is awaiting approval.** Answering a type a second time replaces the first answer. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Koppel één activiteitsoort aan een afspraaktype, of sla hem over
         * @param {string} runId The run the activity type belongs to
         * @param {ResolveImportAppointmentTypeRequest} resolveImportAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportAppointmentType: async (runId: string, resolveImportAppointmentTypeRequest: ResolveImportAppointmentTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('resolveImportAppointmentType', 'runId', runId)
            // verify required parameter 'resolveImportAppointmentTypeRequest' is not null or undefined
            assertParamExists('resolveImportAppointmentType', 'resolveImportAppointmentTypeRequest', resolveImportAppointmentTypeRequest)
            const localVarPath = `/api/v1/imports/{runId}/appointment-types/resolve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(resolveImportAppointmentTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportAppointmentTypesApi - functional programming interface
 */
export const ImportAppointmentTypesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportAppointmentTypesApiAxiosParamCreator(configuration)
    return {
        /**
         * Every activity type this run\'s delivery uses, how many appointments came in under each, and which Scrivio appointment type the practice says it is.  The list is **derived from the delivery**, never from a list configured in advance: the layout of an EPD export is a property of the product, its vocabulary is a property of the practice. ZorgAdmin files a session\'s type and a treatment group\'s name in one column, so a group name is one of these entries too.  **The billing category is the point.** `billable_consult_v` only considers an appointment whose appointment type maps to `INSURED` or `PRIVATE`, so the type chosen here is what decides whether years of imported history arrive as *te declareren*. Every candidate carries its category, and so does every answer, so no mapping is made blind — and **no type is guessed**: one nobody has answered for refuses the run\'s approval.  Where the delivery\'s name is exactly one of the organization\'s own appointment type names, that type is offered as `suggested…`. A suggestion is not a decision: a matching name says nothing about the billing category behind it.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De activiteitsoorten van deze levering, en welk afspraaktype elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportAppointmentTypes(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportAppointmentTypeReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportAppointmentTypes(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportAppointmentTypesApi.getImportAppointmentTypes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers one activity type, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak of that type the chosen appointment type\'s id — and with it the billing category everything downstream reads — and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them.  **Both directions are available for as long as the run is awaiting approval.** Answering a type a second time replaces the first answer. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Koppel één activiteitsoort aan een afspraaktype, of sla hem over
         * @param {string} runId The run the activity type belongs to
         * @param {ResolveImportAppointmentTypeRequest} resolveImportAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveImportAppointmentType(runId: string, resolveImportAppointmentTypeRequest: ResolveImportAppointmentTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportAppointmentTypeReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveImportAppointmentType(runId, resolveImportAppointmentTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportAppointmentTypesApi.resolveImportAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportAppointmentTypesApi - factory interface
 */
export const ImportAppointmentTypesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportAppointmentTypesApiFp(configuration)
    return {
        /**
         * Every activity type this run\'s delivery uses, how many appointments came in under each, and which Scrivio appointment type the practice says it is.  The list is **derived from the delivery**, never from a list configured in advance: the layout of an EPD export is a property of the product, its vocabulary is a property of the practice. ZorgAdmin files a session\'s type and a treatment group\'s name in one column, so a group name is one of these entries too.  **The billing category is the point.** `billable_consult_v` only considers an appointment whose appointment type maps to `INSURED` or `PRIVATE`, so the type chosen here is what decides whether years of imported history arrive as *te declareren*. Every candidate carries its category, and so does every answer, so no mapping is made blind — and **no type is guessed**: one nobody has answered for refuses the run\'s approval.  Where the delivery\'s name is exactly one of the organization\'s own appointment type names, that type is offered as `suggested…`. A suggestion is not a decision: a matching name says nothing about the billing category behind it.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De activiteitsoorten van deze levering, en welk afspraaktype elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportAppointmentTypes(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportAppointmentTypeReport> {
            return localVarFp.getImportAppointmentTypes(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers one activity type, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak of that type the chosen appointment type\'s id — and with it the billing category everything downstream reads — and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them.  **Both directions are available for as long as the run is awaiting approval.** Answering a type a second time replaces the first answer. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Koppel één activiteitsoort aan een afspraaktype, of sla hem over
         * @param {string} runId The run the activity type belongs to
         * @param {ResolveImportAppointmentTypeRequest} resolveImportAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportAppointmentType(runId: string, resolveImportAppointmentTypeRequest: ResolveImportAppointmentTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportAppointmentTypeReport> {
            return localVarFp.resolveImportAppointmentType(runId, resolveImportAppointmentTypeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportAppointmentTypesApi - object-oriented interface
 */
export class ImportAppointmentTypesApi extends BaseAPI {
    /**
     * Every activity type this run\'s delivery uses, how many appointments came in under each, and which Scrivio appointment type the practice says it is.  The list is **derived from the delivery**, never from a list configured in advance: the layout of an EPD export is a property of the product, its vocabulary is a property of the practice. ZorgAdmin files a session\'s type and a treatment group\'s name in one column, so a group name is one of these entries too.  **The billing category is the point.** `billable_consult_v` only considers an appointment whose appointment type maps to `INSURED` or `PRIVATE`, so the type chosen here is what decides whether years of imported history arrive as *te declareren*. Every candidate carries its category, and so does every answer, so no mapping is made blind — and **no type is guessed**: one nobody has answered for refuses the run\'s approval.  Where the delivery\'s name is exactly one of the organization\'s own appointment type names, that type is offered as `suggested…`. A suggestion is not a decision: a matching name says nothing about the billing category behind it.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De activiteitsoorten van deze levering, en welk afspraaktype elk daarvan is
     * @param {string} runId The run to read
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportAppointmentTypes(runId: string, options?: RawAxiosRequestConfig) {
        return ImportAppointmentTypesApiFp(this.configuration).getImportAppointmentTypes(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers one activity type, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak of that type the chosen appointment type\'s id — and with it the billing category everything downstream reads — and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them.  **Both directions are available for as long as the run is awaiting approval.** Answering a type a second time replaces the first answer. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Koppel één activiteitsoort aan een afspraaktype, of sla hem over
     * @param {string} runId The run the activity type belongs to
     * @param {ResolveImportAppointmentTypeRequest} resolveImportAppointmentTypeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveImportAppointmentType(runId: string, resolveImportAppointmentTypeRequest: ResolveImportAppointmentTypeRequest, options?: RawAxiosRequestConfig) {
        return ImportAppointmentTypesApiFp(this.configuration).resolveImportAppointmentType(runId, resolveImportAppointmentTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportApprovalApi - axios parameter creator
 */
export const ImportApprovalApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Releases the run\'s staged rows to the apply worker: they move from `STAGED` to `PENDING` and the run itself to `PENDING`. Nothing is written to any business table by this request — the worker applies the rows a batch at a time afterwards (ADR-039), so follow the preview as an import is followed.  **A run with a blocking finding cannot be approved** and answers `422`, mirroring the workbook imports: an ERROR anywhere in the run counts, including one about the delivery itself, which belongs to no entity type. Warnings never block.  **Unless the practice accepts, deliberately, that those rows will not land** (#4051). One blocking finding used to stop the whole delivery however unrelated it was to what the practice needed: FIER\'s dump carries 116 of them across zorgtrajecten, afspraken and vragenlijsten, and behind them sit the 422 cliënten, ~400 verzekeringen and 7.205 dossiernotities they have to declare from — none of which any of the 116 touches. For 94 of the 116 no corrected re-delivery can exist, because the answer is one only the practice has and the source system does not hold.  So an approval may carry an `ApproveImportRunRequest` stating how many rows it accepts will be skipped. Those rows become `SKIPPED` — terminal, keeping their findings, and deliberately not `FAILED`, because nothing was attempted — and everything else is released. The number must equal what the run currently holds (the sum of `blockedRows` over the preview\'s entities) or the request answers `409`: what the practice accepted has to be what they were shown.  An approval that accepts nothing states no `skipBlockedRows`, and may spell that as an absent body, a `{}` or an explicit `null` — all three mean the same thing (#4098).  `skippedRows` on the answer is what the run then records, beside a `total` that stays what was released.  **An unanswered practitioner name refuses it the same way.** It is not a finding — nothing is wrong with the source and a re-delivery would not fix it — it is a question only the practice can answer, through `POST /api/v1/imports/{runId}/practitioners/resolve`. While `ImportPractitionerReport.undecided` is above zero, approval answers `422` naming the names, because no name is ever silently guessed.  **An unanswered activity type refuses it too**, and for a stake that is larger still: the Scrivio appointment type an activity type maps to is what carries its **billing category**, and `billable_consult_v` reads that category to decide whether an appointment is declarable. Get it wrong and years of imported history reappear as *te declareren*. So while `ImportAppointmentTypeReport.undecided` is above zero, approval answers `422` naming the types — see `POST /api/v1/imports/{runId}/appointment-types/resolve`.  **An unanswered setting code refuses it as well** (#4769). The behandelsetting decides every prestatiecode on an imported zorgtraject, and there is no default — not even the organisation\'s own kwaliteitsstatuut. While `ImportSettingReport.undecided` is above zero, approval answers `422` naming the codes — see `POST /api/v1/imports/{runId}/settings/resolve`.  **And an unanswered zorgtraject overlap refuses it**, for the one thing on this screen a revert cannot take back (#4356): an imported zorgtraject closes the cliënt\'s own active one, and reopening it afterwards is not available. So while `ImportTrajectoryOverlapReport.unanswered` is above zero, approval answers `422` — see `POST /api/v1/imports/{runId}/trajectory-overlaps/resolve-remaining`, which is how a delivery that overlaps in bulk is answered in one act.  **Approving twice queues nothing twice.** A run that has already been approved answers `202` with what it now looks like and writes nothing: the rows it released are no longer `STAGED`, so a second approval has nothing to move. A run that was rejected, or one that has been reverted, answers `409` — those are decisions that have already been taken the other way.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Keur deze import goed en zet hem in de wachtrij
         * @param {string} runId The run to approve
         * @param {ApproveImportRunRequest} [approveImportRunRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        approveImportRun: async (runId: string, approveImportRunRequest?: ApproveImportRunRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('approveImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/approve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(approveImportRunRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Turns the run down: every staged row becomes `WITHDRAWN` and the run becomes `REJECTED`, which is terminal. **A rejected run applies nothing, ever** — its rows are invisible to every apply worker because of what their status is, and there is no transition back.  The rows are kept rather than deleted: what an import was going to do is part of the record of what it did, and the preview of a rejected run still answers. A delivery a practice wants after all is staged again as a new run.  Rejecting twice answers `200` and writes nothing. A run that was already approved answers `409`: its rows may already have been applied, and taking those back is a revert rather than a rejection.  Requires the same permission as approving. 
         * @summary Wijs deze import af
         * @param {string} runId The run to turn down
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rejectImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('rejectImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/reject`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportApprovalApi - functional programming interface
 */
export const ImportApprovalApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportApprovalApiAxiosParamCreator(configuration)
    return {
        /**
         * Releases the run\'s staged rows to the apply worker: they move from `STAGED` to `PENDING` and the run itself to `PENDING`. Nothing is written to any business table by this request — the worker applies the rows a batch at a time afterwards (ADR-039), so follow the preview as an import is followed.  **A run with a blocking finding cannot be approved** and answers `422`, mirroring the workbook imports: an ERROR anywhere in the run counts, including one about the delivery itself, which belongs to no entity type. Warnings never block.  **Unless the practice accepts, deliberately, that those rows will not land** (#4051). One blocking finding used to stop the whole delivery however unrelated it was to what the practice needed: FIER\'s dump carries 116 of them across zorgtrajecten, afspraken and vragenlijsten, and behind them sit the 422 cliënten, ~400 verzekeringen and 7.205 dossiernotities they have to declare from — none of which any of the 116 touches. For 94 of the 116 no corrected re-delivery can exist, because the answer is one only the practice has and the source system does not hold.  So an approval may carry an `ApproveImportRunRequest` stating how many rows it accepts will be skipped. Those rows become `SKIPPED` — terminal, keeping their findings, and deliberately not `FAILED`, because nothing was attempted — and everything else is released. The number must equal what the run currently holds (the sum of `blockedRows` over the preview\'s entities) or the request answers `409`: what the practice accepted has to be what they were shown.  An approval that accepts nothing states no `skipBlockedRows`, and may spell that as an absent body, a `{}` or an explicit `null` — all three mean the same thing (#4098).  `skippedRows` on the answer is what the run then records, beside a `total` that stays what was released.  **An unanswered practitioner name refuses it the same way.** It is not a finding — nothing is wrong with the source and a re-delivery would not fix it — it is a question only the practice can answer, through `POST /api/v1/imports/{runId}/practitioners/resolve`. While `ImportPractitionerReport.undecided` is above zero, approval answers `422` naming the names, because no name is ever silently guessed.  **An unanswered activity type refuses it too**, and for a stake that is larger still: the Scrivio appointment type an activity type maps to is what carries its **billing category**, and `billable_consult_v` reads that category to decide whether an appointment is declarable. Get it wrong and years of imported history reappear as *te declareren*. So while `ImportAppointmentTypeReport.undecided` is above zero, approval answers `422` naming the types — see `POST /api/v1/imports/{runId}/appointment-types/resolve`.  **An unanswered setting code refuses it as well** (#4769). The behandelsetting decides every prestatiecode on an imported zorgtraject, and there is no default — not even the organisation\'s own kwaliteitsstatuut. While `ImportSettingReport.undecided` is above zero, approval answers `422` naming the codes — see `POST /api/v1/imports/{runId}/settings/resolve`.  **And an unanswered zorgtraject overlap refuses it**, for the one thing on this screen a revert cannot take back (#4356): an imported zorgtraject closes the cliënt\'s own active one, and reopening it afterwards is not available. So while `ImportTrajectoryOverlapReport.unanswered` is above zero, approval answers `422` — see `POST /api/v1/imports/{runId}/trajectory-overlaps/resolve-remaining`, which is how a delivery that overlaps in bulk is answered in one act.  **Approving twice queues nothing twice.** A run that has already been approved answers `202` with what it now looks like and writes nothing: the rows it released are no longer `STAGED`, so a second approval has nothing to move. A run that was rejected, or one that has been reverted, answers `409` — those are decisions that have already been taken the other way.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Keur deze import goed en zet hem in de wachtrij
         * @param {string} runId The run to approve
         * @param {ApproveImportRunRequest} [approveImportRunRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async approveImportRun(runId: string, approveImportRunRequest?: ApproveImportRunRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.approveImportRun(runId, approveImportRunRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportApprovalApi.approveImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Turns the run down: every staged row becomes `WITHDRAWN` and the run becomes `REJECTED`, which is terminal. **A rejected run applies nothing, ever** — its rows are invisible to every apply worker because of what their status is, and there is no transition back.  The rows are kept rather than deleted: what an import was going to do is part of the record of what it did, and the preview of a rejected run still answers. A delivery a practice wants after all is staged again as a new run.  Rejecting twice answers `200` and writes nothing. A run that was already approved answers `409`: its rows may already have been applied, and taking those back is a revert rather than a rejection.  Requires the same permission as approving. 
         * @summary Wijs deze import af
         * @param {string} runId The run to turn down
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async rejectImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.rejectImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportApprovalApi.rejectImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportApprovalApi - factory interface
 */
export const ImportApprovalApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportApprovalApiFp(configuration)
    return {
        /**
         * Releases the run\'s staged rows to the apply worker: they move from `STAGED` to `PENDING` and the run itself to `PENDING`. Nothing is written to any business table by this request — the worker applies the rows a batch at a time afterwards (ADR-039), so follow the preview as an import is followed.  **A run with a blocking finding cannot be approved** and answers `422`, mirroring the workbook imports: an ERROR anywhere in the run counts, including one about the delivery itself, which belongs to no entity type. Warnings never block.  **Unless the practice accepts, deliberately, that those rows will not land** (#4051). One blocking finding used to stop the whole delivery however unrelated it was to what the practice needed: FIER\'s dump carries 116 of them across zorgtrajecten, afspraken and vragenlijsten, and behind them sit the 422 cliënten, ~400 verzekeringen and 7.205 dossiernotities they have to declare from — none of which any of the 116 touches. For 94 of the 116 no corrected re-delivery can exist, because the answer is one only the practice has and the source system does not hold.  So an approval may carry an `ApproveImportRunRequest` stating how many rows it accepts will be skipped. Those rows become `SKIPPED` — terminal, keeping their findings, and deliberately not `FAILED`, because nothing was attempted — and everything else is released. The number must equal what the run currently holds (the sum of `blockedRows` over the preview\'s entities) or the request answers `409`: what the practice accepted has to be what they were shown.  An approval that accepts nothing states no `skipBlockedRows`, and may spell that as an absent body, a `{}` or an explicit `null` — all three mean the same thing (#4098).  `skippedRows` on the answer is what the run then records, beside a `total` that stays what was released.  **An unanswered practitioner name refuses it the same way.** It is not a finding — nothing is wrong with the source and a re-delivery would not fix it — it is a question only the practice can answer, through `POST /api/v1/imports/{runId}/practitioners/resolve`. While `ImportPractitionerReport.undecided` is above zero, approval answers `422` naming the names, because no name is ever silently guessed.  **An unanswered activity type refuses it too**, and for a stake that is larger still: the Scrivio appointment type an activity type maps to is what carries its **billing category**, and `billable_consult_v` reads that category to decide whether an appointment is declarable. Get it wrong and years of imported history reappear as *te declareren*. So while `ImportAppointmentTypeReport.undecided` is above zero, approval answers `422` naming the types — see `POST /api/v1/imports/{runId}/appointment-types/resolve`.  **An unanswered setting code refuses it as well** (#4769). The behandelsetting decides every prestatiecode on an imported zorgtraject, and there is no default — not even the organisation\'s own kwaliteitsstatuut. While `ImportSettingReport.undecided` is above zero, approval answers `422` naming the codes — see `POST /api/v1/imports/{runId}/settings/resolve`.  **And an unanswered zorgtraject overlap refuses it**, for the one thing on this screen a revert cannot take back (#4356): an imported zorgtraject closes the cliënt\'s own active one, and reopening it afterwards is not available. So while `ImportTrajectoryOverlapReport.unanswered` is above zero, approval answers `422` — see `POST /api/v1/imports/{runId}/trajectory-overlaps/resolve-remaining`, which is how a delivery that overlaps in bulk is answered in one act.  **Approving twice queues nothing twice.** A run that has already been approved answers `202` with what it now looks like and writes nothing: the rows it released are no longer `STAGED`, so a second approval has nothing to move. A run that was rejected, or one that has been reverted, answers `409` — those are decisions that have already been taken the other way.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Keur deze import goed en zet hem in de wachtrij
         * @param {string} runId The run to approve
         * @param {ApproveImportRunRequest} [approveImportRunRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        approveImportRun(runId: string, approveImportRunRequest?: ApproveImportRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.approveImportRun(runId, approveImportRunRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Turns the run down: every staged row becomes `WITHDRAWN` and the run becomes `REJECTED`, which is terminal. **A rejected run applies nothing, ever** — its rows are invisible to every apply worker because of what their status is, and there is no transition back.  The rows are kept rather than deleted: what an import was going to do is part of the record of what it did, and the preview of a rejected run still answers. A delivery a practice wants after all is staged again as a new run.  Rejecting twice answers `200` and writes nothing. A run that was already approved answers `409`: its rows may already have been applied, and taking those back is a revert rather than a rejection.  Requires the same permission as approving. 
         * @summary Wijs deze import af
         * @param {string} runId The run to turn down
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rejectImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.rejectImportRun(runId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportApprovalApi - object-oriented interface
 */
export class ImportApprovalApi extends BaseAPI {
    /**
     * Releases the run\'s staged rows to the apply worker: they move from `STAGED` to `PENDING` and the run itself to `PENDING`. Nothing is written to any business table by this request — the worker applies the rows a batch at a time afterwards (ADR-039), so follow the preview as an import is followed.  **A run with a blocking finding cannot be approved** and answers `422`, mirroring the workbook imports: an ERROR anywhere in the run counts, including one about the delivery itself, which belongs to no entity type. Warnings never block.  **Unless the practice accepts, deliberately, that those rows will not land** (#4051). One blocking finding used to stop the whole delivery however unrelated it was to what the practice needed: FIER\'s dump carries 116 of them across zorgtrajecten, afspraken and vragenlijsten, and behind them sit the 422 cliënten, ~400 verzekeringen and 7.205 dossiernotities they have to declare from — none of which any of the 116 touches. For 94 of the 116 no corrected re-delivery can exist, because the answer is one only the practice has and the source system does not hold.  So an approval may carry an `ApproveImportRunRequest` stating how many rows it accepts will be skipped. Those rows become `SKIPPED` — terminal, keeping their findings, and deliberately not `FAILED`, because nothing was attempted — and everything else is released. The number must equal what the run currently holds (the sum of `blockedRows` over the preview\'s entities) or the request answers `409`: what the practice accepted has to be what they were shown.  An approval that accepts nothing states no `skipBlockedRows`, and may spell that as an absent body, a `{}` or an explicit `null` — all three mean the same thing (#4098).  `skippedRows` on the answer is what the run then records, beside a `total` that stays what was released.  **An unanswered practitioner name refuses it the same way.** It is not a finding — nothing is wrong with the source and a re-delivery would not fix it — it is a question only the practice can answer, through `POST /api/v1/imports/{runId}/practitioners/resolve`. While `ImportPractitionerReport.undecided` is above zero, approval answers `422` naming the names, because no name is ever silently guessed.  **An unanswered activity type refuses it too**, and for a stake that is larger still: the Scrivio appointment type an activity type maps to is what carries its **billing category**, and `billable_consult_v` reads that category to decide whether an appointment is declarable. Get it wrong and years of imported history reappear as *te declareren*. So while `ImportAppointmentTypeReport.undecided` is above zero, approval answers `422` naming the types — see `POST /api/v1/imports/{runId}/appointment-types/resolve`.  **An unanswered setting code refuses it as well** (#4769). The behandelsetting decides every prestatiecode on an imported zorgtraject, and there is no default — not even the organisation\'s own kwaliteitsstatuut. While `ImportSettingReport.undecided` is above zero, approval answers `422` naming the codes — see `POST /api/v1/imports/{runId}/settings/resolve`.  **And an unanswered zorgtraject overlap refuses it**, for the one thing on this screen a revert cannot take back (#4356): an imported zorgtraject closes the cliënt\'s own active one, and reopening it afterwards is not available. So while `ImportTrajectoryOverlapReport.unanswered` is above zero, approval answers `422` — see `POST /api/v1/imports/{runId}/trajectory-overlaps/resolve-remaining`, which is how a delivery that overlaps in bulk is answered in one act.  **Approving twice queues nothing twice.** A run that has already been approved answers `202` with what it now looks like and writes nothing: the rows it released are no longer `STAGED`, so a second approval has nothing to move. A run that was rejected, or one that has been reverted, answers `409` — those are decisions that have already been taken the other way.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Keur deze import goed en zet hem in de wachtrij
     * @param {string} runId The run to approve
     * @param {ApproveImportRunRequest} [approveImportRunRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public approveImportRun(runId: string, approveImportRunRequest?: ApproveImportRunRequest, options?: RawAxiosRequestConfig) {
        return ImportApprovalApiFp(this.configuration).approveImportRun(runId, approveImportRunRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Turns the run down: every staged row becomes `WITHDRAWN` and the run becomes `REJECTED`, which is terminal. **A rejected run applies nothing, ever** — its rows are invisible to every apply worker because of what their status is, and there is no transition back.  The rows are kept rather than deleted: what an import was going to do is part of the record of what it did, and the preview of a rejected run still answers. A delivery a practice wants after all is staged again as a new run.  Rejecting twice answers `200` and writes nothing. A run that was already approved answers `409`: its rows may already have been applied, and taking those back is a revert rather than a rejection.  Requires the same permission as approving. 
     * @summary Wijs deze import af
     * @param {string} runId The run to turn down
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public rejectImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return ImportApprovalApiFp(this.configuration).rejectImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportFindingsApi - axios parameter creator
 */
export const ImportFindingsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every finding of the run as one CSV file, with the cliënt each concerns (#4053).  An overview for the EPD vendor is the artefact a blocked run exists to produce, and until this endpoint it required database access: the findings had to be read out by hand and joined to the staged cliënt rows. The file carries the same columns the screen shows, for the whole run rather than one page.  **`;`-separated with a UTF-8 BOM**, which is what makes Excel in a Dutch locale open it as a table with the accented characters intact rather than as one column of mojibake.  The file names practitioners and cliënten, so it is PHI leaving the product and sits behind the same permission as the preview it is read beside.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Exporteer alle bevindingen van deze import als CSV
         * @param {string} runId The run whose findings to export
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportImportFindings: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('exportImportFindings', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/findings/export`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'text/csv,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The findings of a run, grouped by the rule that raised them (#4053).  A blocked delivery is read to be turned into a list somebody can act on, and a flat list cannot be: 116 findings are usually a handful of causes, and 59 identical sentences hide that. The count per cause is what says whether this is one problem or five.  Grouped by `code`, `severity` **and** `entityType`, because the same rule can be raised about two kinds of record and those are two lines of an overview, not one. A group with no `entityType` is about the delivery itself — a part that would not parse — and belongs to no staged table.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gevonden heeft, per oorzaak
         * @param {string} runId The run whose findings to summarise
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportFindingSummary: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportFindingSummary', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/findings/summary`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * One page of a run\'s findings, each naming the cliënt it concerns (#4053).  The identifying half of a finding is its staged row, and the staged tables all name the staged cliënt they hang off — so a finding about a zorgtraject, an afspraak, een notitie or een vragenlijst can say whose it is: name, patiëntnummer and the source key that locates the cliënt in the delivery, plus the zorgtrajectnummer where the row carries one. Without that the same sentence repeated 59 times is unactionable.  `sourcePart`, `sourceRow` and `sourceField` stay beside it: they are what lets the EPD vendor find the line in their own export, which the cliënt\'s name does not.  A finding about the **delivery itself** — a part that would not parse — names no staged row and therefore no cliënt. It keeps saying what it says, with the file it is about.  Ordered as the source reads: part by part, top to bottom, with a part-level finding ahead of the rows of its part.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De bevindingen van deze import, met de cliënt waar ze over gaan
         * @param {string} runId The run to read
         * @param {string} [code] Only findings the named rule raised; every rule when omitted
         * @param {ImportFindingSeverity} [severity] Only findings of this severity; both when omitted
         * @param {ImportEntityType} [entityType] Only findings about rows of this kind of record; every kind when omitted. A finding about the delivery itself is about no kind and is therefore excluded by any value here
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportFindings: async (runId: string, code?: string, severity?: ImportFindingSeverity, entityType?: ImportEntityType, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportFindings', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/findings`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (code !== undefined) {
                localVarQueryParameter['code'] = code;
            }

            if (severity !== undefined) {
                localVarQueryParameter['severity'] = severity;
            }

            if (entityType !== undefined) {
                localVarQueryParameter['entityType'] = entityType;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportFindingsApi - functional programming interface
 */
export const ImportFindingsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportFindingsApiAxiosParamCreator(configuration)
    return {
        /**
         * Every finding of the run as one CSV file, with the cliënt each concerns (#4053).  An overview for the EPD vendor is the artefact a blocked run exists to produce, and until this endpoint it required database access: the findings had to be read out by hand and joined to the staged cliënt rows. The file carries the same columns the screen shows, for the whole run rather than one page.  **`;`-separated with a UTF-8 BOM**, which is what makes Excel in a Dutch locale open it as a table with the accented characters intact rather than as one column of mojibake.  The file names practitioners and cliënten, so it is PHI leaving the product and sits behind the same permission as the preview it is read beside.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Exporteer alle bevindingen van deze import als CSV
         * @param {string} runId The run whose findings to export
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportImportFindings(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportImportFindings(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportFindingsApi.exportImportFindings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The findings of a run, grouped by the rule that raised them (#4053).  A blocked delivery is read to be turned into a list somebody can act on, and a flat list cannot be: 116 findings are usually a handful of causes, and 59 identical sentences hide that. The count per cause is what says whether this is one problem or five.  Grouped by `code`, `severity` **and** `entityType`, because the same rule can be raised about two kinds of record and those are two lines of an overview, not one. A group with no `entityType` is about the delivery itself — a part that would not parse — and belongs to no staged table.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gevonden heeft, per oorzaak
         * @param {string} runId The run whose findings to summarise
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportFindingSummary(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportFindingSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportFindingSummary(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportFindingsApi.getImportFindingSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * One page of a run\'s findings, each naming the cliënt it concerns (#4053).  The identifying half of a finding is its staged row, and the staged tables all name the staged cliënt they hang off — so a finding about a zorgtraject, an afspraak, een notitie or een vragenlijst can say whose it is: name, patiëntnummer and the source key that locates the cliënt in the delivery, plus the zorgtrajectnummer where the row carries one. Without that the same sentence repeated 59 times is unactionable.  `sourcePart`, `sourceRow` and `sourceField` stay beside it: they are what lets the EPD vendor find the line in their own export, which the cliënt\'s name does not.  A finding about the **delivery itself** — a part that would not parse — names no staged row and therefore no cliënt. It keeps saying what it says, with the file it is about.  Ordered as the source reads: part by part, top to bottom, with a part-level finding ahead of the rows of its part.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De bevindingen van deze import, met de cliënt waar ze over gaan
         * @param {string} runId The run to read
         * @param {string} [code] Only findings the named rule raised; every rule when omitted
         * @param {ImportFindingSeverity} [severity] Only findings of this severity; both when omitted
         * @param {ImportEntityType} [entityType] Only findings about rows of this kind of record; every kind when omitted. A finding about the delivery itself is about no kind and is therefore excluded by any value here
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportFindings(runId: string, code?: string, severity?: ImportFindingSeverity, entityType?: ImportEntityType, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportFindingPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportFindings(runId, code, severity, entityType, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportFindingsApi.getImportFindings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportFindingsApi - factory interface
 */
export const ImportFindingsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportFindingsApiFp(configuration)
    return {
        /**
         * Every finding of the run as one CSV file, with the cliënt each concerns (#4053).  An overview for the EPD vendor is the artefact a blocked run exists to produce, and until this endpoint it required database access: the findings had to be read out by hand and joined to the staged cliënt rows. The file carries the same columns the screen shows, for the whole run rather than one page.  **`;`-separated with a UTF-8 BOM**, which is what makes Excel in a Dutch locale open it as a table with the accented characters intact rather than as one column of mojibake.  The file names practitioners and cliënten, so it is PHI leaving the product and sits behind the same permission as the preview it is read beside.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Exporteer alle bevindingen van deze import als CSV
         * @param {string} runId The run whose findings to export
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportImportFindings(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportImportFindings(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * The findings of a run, grouped by the rule that raised them (#4053).  A blocked delivery is read to be turned into a list somebody can act on, and a flat list cannot be: 116 findings are usually a handful of causes, and 59 identical sentences hide that. The count per cause is what says whether this is one problem or five.  Grouped by `code`, `severity` **and** `entityType`, because the same rule can be raised about two kinds of record and those are two lines of an overview, not one. A group with no `entityType` is about the delivery itself — a part that would not parse — and belongs to no staged table.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gevonden heeft, per oorzaak
         * @param {string} runId The run whose findings to summarise
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportFindingSummary(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportFindingSummary> {
            return localVarFp.getImportFindingSummary(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * One page of a run\'s findings, each naming the cliënt it concerns (#4053).  The identifying half of a finding is its staged row, and the staged tables all name the staged cliënt they hang off — so a finding about a zorgtraject, an afspraak, een notitie or een vragenlijst can say whose it is: name, patiëntnummer and the source key that locates the cliënt in the delivery, plus the zorgtrajectnummer where the row carries one. Without that the same sentence repeated 59 times is unactionable.  `sourcePart`, `sourceRow` and `sourceField` stay beside it: they are what lets the EPD vendor find the line in their own export, which the cliënt\'s name does not.  A finding about the **delivery itself** — a part that would not parse — names no staged row and therefore no cliënt. It keeps saying what it says, with the file it is about.  Ordered as the source reads: part by part, top to bottom, with a part-level finding ahead of the rows of its part.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De bevindingen van deze import, met de cliënt waar ze over gaan
         * @param {string} runId The run to read
         * @param {string} [code] Only findings the named rule raised; every rule when omitted
         * @param {ImportFindingSeverity} [severity] Only findings of this severity; both when omitted
         * @param {ImportEntityType} [entityType] Only findings about rows of this kind of record; every kind when omitted. A finding about the delivery itself is about no kind and is therefore excluded by any value here
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportFindings(runId: string, code?: string, severity?: ImportFindingSeverity, entityType?: ImportEntityType, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ImportFindingPage> {
            return localVarFp.getImportFindings(runId, code, severity, entityType, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportFindingsApi - object-oriented interface
 */
export class ImportFindingsApi extends BaseAPI {
    /**
     * Every finding of the run as one CSV file, with the cliënt each concerns (#4053).  An overview for the EPD vendor is the artefact a blocked run exists to produce, and until this endpoint it required database access: the findings had to be read out by hand and joined to the staged cliënt rows. The file carries the same columns the screen shows, for the whole run rather than one page.  **`;`-separated with a UTF-8 BOM**, which is what makes Excel in a Dutch locale open it as a table with the accented characters intact rather than as one column of mojibake.  The file names practitioners and cliënten, so it is PHI leaving the product and sits behind the same permission as the preview it is read beside.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary Exporteer alle bevindingen van deze import als CSV
     * @param {string} runId The run whose findings to export
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportImportFindings(runId: string, options?: RawAxiosRequestConfig) {
        return ImportFindingsApiFp(this.configuration).exportImportFindings(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The findings of a run, grouped by the rule that raised them (#4053).  A blocked delivery is read to be turned into a list somebody can act on, and a flat list cannot be: 116 findings are usually a handful of causes, and 59 identical sentences hide that. The count per cause is what says whether this is one problem or five.  Grouped by `code`, `severity` **and** `entityType`, because the same rule can be raised about two kinds of record and those are two lines of an overview, not one. A group with no `entityType` is about the delivery itself — a part that would not parse — and belongs to no staged table.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary Wat deze import gevonden heeft, per oorzaak
     * @param {string} runId The run whose findings to summarise
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportFindingSummary(runId: string, options?: RawAxiosRequestConfig) {
        return ImportFindingsApiFp(this.configuration).getImportFindingSummary(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * One page of a run\'s findings, each naming the cliënt it concerns (#4053).  The identifying half of a finding is its staged row, and the staged tables all name the staged cliënt they hang off — so a finding about a zorgtraject, an afspraak, een notitie or een vragenlijst can say whose it is: name, patiëntnummer and the source key that locates the cliënt in the delivery, plus the zorgtrajectnummer where the row carries one. Without that the same sentence repeated 59 times is unactionable.  `sourcePart`, `sourceRow` and `sourceField` stay beside it: they are what lets the EPD vendor find the line in their own export, which the cliënt\'s name does not.  A finding about the **delivery itself** — a part that would not parse — names no staged row and therefore no cliënt. It keeps saying what it says, with the file it is about.  Ordered as the source reads: part by part, top to bottom, with a part-level finding ahead of the rows of its part.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De bevindingen van deze import, met de cliënt waar ze over gaan
     * @param {string} runId The run to read
     * @param {string} [code] Only findings the named rule raised; every rule when omitted
     * @param {ImportFindingSeverity} [severity] Only findings of this severity; both when omitted
     * @param {ImportEntityType} [entityType] Only findings about rows of this kind of record; every kind when omitted. A finding about the delivery itself is about no kind and is therefore excluded by any value here
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportFindings(runId: string, code?: string, severity?: ImportFindingSeverity, entityType?: ImportEntityType, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ImportFindingsApiFp(this.configuration).getImportFindings(runId, code, severity, entityType, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportMissingFieldsApi - axios parameter creator
 */
export const ImportMissingFieldsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Answers one or more staged rows\' missing required fields, **in bulk** — 94 fields through a per-row modal is a form nobody finishes, so the whole screen is submitted at once. Each answer is applied to its row and recorded as an answer: the row keeps saying the delivery carried no value, beside who answered and when.  **There are two answers, and a skip is one of them** (#4055). `SUPPLIED` gives the value; `SKIPPED` says there is none to give and the row should not be imported — a zorgtraject that has not started yet has no startdatum anywhere, and `care_trajectories.startdatum` is NOT NULL, so without this such a row could refuse an approval for ever. A skipped row leaves the run, keeps its findings, and reaches a terminal state that is deliberately **not** failed: nothing was attempted, and \"did all 7.205 verslagen arrive?\" has to stay answerable.  Answering re-evaluates that row: a supplied value replaces the finding that reported the gap, so the approval gate opens as the last gap is answered. Both answers are reversible while the run is awaiting approval — supplying a value for a row that was skipped brings it and everything the skip took with it back into the run.  **This is not an editor.** Only a field the delivery left *empty* can be answered — a value the delivery did carry is refused with `400`, because an import is the record of what another system held and letting it be rewritten during the import makes the migration unauditable. Re-answering a field this same run already answered replaces that answer, which is how a mistyped date is corrected.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Vul de ontbrekende verplichte velden van gestagede regels aan
         * @param {string} runId The run whose rows are being answered
         * @param {AnswerImportFieldsRequest} answerImportFieldsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        answerImportMissingFields: async (runId: string, answerImportFieldsRequest: AnswerImportFieldsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('answerImportMissingFields', 'runId', runId)
            // verify required parameter 'answerImportFieldsRequest' is not null or undefined
            assertParamExists('answerImportMissingFields', 'answerImportFieldsRequest', answerImportFieldsRequest)
            const localVarPath = `/api/v1/imports/{runId}/missing-fields/answer`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(answerImportFieldsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A staged row missing a **required** field cannot be written — `care_trajectories.startdatum` and `hoofdbehandelaar_id`, `appointments.behandelaar_id` and `form_submission.submitted_at` are all NOT NULL — and for most of them no corrected re-delivery can help: the source system does not hold the value. Measured on FIER\'s live run, 94 of 116 blocking findings were of that kind (#4052).  So the gap is a **question**, exactly as an unresolved practitioner name is (#3778), and this is where it is asked. The report is grouped by field: every group says how many rows are still missing it, how many have been given a value and how many the practice has skipped, and `entries` is one page of one group\'s rows.  **A gap can also have no answer**, and that is the second thing this report says (#4055). Some trajecten have no startdatum because they have not started; there is nothing to look up and nothing to ask the vendor for. Such a row is answered by skipping it, and each entry carries what that skip would take with it — `skipImpact` — so the practice sees the cost before confirming rather than at apply time.  **Where the delivery implies an answer it is offered rather than demanded.** A zorgtraject with activiteiten has a first activity date; a questionnaire hangs off a zorgtraject with a start date; an afspraak hangs off a zorgtraject with a regiebehandelaar. A suggestion carries the sentence saying where it came from, and it is never applied on its own — confirming 94 offered values is a different act from typing them, but it is still an act.  **The gap is the empty column, not the finding.** A zorgtraject that was never declared has no startdatum *and* no regiebehandelaar while raising one finding for the pair, so a report derived from the findings would open the approval gate on a row the apply worker still refuses. Reading the rows themselves is what makes \"answer the last one and the gate opens\" true.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De verplichte velden die de levering leeg liet, en wat de praktijk erop heeft geantwoord
         * @param {string} runId The run to read
         * @param {ImportRequiredField} [field] Which group &#x60;entries&#x60; is a page of. Defaults to the first group that still has an unanswered row, so a caller that names none lands on the work there is to do.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportMissingFields: async (runId: string, field?: ImportRequiredField, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportMissingFields', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/missing-fields`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (field !== undefined) {
                localVarQueryParameter['field'] = field;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportMissingFieldsApi - functional programming interface
 */
export const ImportMissingFieldsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportMissingFieldsApiAxiosParamCreator(configuration)
    return {
        /**
         * Answers one or more staged rows\' missing required fields, **in bulk** — 94 fields through a per-row modal is a form nobody finishes, so the whole screen is submitted at once. Each answer is applied to its row and recorded as an answer: the row keeps saying the delivery carried no value, beside who answered and when.  **There are two answers, and a skip is one of them** (#4055). `SUPPLIED` gives the value; `SKIPPED` says there is none to give and the row should not be imported — a zorgtraject that has not started yet has no startdatum anywhere, and `care_trajectories.startdatum` is NOT NULL, so without this such a row could refuse an approval for ever. A skipped row leaves the run, keeps its findings, and reaches a terminal state that is deliberately **not** failed: nothing was attempted, and \"did all 7.205 verslagen arrive?\" has to stay answerable.  Answering re-evaluates that row: a supplied value replaces the finding that reported the gap, so the approval gate opens as the last gap is answered. Both answers are reversible while the run is awaiting approval — supplying a value for a row that was skipped brings it and everything the skip took with it back into the run.  **This is not an editor.** Only a field the delivery left *empty* can be answered — a value the delivery did carry is refused with `400`, because an import is the record of what another system held and letting it be rewritten during the import makes the migration unauditable. Re-answering a field this same run already answered replaces that answer, which is how a mistyped date is corrected.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Vul de ontbrekende verplichte velden van gestagede regels aan
         * @param {string} runId The run whose rows are being answered
         * @param {AnswerImportFieldsRequest} answerImportFieldsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async answerImportMissingFields(runId: string, answerImportFieldsRequest: AnswerImportFieldsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportFieldAnswerOutcome>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.answerImportMissingFields(runId, answerImportFieldsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportMissingFieldsApi.answerImportMissingFields']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A staged row missing a **required** field cannot be written — `care_trajectories.startdatum` and `hoofdbehandelaar_id`, `appointments.behandelaar_id` and `form_submission.submitted_at` are all NOT NULL — and for most of them no corrected re-delivery can help: the source system does not hold the value. Measured on FIER\'s live run, 94 of 116 blocking findings were of that kind (#4052).  So the gap is a **question**, exactly as an unresolved practitioner name is (#3778), and this is where it is asked. The report is grouped by field: every group says how many rows are still missing it, how many have been given a value and how many the practice has skipped, and `entries` is one page of one group\'s rows.  **A gap can also have no answer**, and that is the second thing this report says (#4055). Some trajecten have no startdatum because they have not started; there is nothing to look up and nothing to ask the vendor for. Such a row is answered by skipping it, and each entry carries what that skip would take with it — `skipImpact` — so the practice sees the cost before confirming rather than at apply time.  **Where the delivery implies an answer it is offered rather than demanded.** A zorgtraject with activiteiten has a first activity date; a questionnaire hangs off a zorgtraject with a start date; an afspraak hangs off a zorgtraject with a regiebehandelaar. A suggestion carries the sentence saying where it came from, and it is never applied on its own — confirming 94 offered values is a different act from typing them, but it is still an act.  **The gap is the empty column, not the finding.** A zorgtraject that was never declared has no startdatum *and* no regiebehandelaar while raising one finding for the pair, so a report derived from the findings would open the approval gate on a row the apply worker still refuses. Reading the rows themselves is what makes \"answer the last one and the gate opens\" true.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De verplichte velden die de levering leeg liet, en wat de praktijk erop heeft geantwoord
         * @param {string} runId The run to read
         * @param {ImportRequiredField} [field] Which group &#x60;entries&#x60; is a page of. Defaults to the first group that still has an unanswered row, so a caller that names none lands on the work there is to do.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportMissingFields(runId: string, field?: ImportRequiredField, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportMissingFieldReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportMissingFields(runId, field, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportMissingFieldsApi.getImportMissingFields']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportMissingFieldsApi - factory interface
 */
export const ImportMissingFieldsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportMissingFieldsApiFp(configuration)
    return {
        /**
         * Answers one or more staged rows\' missing required fields, **in bulk** — 94 fields through a per-row modal is a form nobody finishes, so the whole screen is submitted at once. Each answer is applied to its row and recorded as an answer: the row keeps saying the delivery carried no value, beside who answered and when.  **There are two answers, and a skip is one of them** (#4055). `SUPPLIED` gives the value; `SKIPPED` says there is none to give and the row should not be imported — a zorgtraject that has not started yet has no startdatum anywhere, and `care_trajectories.startdatum` is NOT NULL, so without this such a row could refuse an approval for ever. A skipped row leaves the run, keeps its findings, and reaches a terminal state that is deliberately **not** failed: nothing was attempted, and \"did all 7.205 verslagen arrive?\" has to stay answerable.  Answering re-evaluates that row: a supplied value replaces the finding that reported the gap, so the approval gate opens as the last gap is answered. Both answers are reversible while the run is awaiting approval — supplying a value for a row that was skipped brings it and everything the skip took with it back into the run.  **This is not an editor.** Only a field the delivery left *empty* can be answered — a value the delivery did carry is refused with `400`, because an import is the record of what another system held and letting it be rewritten during the import makes the migration unauditable. Re-answering a field this same run already answered replaces that answer, which is how a mistyped date is corrected.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Vul de ontbrekende verplichte velden van gestagede regels aan
         * @param {string} runId The run whose rows are being answered
         * @param {AnswerImportFieldsRequest} answerImportFieldsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        answerImportMissingFields(runId: string, answerImportFieldsRequest: AnswerImportFieldsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportFieldAnswerOutcome> {
            return localVarFp.answerImportMissingFields(runId, answerImportFieldsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * A staged row missing a **required** field cannot be written — `care_trajectories.startdatum` and `hoofdbehandelaar_id`, `appointments.behandelaar_id` and `form_submission.submitted_at` are all NOT NULL — and for most of them no corrected re-delivery can help: the source system does not hold the value. Measured on FIER\'s live run, 94 of 116 blocking findings were of that kind (#4052).  So the gap is a **question**, exactly as an unresolved practitioner name is (#3778), and this is where it is asked. The report is grouped by field: every group says how many rows are still missing it, how many have been given a value and how many the practice has skipped, and `entries` is one page of one group\'s rows.  **A gap can also have no answer**, and that is the second thing this report says (#4055). Some trajecten have no startdatum because they have not started; there is nothing to look up and nothing to ask the vendor for. Such a row is answered by skipping it, and each entry carries what that skip would take with it — `skipImpact` — so the practice sees the cost before confirming rather than at apply time.  **Where the delivery implies an answer it is offered rather than demanded.** A zorgtraject with activiteiten has a first activity date; a questionnaire hangs off a zorgtraject with a start date; an afspraak hangs off a zorgtraject with a regiebehandelaar. A suggestion carries the sentence saying where it came from, and it is never applied on its own — confirming 94 offered values is a different act from typing them, but it is still an act.  **The gap is the empty column, not the finding.** A zorgtraject that was never declared has no startdatum *and* no regiebehandelaar while raising one finding for the pair, so a report derived from the findings would open the approval gate on a row the apply worker still refuses. Reading the rows themselves is what makes \"answer the last one and the gate opens\" true.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De verplichte velden die de levering leeg liet, en wat de praktijk erop heeft geantwoord
         * @param {string} runId The run to read
         * @param {ImportRequiredField} [field] Which group &#x60;entries&#x60; is a page of. Defaults to the first group that still has an unanswered row, so a caller that names none lands on the work there is to do.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportMissingFields(runId: string, field?: ImportRequiredField, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ImportMissingFieldReport> {
            return localVarFp.getImportMissingFields(runId, field, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportMissingFieldsApi - object-oriented interface
 */
export class ImportMissingFieldsApi extends BaseAPI {
    /**
     * Answers one or more staged rows\' missing required fields, **in bulk** — 94 fields through a per-row modal is a form nobody finishes, so the whole screen is submitted at once. Each answer is applied to its row and recorded as an answer: the row keeps saying the delivery carried no value, beside who answered and when.  **There are two answers, and a skip is one of them** (#4055). `SUPPLIED` gives the value; `SKIPPED` says there is none to give and the row should not be imported — a zorgtraject that has not started yet has no startdatum anywhere, and `care_trajectories.startdatum` is NOT NULL, so without this such a row could refuse an approval for ever. A skipped row leaves the run, keeps its findings, and reaches a terminal state that is deliberately **not** failed: nothing was attempted, and \"did all 7.205 verslagen arrive?\" has to stay answerable.  Answering re-evaluates that row: a supplied value replaces the finding that reported the gap, so the approval gate opens as the last gap is answered. Both answers are reversible while the run is awaiting approval — supplying a value for a row that was skipped brings it and everything the skip took with it back into the run.  **This is not an editor.** Only a field the delivery left *empty* can be answered — a value the delivery did carry is refused with `400`, because an import is the record of what another system held and letting it be rewritten during the import makes the migration unauditable. Re-answering a field this same run already answered replaces that answer, which is how a mistyped date is corrected.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Vul de ontbrekende verplichte velden van gestagede regels aan
     * @param {string} runId The run whose rows are being answered
     * @param {AnswerImportFieldsRequest} answerImportFieldsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public answerImportMissingFields(runId: string, answerImportFieldsRequest: AnswerImportFieldsRequest, options?: RawAxiosRequestConfig) {
        return ImportMissingFieldsApiFp(this.configuration).answerImportMissingFields(runId, answerImportFieldsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A staged row missing a **required** field cannot be written — `care_trajectories.startdatum` and `hoofdbehandelaar_id`, `appointments.behandelaar_id` and `form_submission.submitted_at` are all NOT NULL — and for most of them no corrected re-delivery can help: the source system does not hold the value. Measured on FIER\'s live run, 94 of 116 blocking findings were of that kind (#4052).  So the gap is a **question**, exactly as an unresolved practitioner name is (#3778), and this is where it is asked. The report is grouped by field: every group says how many rows are still missing it, how many have been given a value and how many the practice has skipped, and `entries` is one page of one group\'s rows.  **A gap can also have no answer**, and that is the second thing this report says (#4055). Some trajecten have no startdatum because they have not started; there is nothing to look up and nothing to ask the vendor for. Such a row is answered by skipping it, and each entry carries what that skip would take with it — `skipImpact` — so the practice sees the cost before confirming rather than at apply time.  **Where the delivery implies an answer it is offered rather than demanded.** A zorgtraject with activiteiten has a first activity date; a questionnaire hangs off a zorgtraject with a start date; an afspraak hangs off a zorgtraject with a regiebehandelaar. A suggestion carries the sentence saying where it came from, and it is never applied on its own — confirming 94 offered values is a different act from typing them, but it is still an act.  **The gap is the empty column, not the finding.** A zorgtraject that was never declared has no startdatum *and* no regiebehandelaar while raising one finding for the pair, so a report derived from the findings would open the approval gate on a row the apply worker still refuses. Reading the rows themselves is what makes \"answer the last one and the gate opens\" true.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De verplichte velden die de levering leeg liet, en wat de praktijk erop heeft geantwoord
     * @param {string} runId The run to read
     * @param {ImportRequiredField} [field] Which group &#x60;entries&#x60; is a page of. Defaults to the first group that still has an unanswered row, so a caller that names none lands on the work there is to do.
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportMissingFields(runId: string, field?: ImportRequiredField, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ImportMissingFieldsApiFp(this.configuration).getImportMissingFields(runId, field, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportPractitionersApi - axios parameter creator
 */
export const ImportPractitionersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every practitioner name this run\'s source used that nothing resolved to a Scrivio user, with how much of the delivery hangs off each of them, and the answer the practice has already given for it.  An EPD dump names practitioners as free text and the names are rough: measured against the FIER dump, plain matching resolves 27% of the agenda. Loosening the matcher is not the answer — the same source holds four separate `Coumans` — so the preview asks instead, and **no name is ever guessed**: a name with no answer refuses the run\'s approval.  `appointments` counts every staged afspraak carrying the name, whatever its state, so the number does not shrink under the reader the moment they decide. `notes` is what a **skip** additionally costs: `appointments.behandelaar_id` is NOT NULL, so skipping a practitioner withdraws their afspraken and the notities filed against them.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De behandelaarsnamen die deze import niet kon herkennen
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPractitioners: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportPractitioners', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/practitioners`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers one name, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak carrying the name the chosen user\'s id and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them; the `notes` count on the report says how many that is **before** the decision is taken.  **Both directions are available for as long as the run is awaiting approval.** Answering a name a second time replaces the first answer: a name skipped by mistake is restored by mapping it, and a name mapped to the wrong user is re-mapped or skipped. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Wijs één behandelaarsnaam toe aan een gebruiker, of sla hem over
         * @param {string} runId The run the name belongs to
         * @param {ResolveImportPractitionerRequest} resolveImportPractitionerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportPractitioner: async (runId: string, resolveImportPractitionerRequest: ResolveImportPractitionerRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('resolveImportPractitioner', 'runId', runId)
            // verify required parameter 'resolveImportPractitionerRequest' is not null or undefined
            assertParamExists('resolveImportPractitioner', 'resolveImportPractitionerRequest', resolveImportPractitionerRequest)
            const localVarPath = `/api/v1/imports/{runId}/practitioners/resolve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(resolveImportPractitionerRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportPractitionersApi - functional programming interface
 */
export const ImportPractitionersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportPractitionersApiAxiosParamCreator(configuration)
    return {
        /**
         * Every practitioner name this run\'s source used that nothing resolved to a Scrivio user, with how much of the delivery hangs off each of them, and the answer the practice has already given for it.  An EPD dump names practitioners as free text and the names are rough: measured against the FIER dump, plain matching resolves 27% of the agenda. Loosening the matcher is not the answer — the same source holds four separate `Coumans` — so the preview asks instead, and **no name is ever guessed**: a name with no answer refuses the run\'s approval.  `appointments` counts every staged afspraak carrying the name, whatever its state, so the number does not shrink under the reader the moment they decide. `notes` is what a **skip** additionally costs: `appointments.behandelaar_id` is NOT NULL, so skipping a practitioner withdraws their afspraken and the notities filed against them.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De behandelaarsnamen die deze import niet kon herkennen
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportPractitioners(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPractitionerReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportPractitioners(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportPractitionersApi.getImportPractitioners']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers one name, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak carrying the name the chosen user\'s id and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them; the `notes` count on the report says how many that is **before** the decision is taken.  **Both directions are available for as long as the run is awaiting approval.** Answering a name a second time replaces the first answer: a name skipped by mistake is restored by mapping it, and a name mapped to the wrong user is re-mapped or skipped. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Wijs één behandelaarsnaam toe aan een gebruiker, of sla hem over
         * @param {string} runId The run the name belongs to
         * @param {ResolveImportPractitionerRequest} resolveImportPractitionerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveImportPractitioner(runId: string, resolveImportPractitionerRequest: ResolveImportPractitionerRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPractitionerReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveImportPractitioner(runId, resolveImportPractitionerRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportPractitionersApi.resolveImportPractitioner']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportPractitionersApi - factory interface
 */
export const ImportPractitionersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportPractitionersApiFp(configuration)
    return {
        /**
         * Every practitioner name this run\'s source used that nothing resolved to a Scrivio user, with how much of the delivery hangs off each of them, and the answer the practice has already given for it.  An EPD dump names practitioners as free text and the names are rough: measured against the FIER dump, plain matching resolves 27% of the agenda. Loosening the matcher is not the answer — the same source holds four separate `Coumans` — so the preview asks instead, and **no name is ever guessed**: a name with no answer refuses the run\'s approval.  `appointments` counts every staged afspraak carrying the name, whatever its state, so the number does not shrink under the reader the moment they decide. `notes` is what a **skip** additionally costs: `appointments.behandelaar_id` is NOT NULL, so skipping a practitioner withdraws their afspraken and the notities filed against them.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De behandelaarsnamen die deze import niet kon herkennen
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPractitioners(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportPractitionerReport> {
            return localVarFp.getImportPractitioners(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers one name, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak carrying the name the chosen user\'s id and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them; the `notes` count on the report says how many that is **before** the decision is taken.  **Both directions are available for as long as the run is awaiting approval.** Answering a name a second time replaces the first answer: a name skipped by mistake is restored by mapping it, and a name mapped to the wrong user is re-mapped or skipped. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Wijs één behandelaarsnaam toe aan een gebruiker, of sla hem over
         * @param {string} runId The run the name belongs to
         * @param {ResolveImportPractitionerRequest} resolveImportPractitionerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportPractitioner(runId: string, resolveImportPractitionerRequest: ResolveImportPractitionerRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportPractitionerReport> {
            return localVarFp.resolveImportPractitioner(runId, resolveImportPractitionerRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportPractitionersApi - object-oriented interface
 */
export class ImportPractitionersApi extends BaseAPI {
    /**
     * Every practitioner name this run\'s source used that nothing resolved to a Scrivio user, with how much of the delivery hangs off each of them, and the answer the practice has already given for it.  An EPD dump names practitioners as free text and the names are rough: measured against the FIER dump, plain matching resolves 27% of the agenda. Loosening the matcher is not the answer — the same source holds four separate `Coumans` — so the preview asks instead, and **no name is ever guessed**: a name with no answer refuses the run\'s approval.  `appointments` counts every staged afspraak carrying the name, whatever its state, so the number does not shrink under the reader the moment they decide. `notes` is what a **skip** additionally costs: `appointments.behandelaar_id` is NOT NULL, so skipping a practitioner withdraws their afspraken and the notities filed against them.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De behandelaarsnamen die deze import niet kon herkennen
     * @param {string} runId The run to read
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportPractitioners(runId: string, options?: RawAxiosRequestConfig) {
        return ImportPractitionersApiFp(this.configuration).getImportPractitioners(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers one name, and applies that answer to the run\'s staged rows straight away — which is why the whole report comes back: the counts and the remaining `undecided` are what the practice reads next.  `MAPPED` gives every staged afspraak carrying the name the chosen user\'s id and puts it back on the queue. `SKIPPED` withdraws those afspraken and the notities filed against them; the `notes` count on the report says how many that is **before** the decision is taken.  **Both directions are available for as long as the run is awaiting approval.** Answering a name a second time replaces the first answer: a name skipped by mistake is restored by mapping it, and a name mapped to the wrong user is re-mapped or skipped. Once the run has been approved or rejected the answers are history and this endpoint answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Wijs één behandelaarsnaam toe aan een gebruiker, of sla hem over
     * @param {string} runId The run the name belongs to
     * @param {ResolveImportPractitionerRequest} resolveImportPractitionerRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveImportPractitioner(runId: string, resolveImportPractitionerRequest: ResolveImportPractitionerRequest, options?: RawAxiosRequestConfig) {
        return ImportPractitionersApiFp(this.configuration).resolveImportPractitioner(runId, resolveImportPractitionerRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportPreviewApi - axios parameter creator
 */
export const ImportPreviewApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The top level of preview-before-apply (ADR-039): what is in this run, before any of it exists. Per staged entity type it answers how many rows the run holds, how many of those name a record Scrivio already has, and how many findings the rules raised about them — which is what a practice decides an approval on without opening the source file.  **The counts are read from the staged rows**, not recomputed from the source: what will be written is what was staged, and nothing re-reads the delivery to answer this.  Every entity type is reported, including the ones this run staged nothing for. A type missing from the answer would be indistinguishable from one the reader forgot to look for, so an absent type is reported as zero.  `blockingFindings` on the preview itself is the run\'s total and is **not** the sum of the per-entity counts: a finding about the delivery as a whole — a file that is missing, a record that could not be staged at all — belongs to no entity type and is counted only there.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gaat doen, samengevat per soort record
         * @param {string} runId The run to preview
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPreview: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportPreview', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/preview`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The drilldown behind the preview summary (ADR-039). The summary answers \"how much\"; this answers \"what exactly\", which is what makes an approval meaningful: every staged row of one entity type, with the key it carries in the source, the Scrivio record it will write or update, and what the rules found about it.  **Paged in the database.** A run of thousands of rows is never read whole — the page is one window of the staged table, and its findings are read only for the rows on it.  `targetId` is the Scrivio record the row names: absent while the run will create it, present once it matched an existing record at stage time or has written one. `matched` filters on exactly that, so `matched=false` is what this run will create.  **A matched row also says what the merge will do to it, field by field** (`fieldMerges`): which fields the import fills a blank in, which already agree, and which it disagrees with and will leave alone — with both values. An import onto an organization that already holds clients merges rather than overwrites, and a count of matched rows says nothing about whether a spelling the practice corrected is about to be replaced.  Requires the `imports.zpm` or `imports.agenda` permission. 
         * @summary De gestagede regels van één soort record, met hun bevindingen
         * @param {string} runId The run to read
         * @param {ImportEntityType} entityType Which staged table to read; one page covers one entity type
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;sourceRef,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: sourceRef, status, createdAt, updatedAt. Any other field falls back to sourceRef.
         * @param {ImportStagedRowState} [status] Only rows in this lifecycle state; every state when omitted
         * @param {boolean} [matched] &#x60;true&#x60; for rows that name a Scrivio record, &#x60;false&#x60; for the ones this run will create; both when omitted
         * @param {boolean} [hasBlockingFinding] &#x60;true&#x60; for rows the rules raised an ERROR about — what refuses an approval — &#x60;false&#x60; for the rest; both when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPreviewRows: async (runId: string, entityType: ImportEntityType, page?: number, size?: number, sort?: string, status?: ImportStagedRowState, matched?: boolean, hasBlockingFinding?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportPreviewRows', 'runId', runId)
            // verify required parameter 'entityType' is not null or undefined
            assertParamExists('getImportPreviewRows', 'entityType', entityType)
            const localVarPath = `/api/v1/imports/{runId}/preview/{entityType}`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)))
                .replace(`{${"entityType"}}`, encodeURIComponent(String(entityType)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (matched !== undefined) {
                localVarQueryParameter['matched'] = matched;
            }

            if (hasBlockingFinding !== undefined) {
                localVarQueryParameter['hasBlockingFinding'] = hasBlockingFinding;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportPreviewApi - functional programming interface
 */
export const ImportPreviewApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportPreviewApiAxiosParamCreator(configuration)
    return {
        /**
         * The top level of preview-before-apply (ADR-039): what is in this run, before any of it exists. Per staged entity type it answers how many rows the run holds, how many of those name a record Scrivio already has, and how many findings the rules raised about them — which is what a practice decides an approval on without opening the source file.  **The counts are read from the staged rows**, not recomputed from the source: what will be written is what was staged, and nothing re-reads the delivery to answer this.  Every entity type is reported, including the ones this run staged nothing for. A type missing from the answer would be indistinguishable from one the reader forgot to look for, so an absent type is reported as zero.  `blockingFindings` on the preview itself is the run\'s total and is **not** the sum of the per-entity counts: a finding about the delivery as a whole — a file that is missing, a record that could not be staged at all — belongs to no entity type and is counted only there.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gaat doen, samengevat per soort record
         * @param {string} runId The run to preview
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportPreview(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportPreview(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportPreviewApi.getImportPreview']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The drilldown behind the preview summary (ADR-039). The summary answers \"how much\"; this answers \"what exactly\", which is what makes an approval meaningful: every staged row of one entity type, with the key it carries in the source, the Scrivio record it will write or update, and what the rules found about it.  **Paged in the database.** A run of thousands of rows is never read whole — the page is one window of the staged table, and its findings are read only for the rows on it.  `targetId` is the Scrivio record the row names: absent while the run will create it, present once it matched an existing record at stage time or has written one. `matched` filters on exactly that, so `matched=false` is what this run will create.  **A matched row also says what the merge will do to it, field by field** (`fieldMerges`): which fields the import fills a blank in, which already agree, and which it disagrees with and will leave alone — with both values. An import onto an organization that already holds clients merges rather than overwrites, and a count of matched rows says nothing about whether a spelling the practice corrected is about to be replaced.  Requires the `imports.zpm` or `imports.agenda` permission. 
         * @summary De gestagede regels van één soort record, met hun bevindingen
         * @param {string} runId The run to read
         * @param {ImportEntityType} entityType Which staged table to read; one page covers one entity type
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;sourceRef,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: sourceRef, status, createdAt, updatedAt. Any other field falls back to sourceRef.
         * @param {ImportStagedRowState} [status] Only rows in this lifecycle state; every state when omitted
         * @param {boolean} [matched] &#x60;true&#x60; for rows that name a Scrivio record, &#x60;false&#x60; for the ones this run will create; both when omitted
         * @param {boolean} [hasBlockingFinding] &#x60;true&#x60; for rows the rules raised an ERROR about — what refuses an approval — &#x60;false&#x60; for the rest; both when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportPreviewRows(runId: string, entityType: ImportEntityType, page?: number, size?: number, sort?: string, status?: ImportStagedRowState, matched?: boolean, hasBlockingFinding?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreviewRowPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportPreviewRows(runId, entityType, page, size, sort, status, matched, hasBlockingFinding, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportPreviewApi.getImportPreviewRows']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportPreviewApi - factory interface
 */
export const ImportPreviewApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportPreviewApiFp(configuration)
    return {
        /**
         * The top level of preview-before-apply (ADR-039): what is in this run, before any of it exists. Per staged entity type it answers how many rows the run holds, how many of those name a record Scrivio already has, and how many findings the rules raised about them — which is what a practice decides an approval on without opening the source file.  **The counts are read from the staged rows**, not recomputed from the source: what will be written is what was staged, and nothing re-reads the delivery to answer this.  Every entity type is reported, including the ones this run staged nothing for. A type missing from the answer would be indistinguishable from one the reader forgot to look for, so an absent type is reported as zero.  `blockingFindings` on the preview itself is the run\'s total and is **not** the sum of the per-entity counts: a finding about the delivery as a whole — a file that is missing, a record that could not be staged at all — belongs to no entity type and is counted only there.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary Wat deze import gaat doen, samengevat per soort record
         * @param {string} runId The run to preview
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPreview(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.getImportPreview(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * The drilldown behind the preview summary (ADR-039). The summary answers \"how much\"; this answers \"what exactly\", which is what makes an approval meaningful: every staged row of one entity type, with the key it carries in the source, the Scrivio record it will write or update, and what the rules found about it.  **Paged in the database.** A run of thousands of rows is never read whole — the page is one window of the staged table, and its findings are read only for the rows on it.  `targetId` is the Scrivio record the row names: absent while the run will create it, present once it matched an existing record at stage time or has written one. `matched` filters on exactly that, so `matched=false` is what this run will create.  **A matched row also says what the merge will do to it, field by field** (`fieldMerges`): which fields the import fills a blank in, which already agree, and which it disagrees with and will leave alone — with both values. An import onto an organization that already holds clients merges rather than overwrites, and a count of matched rows says nothing about whether a spelling the practice corrected is about to be replaced.  Requires the `imports.zpm` or `imports.agenda` permission. 
         * @summary De gestagede regels van één soort record, met hun bevindingen
         * @param {string} runId The run to read
         * @param {ImportEntityType} entityType Which staged table to read; one page covers one entity type
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;sourceRef,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: sourceRef, status, createdAt, updatedAt. Any other field falls back to sourceRef.
         * @param {ImportStagedRowState} [status] Only rows in this lifecycle state; every state when omitted
         * @param {boolean} [matched] &#x60;true&#x60; for rows that name a Scrivio record, &#x60;false&#x60; for the ones this run will create; both when omitted
         * @param {boolean} [hasBlockingFinding] &#x60;true&#x60; for rows the rules raised an ERROR about — what refuses an approval — &#x60;false&#x60; for the rest; both when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportPreviewRows(runId: string, entityType: ImportEntityType, page?: number, size?: number, sort?: string, status?: ImportStagedRowState, matched?: boolean, hasBlockingFinding?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreviewRowPage> {
            return localVarFp.getImportPreviewRows(runId, entityType, page, size, sort, status, matched, hasBlockingFinding, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportPreviewApi - object-oriented interface
 */
export class ImportPreviewApi extends BaseAPI {
    /**
     * The top level of preview-before-apply (ADR-039): what is in this run, before any of it exists. Per staged entity type it answers how many rows the run holds, how many of those name a record Scrivio already has, and how many findings the rules raised about them — which is what a practice decides an approval on without opening the source file.  **The counts are read from the staged rows**, not recomputed from the source: what will be written is what was staged, and nothing re-reads the delivery to answer this.  Every entity type is reported, including the ones this run staged nothing for. A type missing from the answer would be indistinguishable from one the reader forgot to look for, so an absent type is reported as zero.  `blockingFindings` on the preview itself is the run\'s total and is **not** the sum of the per-entity counts: a finding about the delivery as a whole — a file that is missing, a record that could not be staged at all — belongs to no entity type and is counted only there.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary Wat deze import gaat doen, samengevat per soort record
     * @param {string} runId The run to preview
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportPreview(runId: string, options?: RawAxiosRequestConfig) {
        return ImportPreviewApiFp(this.configuration).getImportPreview(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The drilldown behind the preview summary (ADR-039). The summary answers \"how much\"; this answers \"what exactly\", which is what makes an approval meaningful: every staged row of one entity type, with the key it carries in the source, the Scrivio record it will write or update, and what the rules found about it.  **Paged in the database.** A run of thousands of rows is never read whole — the page is one window of the staged table, and its findings are read only for the rows on it.  `targetId` is the Scrivio record the row names: absent while the run will create it, present once it matched an existing record at stage time or has written one. `matched` filters on exactly that, so `matched=false` is what this run will create.  **A matched row also says what the merge will do to it, field by field** (`fieldMerges`): which fields the import fills a blank in, which already agree, and which it disagrees with and will leave alone — with both values. An import onto an organization that already holds clients merges rather than overwrites, and a count of matched rows says nothing about whether a spelling the practice corrected is about to be replaced.  Requires the `imports.zpm` or `imports.agenda` permission. 
     * @summary De gestagede regels van één soort record, met hun bevindingen
     * @param {string} runId The run to read
     * @param {ImportEntityType} entityType Which staged table to read; one page covers one entity type
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;sourceRef,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: sourceRef, status, createdAt, updatedAt. Any other field falls back to sourceRef.
     * @param {ImportStagedRowState} [status] Only rows in this lifecycle state; every state when omitted
     * @param {boolean} [matched] &#x60;true&#x60; for rows that name a Scrivio record, &#x60;false&#x60; for the ones this run will create; both when omitted
     * @param {boolean} [hasBlockingFinding] &#x60;true&#x60; for rows the rules raised an ERROR about — what refuses an approval — &#x60;false&#x60; for the rest; both when omitted
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportPreviewRows(runId: string, entityType: ImportEntityType, page?: number, size?: number, sort?: string, status?: ImportStagedRowState, matched?: boolean, hasBlockingFinding?: boolean, options?: RawAxiosRequestConfig) {
        return ImportPreviewApiFp(this.configuration).getImportPreviewRows(runId, entityType, page, size, sort, status, matched, hasBlockingFinding, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportRunsApi - axios parameter creator
 */
export const ImportRunsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The caller\'s organization\'s EPD-dump run that is not over yet, so a screen reopened after the upload was closed picks that run up instead of offering a fresh upload. The archive is unpacked and read on the server, not in the browser: closing the page does not interrupt it, which is what the upload screen tells the practice.  Without this there is nothing to come back to. The run id lives only in the screen that started the upload, so closing the tab makes the run unreachable — and picking the same archive again registers a *second* run against one delivery.  \"Not over\" is `finishedAt` being unset, so a run whose preview nobody has answered is answered here too: that is exactly what someone who closed the tab mid-decision is coming back for.  Both dump sources are covered. A delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so a run changes source halfway through its own lifecycle and naming one of them would lose it there. The agenda import has its own endpoint (`/api/v1/imports/agenda/runs/active`), whose progress counts a dump run cannot fill.  Answers `204` when no such run exists — a finished run is not \"active\" and is read through its preview by id instead.  Requires the `imports.epd` permission. 
         * @summary De EPD-levering die nog loopt, als die er is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveDumpImportRun: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/imports/runs/active`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * How much of an EPD delivery a revert would take back, per staged entity type and as a total, before anything is started: the rows that were applied and would be taken back, and the rows that were never applied and would simply be withdrawn.  Read-only — nothing about the run or the records it wrote changes.  **It counts; it does not assess.** Whether one record can really be removed is the removability rule the revert worker applies row by row, and this deliberately does not restate it: a delivery stages some 40.000 rows across nine types, each needing at least one query of its own, and no request can answer that. A cheaper second estimate of removability is worse than none — it could disagree with the verdict the worker then acts on. What a practice decides on is the size of what it is taking back, which is exactly what these counts are; a row the revert leaves standing is a record that survives, which is not a failure and destroys nothing.  What was left standing is read off the run as the revert proceeds, where the practice is already watching it: `GET /api/v1/imports/{runId}/preview/{entityType}?status=REVERT_BLOCKED` lists those rows with the Dutch reason each carries.  The agenda import answers a different question, per appointment and with a blocked breakdown, because at its scale it can — see `/api/v1/imports/agenda/runs/{runId}/revert-preflight`.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so. A run nobody has approved is counted all the same and is still refused a revert (`409`): throwing away a delivery nobody decided on is a rejection, and `state` is again what tells the two apart.  Requires the `imports.epd` permission. 
         * @summary Wat het terugdraaien van deze levering zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDumpImportRevertPreflight: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getDumpImportRevertPreflight', 'runId', runId)
            const localVarPath = `/api/v1/imports/runs/{runId}/revert-preflight`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * How far the worker got with one EPD-dump run: what the delivery staged, what has been written, what could not be written, and what a revert has since taken back.  The direct counterpart of `/api/v1/imports/agenda/runs/{runId}`, which is scoped to the agenda source and answers `404` for a dump run — so the preview screen, which serves every source, had a progress bar for an agenda run and a bare state badge for a dump one.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Both dump sources are covered, for the reason `/api/v1/imports/runs` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Deliberately without per-row failures, exactly as the history is: a dump stages some 40.000 rows, and a failed row is read per entity type through the run\'s preview (`GET /api/v1/imports/{runId}/preview/{entityType}`).  Requires the `imports.epd` permission. 
         * @summary Voortgang van één EPD-levering
         * @param {string} runId The run, as the preview or the history named it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDumpImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getDumpImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/runs/{runId}`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The caller\'s organization\'s EPD-dump runs, newest first — the history a practice picks an earlier delivery from, for instance to take it back.  Without this a finished delivery cannot be named at all. `/api/v1/imports/runs/active` deliberately answers `204` for every run that has ended, and the revert and its preflight are both addressed by `runId` — so the only record of a finished run\'s id was the screen that started it, which is exactly what closing a tab loses.  Both dump sources are covered, for the reason `/api/v1/imports/runs/active` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.epd` permission. 
         * @summary Eerdere EPD-leveringen van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDumpImportRuns: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/imports/runs`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reads a delivery that is already in the building a second time, without re-uploading it.  A run parses exactly once, so a parser fix landed while the practice was reading the preview could only be applied by uploading the archive again and unpacking it again — 3h 38m on FIER\'s migration, for entries already in the bucket and already correct. This clears what the previous parse staged and hands the run back to the parse worker; the unpacked entries are untouched, which is the whole point.  `202` rather than `200`: what this request writes is only the reset. The delivery is read again by a worker over the minutes that follow (ADR-039), exactly as it was the first time, and the preview this answers with is the run as it now stands — empty, and reading. Follow it with the preview endpoint, as an import in flight is followed.  **What the practice has answered is lost.** Practitioner mappings, activity-type decisions and the values supplied for required fields the delivery left empty all reach the staged rows, and the rows are what go — so they are asked again on the new preview. The findings of the unpack and the recognition are kept: those phases are not re-run, so nothing would ever raise them a second time.  Re-parsing a delivery that has not been unpacked yet, one that is being read right now, or a run that has already been approved or rejected answers `409` — a decided run is reverted, not re-parsed.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Lees een al uitgepakte EPD-levering opnieuw in
         * @param {string} runId The run to read again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reparseDumpImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('reparseDumpImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/runs/{runId}/reparse`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reconsiders the declarations of an already-applied delivery that reached no session, and queues the ones a single answer exists for.  A declaration is placed on a session by the patient and the moment, and one nothing places cannot be written at all — `billing_lines.practitioner_id` is NOT NULL and a GDS801 message names only the practice\'s own AGB code. A co-led groepsconsult is declared once per behandelaar and the co-behandelaar\'s quarters fall past the end of the session the export states, so those declarations were left out: 1.176 of FIER\'s 1.351. Where the cliënt has exactly one imported afspraak that day and exactly one practitioner on it holding no declaratieregel for them, that is who provided the care, and this places them there.  Every other unplaced declaration is left exactly where it was. **Nothing already applied is touched**, and running this twice changes nothing the first run did not: who already holds a line is read before anything is placed.  `202` rather than `200`: what this request writes is only the queue. The lines it placed are written by the apply worker over the seconds that follow (ADR-039), exactly as the delivery\'s own rows were, and a revert of the run takes them back with everything else it wrote.  A run that has not been approved answers `409` — re-reading the delivery is what applies a corrected rule there. So does one still being applied, one being taken back, and one that was rejected.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Plaats de gedeclareerde prestaties die geen afspraak kregen opnieuw
         * @param {string} runId The applied run whose unplaced declarations are reconsidered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reresolveDumpImportBillingLines: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('reresolveDumpImportBillingLines', 'runId', runId)
            const localVarPath = `/api/v1/imports/runs/{runId}/billing-lines/re-resolve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Starts taking the delivery back and returns immediately, exactly as the approval does: the records are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the dossiers empty gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the apply worker no longer creates them, and the run moves to `REVERTING`. Follow it with the preview endpoint, as an import in flight is followed.  A record carrying work the import did not create is refused rather than removed and stays in the dossier; the row says why, and the run\'s own counts say how many. Use `revert-preflight` to see how much is being taken back before starting.  Reverting a run that is already being reverted, has been, or was never approved answers `409`.  Requires the `imports.epd` permission — the permission that put these records in the dossiers in the first place. 
         * @summary Draai een eerdere EPD-levering terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertDumpImportRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('revertDumpImportRun', 'runId', runId)
            const localVarPath = `/api/v1/imports/runs/{runId}/revert`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Opens a delivery whose archive is password-protected with the password the practice enters, without uploading it again.  Accepted only while the preview\'s `archivePasswordRequested` is true: the unpack ended on the protection, or on a password that did not open it. The password is stored encrypted with Cloud KMS, bound to this run, and removed when the unpack attempt it is entered for ends — whatever that attempt comes to. A wrong one ends the attempt with one finding and the run asks again; there is no limit on attempts.  `202` for the reason a re-parse answers one: what this request writes is the queue. The archive is unpacked by a worker over the minutes that follow, and the preview this answers with is the run as it now stands. Follow it with the preview endpoint.  Requires the `imports.epd` permission — the permission that could approve the run. 
         * @summary Open een met een wachtwoord beveiligde EPD-levering
         * @param {string} runId The run whose archive to open
         * @param {ImportArchivePasswordRequest} importArchivePasswordRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitDumpImportArchivePassword: async (runId: string, importArchivePasswordRequest: ImportArchivePasswordRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('submitDumpImportArchivePassword', 'runId', runId)
            // verify required parameter 'importArchivePasswordRequest' is not null or undefined
            assertParamExists('submitDumpImportArchivePassword', 'importArchivePasswordRequest', importArchivePasswordRequest)
            const localVarPath = `/api/v1/imports/runs/{runId}/archive-password`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(importArchivePasswordRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportRunsApi - functional programming interface
 */
export const ImportRunsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportRunsApiAxiosParamCreator(configuration)
    return {
        /**
         * The caller\'s organization\'s EPD-dump run that is not over yet, so a screen reopened after the upload was closed picks that run up instead of offering a fresh upload. The archive is unpacked and read on the server, not in the browser: closing the page does not interrupt it, which is what the upload screen tells the practice.  Without this there is nothing to come back to. The run id lives only in the screen that started the upload, so closing the tab makes the run unreachable — and picking the same archive again registers a *second* run against one delivery.  \"Not over\" is `finishedAt` being unset, so a run whose preview nobody has answered is answered here too: that is exactly what someone who closed the tab mid-decision is coming back for.  Both dump sources are covered. A delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so a run changes source halfway through its own lifecycle and naming one of them would lose it there. The agenda import has its own endpoint (`/api/v1/imports/agenda/runs/active`), whose progress counts a dump run cannot fill.  Answers `204` when no such run exists — a finished run is not \"active\" and is read through its preview by id instead.  Requires the `imports.epd` permission. 
         * @summary De EPD-levering die nog loopt, als die er is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActiveDumpImportRun(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ActiveDumpImportRun>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActiveDumpImportRun(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.getActiveDumpImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * How much of an EPD delivery a revert would take back, per staged entity type and as a total, before anything is started: the rows that were applied and would be taken back, and the rows that were never applied and would simply be withdrawn.  Read-only — nothing about the run or the records it wrote changes.  **It counts; it does not assess.** Whether one record can really be removed is the removability rule the revert worker applies row by row, and this deliberately does not restate it: a delivery stages some 40.000 rows across nine types, each needing at least one query of its own, and no request can answer that. A cheaper second estimate of removability is worse than none — it could disagree with the verdict the worker then acts on. What a practice decides on is the size of what it is taking back, which is exactly what these counts are; a row the revert leaves standing is a record that survives, which is not a failure and destroys nothing.  What was left standing is read off the run as the revert proceeds, where the practice is already watching it: `GET /api/v1/imports/{runId}/preview/{entityType}?status=REVERT_BLOCKED` lists those rows with the Dutch reason each carries.  The agenda import answers a different question, per appointment and with a blocked breakdown, because at its scale it can — see `/api/v1/imports/agenda/runs/{runId}/revert-preflight`.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so. A run nobody has approved is counted all the same and is still refused a revert (`409`): throwing away a delivery nobody decided on is a rejection, and `state` is again what tells the two apart.  Requires the `imports.epd` permission. 
         * @summary Wat het terugdraaien van deze levering zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDumpImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DumpImportRevertPreflight>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDumpImportRevertPreflight(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.getDumpImportRevertPreflight']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * How far the worker got with one EPD-dump run: what the delivery staged, what has been written, what could not be written, and what a revert has since taken back.  The direct counterpart of `/api/v1/imports/agenda/runs/{runId}`, which is scoped to the agenda source and answers `404` for a dump run — so the preview screen, which serves every source, had a progress bar for an agenda run and a bare state badge for a dump one.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Both dump sources are covered, for the reason `/api/v1/imports/runs` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Deliberately without per-row failures, exactly as the history is: a dump stages some 40.000 rows, and a failed row is read per entity type through the run\'s preview (`GET /api/v1/imports/{runId}/preview/{entityType}`).  Requires the `imports.epd` permission. 
         * @summary Voortgang van één EPD-levering
         * @param {string} runId The run, as the preview or the history named it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDumpImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DumpImportRunSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDumpImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.getDumpImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The caller\'s organization\'s EPD-dump runs, newest first — the history a practice picks an earlier delivery from, for instance to take it back.  Without this a finished delivery cannot be named at all. `/api/v1/imports/runs/active` deliberately answers `204` for every run that has ended, and the revert and its preflight are both addressed by `runId` — so the only record of a finished run\'s id was the screen that started it, which is exactly what closing a tab loses.  Both dump sources are covered, for the reason `/api/v1/imports/runs/active` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.epd` permission. 
         * @summary Eerdere EPD-leveringen van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDumpImportRuns(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DumpImportRunSummary>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDumpImportRuns(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.listDumpImportRuns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reads a delivery that is already in the building a second time, without re-uploading it.  A run parses exactly once, so a parser fix landed while the practice was reading the preview could only be applied by uploading the archive again and unpacking it again — 3h 38m on FIER\'s migration, for entries already in the bucket and already correct. This clears what the previous parse staged and hands the run back to the parse worker; the unpacked entries are untouched, which is the whole point.  `202` rather than `200`: what this request writes is only the reset. The delivery is read again by a worker over the minutes that follow (ADR-039), exactly as it was the first time, and the preview this answers with is the run as it now stands — empty, and reading. Follow it with the preview endpoint, as an import in flight is followed.  **What the practice has answered is lost.** Practitioner mappings, activity-type decisions and the values supplied for required fields the delivery left empty all reach the staged rows, and the rows are what go — so they are asked again on the new preview. The findings of the unpack and the recognition are kept: those phases are not re-run, so nothing would ever raise them a second time.  Re-parsing a delivery that has not been unpacked yet, one that is being read right now, or a run that has already been approved or rejected answers `409` — a decided run is reverted, not re-parsed.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Lees een al uitgepakte EPD-levering opnieuw in
         * @param {string} runId The run to read again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reparseDumpImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reparseDumpImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.reparseDumpImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reconsiders the declarations of an already-applied delivery that reached no session, and queues the ones a single answer exists for.  A declaration is placed on a session by the patient and the moment, and one nothing places cannot be written at all — `billing_lines.practitioner_id` is NOT NULL and a GDS801 message names only the practice\'s own AGB code. A co-led groepsconsult is declared once per behandelaar and the co-behandelaar\'s quarters fall past the end of the session the export states, so those declarations were left out: 1.176 of FIER\'s 1.351. Where the cliënt has exactly one imported afspraak that day and exactly one practitioner on it holding no declaratieregel for them, that is who provided the care, and this places them there.  Every other unplaced declaration is left exactly where it was. **Nothing already applied is touched**, and running this twice changes nothing the first run did not: who already holds a line is read before anything is placed.  `202` rather than `200`: what this request writes is only the queue. The lines it placed are written by the apply worker over the seconds that follow (ADR-039), exactly as the delivery\'s own rows were, and a revert of the run takes them back with everything else it wrote.  A run that has not been approved answers `409` — re-reading the delivery is what applies a corrected rule there. So does one still being applied, one being taken back, and one that was rejected.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Plaats de gedeclareerde prestaties die geen afspraak kregen opnieuw
         * @param {string} runId The applied run whose unplaced declarations are reconsidered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reresolveDumpImportBillingLines(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportBillingLineReresolution>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reresolveDumpImportBillingLines(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.reresolveDumpImportBillingLines']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Starts taking the delivery back and returns immediately, exactly as the approval does: the records are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the dossiers empty gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the apply worker no longer creates them, and the run moves to `REVERTING`. Follow it with the preview endpoint, as an import in flight is followed.  A record carrying work the import did not create is refused rather than removed and stays in the dossier; the row says why, and the run\'s own counts say how many. Use `revert-preflight` to see how much is being taken back before starting.  Reverting a run that is already being reverted, has been, or was never approved answers `409`.  Requires the `imports.epd` permission — the permission that put these records in the dossiers in the first place. 
         * @summary Draai een eerdere EPD-levering terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revertDumpImportRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revertDumpImportRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.revertDumpImportRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Opens a delivery whose archive is password-protected with the password the practice enters, without uploading it again.  Accepted only while the preview\'s `archivePasswordRequested` is true: the unpack ended on the protection, or on a password that did not open it. The password is stored encrypted with Cloud KMS, bound to this run, and removed when the unpack attempt it is entered for ends — whatever that attempt comes to. A wrong one ends the attempt with one finding and the run asks again; there is no limit on attempts.  `202` for the reason a re-parse answers one: what this request writes is the queue. The archive is unpacked by a worker over the minutes that follow, and the preview this answers with is the run as it now stands. Follow it with the preview endpoint.  Requires the `imports.epd` permission — the permission that could approve the run. 
         * @summary Open een met een wachtwoord beveiligde EPD-levering
         * @param {string} runId The run whose archive to open
         * @param {ImportArchivePasswordRequest} importArchivePasswordRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitDumpImportArchivePassword(runId: string, importArchivePasswordRequest: ImportArchivePasswordRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportPreview>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitDumpImportArchivePassword(runId, importArchivePasswordRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportRunsApi.submitDumpImportArchivePassword']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportRunsApi - factory interface
 */
export const ImportRunsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportRunsApiFp(configuration)
    return {
        /**
         * The caller\'s organization\'s EPD-dump run that is not over yet, so a screen reopened after the upload was closed picks that run up instead of offering a fresh upload. The archive is unpacked and read on the server, not in the browser: closing the page does not interrupt it, which is what the upload screen tells the practice.  Without this there is nothing to come back to. The run id lives only in the screen that started the upload, so closing the tab makes the run unreachable — and picking the same archive again registers a *second* run against one delivery.  \"Not over\" is `finishedAt` being unset, so a run whose preview nobody has answered is answered here too: that is exactly what someone who closed the tab mid-decision is coming back for.  Both dump sources are covered. A delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so a run changes source halfway through its own lifecycle and naming one of them would lose it there. The agenda import has its own endpoint (`/api/v1/imports/agenda/runs/active`), whose progress counts a dump run cannot fill.  Answers `204` when no such run exists — a finished run is not \"active\" and is read through its preview by id instead.  Requires the `imports.epd` permission. 
         * @summary De EPD-levering die nog loopt, als die er is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveDumpImportRun(options?: RawAxiosRequestConfig): AxiosPromise<ActiveDumpImportRun> {
            return localVarFp.getActiveDumpImportRun(options).then((request) => request(axios, basePath));
        },
        /**
         * How much of an EPD delivery a revert would take back, per staged entity type and as a total, before anything is started: the rows that were applied and would be taken back, and the rows that were never applied and would simply be withdrawn.  Read-only — nothing about the run or the records it wrote changes.  **It counts; it does not assess.** Whether one record can really be removed is the removability rule the revert worker applies row by row, and this deliberately does not restate it: a delivery stages some 40.000 rows across nine types, each needing at least one query of its own, and no request can answer that. A cheaper second estimate of removability is worse than none — it could disagree with the verdict the worker then acts on. What a practice decides on is the size of what it is taking back, which is exactly what these counts are; a row the revert leaves standing is a record that survives, which is not a failure and destroys nothing.  What was left standing is read off the run as the revert proceeds, where the practice is already watching it: `GET /api/v1/imports/{runId}/preview/{entityType}?status=REVERT_BLOCKED` lists those rows with the Dutch reason each carries.  The agenda import answers a different question, per appointment and with a blocked breakdown, because at its scale it can — see `/api/v1/imports/agenda/runs/{runId}/revert-preflight`.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so. A run nobody has approved is counted all the same and is still refused a revert (`409`): throwing away a delivery nobody decided on is a rejection, and `state` is again what tells the two apart.  Requires the `imports.epd` permission. 
         * @summary Wat het terugdraaien van deze levering zou doen
         * @param {string} runId The run whose revert is being considered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDumpImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<DumpImportRevertPreflight> {
            return localVarFp.getDumpImportRevertPreflight(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * How far the worker got with one EPD-dump run: what the delivery staged, what has been written, what could not be written, and what a revert has since taken back.  The direct counterpart of `/api/v1/imports/agenda/runs/{runId}`, which is scoped to the agenda source and answers `404` for a dump run — so the preview screen, which serves every source, had a progress bar for an agenda run and a bare state badge for a dump one.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Both dump sources are covered, for the reason `/api/v1/imports/runs` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Deliberately without per-row failures, exactly as the history is: a dump stages some 40.000 rows, and a failed row is read per entity type through the run\'s preview (`GET /api/v1/imports/{runId}/preview/{entityType}`).  Requires the `imports.epd` permission. 
         * @summary Voortgang van één EPD-levering
         * @param {string} runId The run, as the preview or the history named it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDumpImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<DumpImportRunSummary> {
            return localVarFp.getDumpImportRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * The caller\'s organization\'s EPD-dump runs, newest first — the history a practice picks an earlier delivery from, for instance to take it back.  Without this a finished delivery cannot be named at all. `/api/v1/imports/runs/active` deliberately answers `204` for every run that has ended, and the revert and its preflight are both addressed by `runId` — so the only record of a finished run\'s id was the screen that started it, which is exactly what closing a tab loses.  Both dump sources are covered, for the reason `/api/v1/imports/runs/active` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.epd` permission. 
         * @summary Eerdere EPD-leveringen van de organisatie
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDumpImportRuns(options?: RawAxiosRequestConfig): AxiosPromise<Array<DumpImportRunSummary>> {
            return localVarFp.listDumpImportRuns(options).then((request) => request(axios, basePath));
        },
        /**
         * Reads a delivery that is already in the building a second time, without re-uploading it.  A run parses exactly once, so a parser fix landed while the practice was reading the preview could only be applied by uploading the archive again and unpacking it again — 3h 38m on FIER\'s migration, for entries already in the bucket and already correct. This clears what the previous parse staged and hands the run back to the parse worker; the unpacked entries are untouched, which is the whole point.  `202` rather than `200`: what this request writes is only the reset. The delivery is read again by a worker over the minutes that follow (ADR-039), exactly as it was the first time, and the preview this answers with is the run as it now stands — empty, and reading. Follow it with the preview endpoint, as an import in flight is followed.  **What the practice has answered is lost.** Practitioner mappings, activity-type decisions and the values supplied for required fields the delivery left empty all reach the staged rows, and the rows are what go — so they are asked again on the new preview. The findings of the unpack and the recognition are kept: those phases are not re-run, so nothing would ever raise them a second time.  Re-parsing a delivery that has not been unpacked yet, one that is being read right now, or a run that has already been approved or rejected answers `409` — a decided run is reverted, not re-parsed.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Lees een al uitgepakte EPD-levering opnieuw in
         * @param {string} runId The run to read again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reparseDumpImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.reparseDumpImportRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reconsiders the declarations of an already-applied delivery that reached no session, and queues the ones a single answer exists for.  A declaration is placed on a session by the patient and the moment, and one nothing places cannot be written at all — `billing_lines.practitioner_id` is NOT NULL and a GDS801 message names only the practice\'s own AGB code. A co-led groepsconsult is declared once per behandelaar and the co-behandelaar\'s quarters fall past the end of the session the export states, so those declarations were left out: 1.176 of FIER\'s 1.351. Where the cliënt has exactly one imported afspraak that day and exactly one practitioner on it holding no declaratieregel for them, that is who provided the care, and this places them there.  Every other unplaced declaration is left exactly where it was. **Nothing already applied is touched**, and running this twice changes nothing the first run did not: who already holds a line is read before anything is placed.  `202` rather than `200`: what this request writes is only the queue. The lines it placed are written by the apply worker over the seconds that follow (ADR-039), exactly as the delivery\'s own rows were, and a revert of the run takes them back with everything else it wrote.  A run that has not been approved answers `409` — re-reading the delivery is what applies a corrected rule there. So does one still being applied, one being taken back, and one that was rejected.  Requires the `imports.epd` permission — the permission that could have approved the run. 
         * @summary Plaats de gedeclareerde prestaties die geen afspraak kregen opnieuw
         * @param {string} runId The applied run whose unplaced declarations are reconsidered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reresolveDumpImportBillingLines(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportBillingLineReresolution> {
            return localVarFp.reresolveDumpImportBillingLines(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Starts taking the delivery back and returns immediately, exactly as the approval does: the records are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the dossiers empty gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the apply worker no longer creates them, and the run moves to `REVERTING`. Follow it with the preview endpoint, as an import in flight is followed.  A record carrying work the import did not create is refused rather than removed and stays in the dossier; the row says why, and the run\'s own counts say how many. Use `revert-preflight` to see how much is being taken back before starting.  Reverting a run that is already being reverted, has been, or was never approved answers `409`.  Requires the `imports.epd` permission — the permission that put these records in the dossiers in the first place. 
         * @summary Draai een eerdere EPD-levering terug
         * @param {string} runId The run to take back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertDumpImportRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.revertDumpImportRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Opens a delivery whose archive is password-protected with the password the practice enters, without uploading it again.  Accepted only while the preview\'s `archivePasswordRequested` is true: the unpack ended on the protection, or on a password that did not open it. The password is stored encrypted with Cloud KMS, bound to this run, and removed when the unpack attempt it is entered for ends — whatever that attempt comes to. A wrong one ends the attempt with one finding and the run asks again; there is no limit on attempts.  `202` for the reason a re-parse answers one: what this request writes is the queue. The archive is unpacked by a worker over the minutes that follow, and the preview this answers with is the run as it now stands. Follow it with the preview endpoint.  Requires the `imports.epd` permission — the permission that could approve the run. 
         * @summary Open een met een wachtwoord beveiligde EPD-levering
         * @param {string} runId The run whose archive to open
         * @param {ImportArchivePasswordRequest} importArchivePasswordRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitDumpImportArchivePassword(runId: string, importArchivePasswordRequest: ImportArchivePasswordRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportPreview> {
            return localVarFp.submitDumpImportArchivePassword(runId, importArchivePasswordRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportRunsApi - object-oriented interface
 */
export class ImportRunsApi extends BaseAPI {
    /**
     * The caller\'s organization\'s EPD-dump run that is not over yet, so a screen reopened after the upload was closed picks that run up instead of offering a fresh upload. The archive is unpacked and read on the server, not in the browser: closing the page does not interrupt it, which is what the upload screen tells the practice.  Without this there is nothing to come back to. The run id lives only in the screen that started the upload, so closing the tab makes the run unreachable — and picking the same archive again registers a *second* run against one delivery.  \"Not over\" is `finishedAt` being unset, so a run whose preview nobody has answered is answered here too: that is exactly what someone who closed the tab mid-decision is coming back for.  Both dump sources are covered. A delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so a run changes source halfway through its own lifecycle and naming one of them would lose it there. The agenda import has its own endpoint (`/api/v1/imports/agenda/runs/active`), whose progress counts a dump run cannot fill.  Answers `204` when no such run exists — a finished run is not \"active\" and is read through its preview by id instead.  Requires the `imports.epd` permission. 
     * @summary De EPD-levering die nog loopt, als die er is
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getActiveDumpImportRun(options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).getActiveDumpImportRun(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * How much of an EPD delivery a revert would take back, per staged entity type and as a total, before anything is started: the rows that were applied and would be taken back, and the rows that were never applied and would simply be withdrawn.  Read-only — nothing about the run or the records it wrote changes.  **It counts; it does not assess.** Whether one record can really be removed is the removability rule the revert worker applies row by row, and this deliberately does not restate it: a delivery stages some 40.000 rows across nine types, each needing at least one query of its own, and no request can answer that. A cheaper second estimate of removability is worse than none — it could disagree with the verdict the worker then acts on. What a practice decides on is the size of what it is taking back, which is exactly what these counts are; a row the revert leaves standing is a record that survives, which is not a failure and destroys nothing.  What was left standing is read off the run as the revert proceeds, where the practice is already watching it: `GET /api/v1/imports/{runId}/preview/{entityType}?status=REVERT_BLOCKED` lists those rows with the Dutch reason each carries.  The agenda import answers a different question, per appointment and with a blocked breakdown, because at its scale it can — see `/api/v1/imports/agenda/runs/{runId}/revert-preflight`.  The numbers are about a revert started **now**, so a run that is already being reverted — or has been — answers with zeroes; its `state` is what says so. A run nobody has approved is counted all the same and is still refused a revert (`409`): throwing away a delivery nobody decided on is a rejection, and `state` is again what tells the two apart.  Requires the `imports.epd` permission. 
     * @summary Wat het terugdraaien van deze levering zou doen
     * @param {string} runId The run whose revert is being considered
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDumpImportRevertPreflight(runId: string, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).getDumpImportRevertPreflight(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * How far the worker got with one EPD-dump run: what the delivery staged, what has been written, what could not be written, and what a revert has since taken back.  The direct counterpart of `/api/v1/imports/agenda/runs/{runId}`, which is scoped to the agenda source and answers `404` for a dump run — so the preview screen, which serves every source, had a progress bar for an agenda run and a bare state badge for a dump one.  The counts come from the run header, which the worker recomputes from its rows every tick, so they lag by at most one tick rather than being a client-side estimate. A run is over once `finishedAt` is set; until then the caller polls.  Both dump sources are covered, for the reason `/api/v1/imports/runs` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Deliberately without per-row failures, exactly as the history is: a dump stages some 40.000 rows, and a failed row is read per entity type through the run\'s preview (`GET /api/v1/imports/{runId}/preview/{entityType}`).  Requires the `imports.epd` permission. 
     * @summary Voortgang van één EPD-levering
     * @param {string} runId The run, as the preview or the history named it
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDumpImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).getDumpImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The caller\'s organization\'s EPD-dump runs, newest first — the history a practice picks an earlier delivery from, for instance to take it back.  Without this a finished delivery cannot be named at all. `/api/v1/imports/runs/active` deliberately answers `204` for every run that has ended, and the revert and its preflight are both addressed by `runId` — so the only record of a finished run\'s id was the screen that started it, which is exactly what closing a tab loses.  Both dump sources are covered, for the reason `/api/v1/imports/runs/active` gives: a delivery is `EPD_DUMP` until it has been recognised and `ZORGADMIN_DUMP` afterwards, so naming one of them would lose a run halfway through its own lifecycle.  Capped at the most recent runs and deliberately without paging or a `sort` parameter: the order of an import history is not a choice a caller makes, and a run this list no longer reaches is still followed by id.  Requires the `imports.epd` permission. 
     * @summary Eerdere EPD-leveringen van de organisatie
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDumpImportRuns(options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).listDumpImportRuns(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reads a delivery that is already in the building a second time, without re-uploading it.  A run parses exactly once, so a parser fix landed while the practice was reading the preview could only be applied by uploading the archive again and unpacking it again — 3h 38m on FIER\'s migration, for entries already in the bucket and already correct. This clears what the previous parse staged and hands the run back to the parse worker; the unpacked entries are untouched, which is the whole point.  `202` rather than `200`: what this request writes is only the reset. The delivery is read again by a worker over the minutes that follow (ADR-039), exactly as it was the first time, and the preview this answers with is the run as it now stands — empty, and reading. Follow it with the preview endpoint, as an import in flight is followed.  **What the practice has answered is lost.** Practitioner mappings, activity-type decisions and the values supplied for required fields the delivery left empty all reach the staged rows, and the rows are what go — so they are asked again on the new preview. The findings of the unpack and the recognition are kept: those phases are not re-run, so nothing would ever raise them a second time.  Re-parsing a delivery that has not been unpacked yet, one that is being read right now, or a run that has already been approved or rejected answers `409` — a decided run is reverted, not re-parsed.  Requires the `imports.epd` permission — the permission that could have approved the run. 
     * @summary Lees een al uitgepakte EPD-levering opnieuw in
     * @param {string} runId The run to read again
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reparseDumpImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).reparseDumpImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reconsiders the declarations of an already-applied delivery that reached no session, and queues the ones a single answer exists for.  A declaration is placed on a session by the patient and the moment, and one nothing places cannot be written at all — `billing_lines.practitioner_id` is NOT NULL and a GDS801 message names only the practice\'s own AGB code. A co-led groepsconsult is declared once per behandelaar and the co-behandelaar\'s quarters fall past the end of the session the export states, so those declarations were left out: 1.176 of FIER\'s 1.351. Where the cliënt has exactly one imported afspraak that day and exactly one practitioner on it holding no declaratieregel for them, that is who provided the care, and this places them there.  Every other unplaced declaration is left exactly where it was. **Nothing already applied is touched**, and running this twice changes nothing the first run did not: who already holds a line is read before anything is placed.  `202` rather than `200`: what this request writes is only the queue. The lines it placed are written by the apply worker over the seconds that follow (ADR-039), exactly as the delivery\'s own rows were, and a revert of the run takes them back with everything else it wrote.  A run that has not been approved answers `409` — re-reading the delivery is what applies a corrected rule there. So does one still being applied, one being taken back, and one that was rejected.  Requires the `imports.epd` permission — the permission that could have approved the run. 
     * @summary Plaats de gedeclareerde prestaties die geen afspraak kregen opnieuw
     * @param {string} runId The applied run whose unplaced declarations are reconsidered
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reresolveDumpImportBillingLines(runId: string, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).reresolveDumpImportBillingLines(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Starts taking the delivery back and returns immediately, exactly as the approval does: the records are removed by a scheduled worker a batch at a time (ADR-039), so none of them is gone yet when this responds and the dossiers empty gradually afterwards.  What this request itself writes is only the queue: rows that were applied are marked for removal, rows still waiting are withdrawn so the apply worker no longer creates them, and the run moves to `REVERTING`. Follow it with the preview endpoint, as an import in flight is followed.  A record carrying work the import did not create is refused rather than removed and stays in the dossier; the row says why, and the run\'s own counts say how many. Use `revert-preflight` to see how much is being taken back before starting.  Reverting a run that is already being reverted, has been, or was never approved answers `409`.  Requires the `imports.epd` permission — the permission that put these records in the dossiers in the first place. 
     * @summary Draai een eerdere EPD-levering terug
     * @param {string} runId The run to take back
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revertDumpImportRun(runId: string, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).revertDumpImportRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Opens a delivery whose archive is password-protected with the password the practice enters, without uploading it again.  Accepted only while the preview\'s `archivePasswordRequested` is true: the unpack ended on the protection, or on a password that did not open it. The password is stored encrypted with Cloud KMS, bound to this run, and removed when the unpack attempt it is entered for ends — whatever that attempt comes to. A wrong one ends the attempt with one finding and the run asks again; there is no limit on attempts.  `202` for the reason a re-parse answers one: what this request writes is the queue. The archive is unpacked by a worker over the minutes that follow, and the preview this answers with is the run as it now stands. Follow it with the preview endpoint.  Requires the `imports.epd` permission — the permission that could approve the run. 
     * @summary Open een met een wachtwoord beveiligde EPD-levering
     * @param {string} runId The run whose archive to open
     * @param {ImportArchivePasswordRequest} importArchivePasswordRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitDumpImportArchivePassword(runId: string, importArchivePasswordRequest: ImportArchivePasswordRequest, options?: RawAxiosRequestConfig) {
        return ImportRunsApiFp(this.configuration).submitDumpImportArchivePassword(runId, importArchivePasswordRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportSettingsApi - axios parameter creator
 */
export const ImportSettingsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every setting code this run\'s zorgtrajecten carry — one entry per distinct code, never per zorgtraject — how many zorgtrajecten carry it, and which Scrivio behandelsetting it is.  The zorgtrajecten whose delivery names **no** setting are an entry of their own, with no `sourceCode`: a ZorgAdmin delivery names none at all.  **No setting is guessed.** A code whose meaning is confirmed (`S01`, kwaliteitsstatuut sectie II) arrives answered and `AUTOMATIC`; every other code starts `UNRESOLVED` and refuses the run\'s approval until it is answered. The organisation\'s default kwaliteitsstatuut is not an answer.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De settingcodes van deze levering, en welke behandelsetting elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportSettings: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportSettings', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/settings`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers one setting code, and gives every staged zorgtraject carrying it that behandelsetting straight away — which is why the whole report comes back.  An answer — the automatic one included — can be replaced for as long as the run is awaiting approval. Once the run has been approved or rejected this answers `409`.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Kies de behandelsetting voor één settingcode
         * @param {string} runId The run the setting code belongs to
         * @param {ResolveImportSettingRequest} resolveImportSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportSetting: async (runId: string, resolveImportSettingRequest: ResolveImportSettingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('resolveImportSetting', 'runId', runId)
            // verify required parameter 'resolveImportSettingRequest' is not null or undefined
            assertParamExists('resolveImportSetting', 'resolveImportSettingRequest', resolveImportSettingRequest)
            const localVarPath = `/api/v1/imports/{runId}/settings/resolve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(resolveImportSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportSettingsApi - functional programming interface
 */
export const ImportSettingsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportSettingsApiAxiosParamCreator(configuration)
    return {
        /**
         * Every setting code this run\'s zorgtrajecten carry — one entry per distinct code, never per zorgtraject — how many zorgtrajecten carry it, and which Scrivio behandelsetting it is.  The zorgtrajecten whose delivery names **no** setting are an entry of their own, with no `sourceCode`: a ZorgAdmin delivery names none at all.  **No setting is guessed.** A code whose meaning is confirmed (`S01`, kwaliteitsstatuut sectie II) arrives answered and `AUTOMATIC`; every other code starts `UNRESOLVED` and refuses the run\'s approval until it is answered. The organisation\'s default kwaliteitsstatuut is not an answer.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De settingcodes van deze levering, en welke behandelsetting elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportSettings(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportSettingReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportSettings(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportSettingsApi.getImportSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers one setting code, and gives every staged zorgtraject carrying it that behandelsetting straight away — which is why the whole report comes back.  An answer — the automatic one included — can be replaced for as long as the run is awaiting approval. Once the run has been approved or rejected this answers `409`.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Kies de behandelsetting voor één settingcode
         * @param {string} runId The run the setting code belongs to
         * @param {ResolveImportSettingRequest} resolveImportSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveImportSetting(runId: string, resolveImportSettingRequest: ResolveImportSettingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportSettingReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveImportSetting(runId, resolveImportSettingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportSettingsApi.resolveImportSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportSettingsApi - factory interface
 */
export const ImportSettingsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportSettingsApiFp(configuration)
    return {
        /**
         * Every setting code this run\'s zorgtrajecten carry — one entry per distinct code, never per zorgtraject — how many zorgtrajecten carry it, and which Scrivio behandelsetting it is.  The zorgtrajecten whose delivery names **no** setting are an entry of their own, with no `sourceCode`: a ZorgAdmin delivery names none at all.  **No setting is guessed.** A code whose meaning is confirmed (`S01`, kwaliteitsstatuut sectie II) arrives answered and `AUTOMATIC`; every other code starts `UNRESOLVED` and refuses the run\'s approval until it is answered. The organisation\'s default kwaliteitsstatuut is not an answer.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De settingcodes van deze levering, en welke behandelsetting elk daarvan is
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportSettings(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportSettingReport> {
            return localVarFp.getImportSettings(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers one setting code, and gives every staged zorgtraject carrying it that behandelsetting straight away — which is why the whole report comes back.  An answer — the automatic one included — can be replaced for as long as the run is awaiting approval. Once the run has been approved or rejected this answers `409`.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Kies de behandelsetting voor één settingcode
         * @param {string} runId The run the setting code belongs to
         * @param {ResolveImportSettingRequest} resolveImportSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportSetting(runId: string, resolveImportSettingRequest: ResolveImportSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportSettingReport> {
            return localVarFp.resolveImportSetting(runId, resolveImportSettingRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportSettingsApi - object-oriented interface
 */
export class ImportSettingsApi extends BaseAPI {
    /**
     * Every setting code this run\'s zorgtrajecten carry — one entry per distinct code, never per zorgtraject — how many zorgtrajecten carry it, and which Scrivio behandelsetting it is.  The zorgtrajecten whose delivery names **no** setting are an entry of their own, with no `sourceCode`: a ZorgAdmin delivery names none at all.  **No setting is guessed.** A code whose meaning is confirmed (`S01`, kwaliteitsstatuut sectie II) arrives answered and `AUTOMATIC`; every other code starts `UNRESOLVED` and refuses the run\'s approval until it is answered. The organisation\'s default kwaliteitsstatuut is not an answer.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De settingcodes van deze levering, en welke behandelsetting elk daarvan is
     * @param {string} runId The run to read
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportSettings(runId: string, options?: RawAxiosRequestConfig) {
        return ImportSettingsApiFp(this.configuration).getImportSettings(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers one setting code, and gives every staged zorgtraject carrying it that behandelsetting straight away — which is why the whole report comes back.  An answer — the automatic one included — can be replaced for as long as the run is awaiting approval. Once the run has been approved or rejected this answers `409`.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Kies de behandelsetting voor één settingcode
     * @param {string} runId The run the setting code belongs to
     * @param {ResolveImportSettingRequest} resolveImportSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveImportSetting(runId: string, resolveImportSettingRequest: ResolveImportSettingRequest, options?: RawAxiosRequestConfig) {
        return ImportSettingsApiFp(this.configuration).resolveImportSetting(runId, resolveImportSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportTrajectoryOverlapsApi - axios parameter creator
 */
export const ImportTrajectoryOverlapsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every zorgtraject of this delivery that would close one the practice already has open, with the existing zorgtraject beside it and the practice\'s answer about it.  `uk_client_location_active_zorgtraject` allows one ACTIEF zorgtraject per cliënt, so an imported one closes whatever stands in its way — and **a revert does not undo that**: by the time one runs, the cliënt\'s own afspraken have been re-parented onto the imported zorgtraject, which is what makes that zorgtraject unremovable in the first place. On one rehearsal it happened 23 times and nothing anywhere said so (#4356).  So the overlap is a question, put here before anything is written. It is knowable while the delivery is being read: the staged zorgtraject names its cliënt, and that cliënt\'s active zorgtraject is one query away.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De zorgtrajecten van deze levering die een lopend zorgtraject zouden sluiten
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportTrajectoryOverlaps: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getImportTrajectoryOverlaps', 'runId', runId)
            const localVarPath = `/api/v1/imports/{runId}/trajectory-overlaps`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers one overlap and applies that answer straight away — which is why the whole report comes back: the counts and the remaining `unanswered` are what the practice reads next.  `SUPERSEDE` puts the staged zorgtraject back on the queue; applying it closes the existing one on the imported zorgtraject\'s start date and re-resolves the cliënt\'s afspraken onto the imported one, and **a revert undoes neither**. `SKIP` takes the staged zorgtraject out of the run with a Dutch reason on it and leaves the practice\'s own zorgtraject untouched; its afspraken, vragenlijsten and declaratieregels are still imported, without the link (#3880).  **Both directions are available for as long as the run is awaiting approval.** Answering the same zorgtraject again replaces the previous answer. Once the run has been approved or rejected the answers are history and this answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord één overlappend zorgtraject
         * @param {string} runId The run the zorgtraject belongs to
         * @param {ResolveImportTrajectoryOverlapRequest} resolveImportTrajectoryOverlapRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportTrajectoryOverlap: async (runId: string, resolveImportTrajectoryOverlapRequest: ResolveImportTrajectoryOverlapRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('resolveImportTrajectoryOverlap', 'runId', runId)
            // verify required parameter 'resolveImportTrajectoryOverlapRequest' is not null or undefined
            assertParamExists('resolveImportTrajectoryOverlap', 'resolveImportTrajectoryOverlapRequest', resolveImportTrajectoryOverlapRequest)
            const localVarPath = `/api/v1/imports/{runId}/trajectory-overlaps/resolve`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(resolveImportTrajectoryOverlapRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The same answer, given to every overlap nobody has answered yet — and the reason this endpoint exists rather than a default on the column.  A merge of a delivery this size overlaps in bulk: on a FIER-shaped one, 124 of 329 zorgtrajecten already existed. One question per row would re-create exactly the problem `acceptedSkips` was filed for (#4051), and a *default* would answer a clinical question without anybody reading it. So the list carries a sensible batch answer — normally `SUPERSEDE`, since a delivery is the practice\'s authoritative history — which the practice gives once, having been told what it means, and overrides per cliënt where they disagree.  Answers already given are left exactly as they are, in both directions: this only reaches the rows that are still unanswered. A row reported as a `heraanmelding` is not reached either: it stays unanswered, and so keeps refusing the approval, until it is answered on its own.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord in één keer elk overlappend zorgtraject dat nog openstaat
         * @param {string} runId The run whose remaining overlaps are being answered
         * @param {ResolveRemainingImportTrajectoryOverlapsRequest} resolveRemainingImportTrajectoryOverlapsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveRemainingImportTrajectoryOverlaps: async (runId: string, resolveRemainingImportTrajectoryOverlapsRequest: ResolveRemainingImportTrajectoryOverlapsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('resolveRemainingImportTrajectoryOverlaps', 'runId', runId)
            // verify required parameter 'resolveRemainingImportTrajectoryOverlapsRequest' is not null or undefined
            assertParamExists('resolveRemainingImportTrajectoryOverlaps', 'resolveRemainingImportTrajectoryOverlapsRequest', resolveRemainingImportTrajectoryOverlapsRequest)
            const localVarPath = `/api/v1/imports/{runId}/trajectory-overlaps/resolve-remaining`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(resolveRemainingImportTrajectoryOverlapsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportTrajectoryOverlapsApi - functional programming interface
 */
export const ImportTrajectoryOverlapsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportTrajectoryOverlapsApiAxiosParamCreator(configuration)
    return {
        /**
         * Every zorgtraject of this delivery that would close one the practice already has open, with the existing zorgtraject beside it and the practice\'s answer about it.  `uk_client_location_active_zorgtraject` allows one ACTIEF zorgtraject per cliënt, so an imported one closes whatever stands in its way — and **a revert does not undo that**: by the time one runs, the cliënt\'s own afspraken have been re-parented onto the imported zorgtraject, which is what makes that zorgtraject unremovable in the first place. On one rehearsal it happened 23 times and nothing anywhere said so (#4356).  So the overlap is a question, put here before anything is written. It is knowable while the delivery is being read: the staged zorgtraject names its cliënt, and that cliënt\'s active zorgtraject is one query away.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De zorgtrajecten van deze levering die een lopend zorgtraject zouden sluiten
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImportTrajectoryOverlaps(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportTrajectoryOverlapReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImportTrajectoryOverlaps(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportTrajectoryOverlapsApi.getImportTrajectoryOverlaps']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers one overlap and applies that answer straight away — which is why the whole report comes back: the counts and the remaining `unanswered` are what the practice reads next.  `SUPERSEDE` puts the staged zorgtraject back on the queue; applying it closes the existing one on the imported zorgtraject\'s start date and re-resolves the cliënt\'s afspraken onto the imported one, and **a revert undoes neither**. `SKIP` takes the staged zorgtraject out of the run with a Dutch reason on it and leaves the practice\'s own zorgtraject untouched; its afspraken, vragenlijsten and declaratieregels are still imported, without the link (#3880).  **Both directions are available for as long as the run is awaiting approval.** Answering the same zorgtraject again replaces the previous answer. Once the run has been approved or rejected the answers are history and this answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord één overlappend zorgtraject
         * @param {string} runId The run the zorgtraject belongs to
         * @param {ResolveImportTrajectoryOverlapRequest} resolveImportTrajectoryOverlapRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveImportTrajectoryOverlap(runId: string, resolveImportTrajectoryOverlapRequest: ResolveImportTrajectoryOverlapRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportTrajectoryOverlapReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveImportTrajectoryOverlap(runId, resolveImportTrajectoryOverlapRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportTrajectoryOverlapsApi.resolveImportTrajectoryOverlap']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The same answer, given to every overlap nobody has answered yet — and the reason this endpoint exists rather than a default on the column.  A merge of a delivery this size overlaps in bulk: on a FIER-shaped one, 124 of 329 zorgtrajecten already existed. One question per row would re-create exactly the problem `acceptedSkips` was filed for (#4051), and a *default* would answer a clinical question without anybody reading it. So the list carries a sensible batch answer — normally `SUPERSEDE`, since a delivery is the practice\'s authoritative history — which the practice gives once, having been told what it means, and overrides per cliënt where they disagree.  Answers already given are left exactly as they are, in both directions: this only reaches the rows that are still unanswered. A row reported as a `heraanmelding` is not reached either: it stays unanswered, and so keeps refusing the approval, until it is answered on its own.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord in één keer elk overlappend zorgtraject dat nog openstaat
         * @param {string} runId The run whose remaining overlaps are being answered
         * @param {ResolveRemainingImportTrajectoryOverlapsRequest} resolveRemainingImportTrajectoryOverlapsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveRemainingImportTrajectoryOverlaps(runId: string, resolveRemainingImportTrajectoryOverlapsRequest: ResolveRemainingImportTrajectoryOverlapsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportTrajectoryOverlapReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveRemainingImportTrajectoryOverlaps(runId, resolveRemainingImportTrajectoryOverlapsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportTrajectoryOverlapsApi.resolveRemainingImportTrajectoryOverlaps']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportTrajectoryOverlapsApi - factory interface
 */
export const ImportTrajectoryOverlapsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportTrajectoryOverlapsApiFp(configuration)
    return {
        /**
         * Every zorgtraject of this delivery that would close one the practice already has open, with the existing zorgtraject beside it and the practice\'s answer about it.  `uk_client_location_active_zorgtraject` allows one ACTIEF zorgtraject per cliënt, so an imported one closes whatever stands in its way — and **a revert does not undo that**: by the time one runs, the cliënt\'s own afspraken have been re-parented onto the imported zorgtraject, which is what makes that zorgtraject unremovable in the first place. On one rehearsal it happened 23 times and nothing anywhere said so (#4356).  So the overlap is a question, put here before anything is written. It is knowable while the delivery is being read: the staged zorgtraject names its cliënt, and that cliënt\'s active zorgtraject is one query away.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
         * @summary De zorgtrajecten van deze levering die een lopend zorgtraject zouden sluiten
         * @param {string} runId The run to read
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImportTrajectoryOverlaps(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<ImportTrajectoryOverlapReport> {
            return localVarFp.getImportTrajectoryOverlaps(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers one overlap and applies that answer straight away — which is why the whole report comes back: the counts and the remaining `unanswered` are what the practice reads next.  `SUPERSEDE` puts the staged zorgtraject back on the queue; applying it closes the existing one on the imported zorgtraject\'s start date and re-resolves the cliënt\'s afspraken onto the imported one, and **a revert undoes neither**. `SKIP` takes the staged zorgtraject out of the run with a Dutch reason on it and leaves the practice\'s own zorgtraject untouched; its afspraken, vragenlijsten and declaratieregels are still imported, without the link (#3880).  **Both directions are available for as long as the run is awaiting approval.** Answering the same zorgtraject again replaces the previous answer. Once the run has been approved or rejected the answers are history and this answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord één overlappend zorgtraject
         * @param {string} runId The run the zorgtraject belongs to
         * @param {ResolveImportTrajectoryOverlapRequest} resolveImportTrajectoryOverlapRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveImportTrajectoryOverlap(runId: string, resolveImportTrajectoryOverlapRequest: ResolveImportTrajectoryOverlapRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportTrajectoryOverlapReport> {
            return localVarFp.resolveImportTrajectoryOverlap(runId, resolveImportTrajectoryOverlapRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * The same answer, given to every overlap nobody has answered yet — and the reason this endpoint exists rather than a default on the column.  A merge of a delivery this size overlaps in bulk: on a FIER-shaped one, 124 of 329 zorgtrajecten already existed. One question per row would re-create exactly the problem `acceptedSkips` was filed for (#4051), and a *default* would answer a clinical question without anybody reading it. So the list carries a sensible batch answer — normally `SUPERSEDE`, since a delivery is the practice\'s authoritative history — which the practice gives once, having been told what it means, and overrides per cliënt where they disagree.  Answers already given are left exactly as they are, in both directions: this only reaches the rows that are still unanswered. A row reported as a `heraanmelding` is not reached either: it stays unanswered, and so keeps refusing the approval, until it is answered on its own.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
         * @summary Beantwoord in één keer elk overlappend zorgtraject dat nog openstaat
         * @param {string} runId The run whose remaining overlaps are being answered
         * @param {ResolveRemainingImportTrajectoryOverlapsRequest} resolveRemainingImportTrajectoryOverlapsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveRemainingImportTrajectoryOverlaps(runId: string, resolveRemainingImportTrajectoryOverlapsRequest: ResolveRemainingImportTrajectoryOverlapsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportTrajectoryOverlapReport> {
            return localVarFp.resolveRemainingImportTrajectoryOverlaps(runId, resolveRemainingImportTrajectoryOverlapsRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportTrajectoryOverlapsApi - object-oriented interface
 */
export class ImportTrajectoryOverlapsApi extends BaseAPI {
    /**
     * Every zorgtraject of this delivery that would close one the practice already has open, with the existing zorgtraject beside it and the practice\'s answer about it.  `uk_client_location_active_zorgtraject` allows one ACTIEF zorgtraject per cliënt, so an imported one closes whatever stands in its way — and **a revert does not undo that**: by the time one runs, the cliënt\'s own afspraken have been re-parented onto the imported zorgtraject, which is what makes that zorgtraject unremovable in the first place. On one rehearsal it happened 23 times and nothing anywhere said so (#4356).  So the overlap is a question, put here before anything is written. It is knowable while the delivery is being read: the staged zorgtraject names its cliënt, and that cliënt\'s active zorgtraject is one query away.  Requires the `imports.zpm`, `imports.agenda` or `imports.epd` permission. 
     * @summary De zorgtrajecten van deze levering die een lopend zorgtraject zouden sluiten
     * @param {string} runId The run to read
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImportTrajectoryOverlaps(runId: string, options?: RawAxiosRequestConfig) {
        return ImportTrajectoryOverlapsApiFp(this.configuration).getImportTrajectoryOverlaps(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers one overlap and applies that answer straight away — which is why the whole report comes back: the counts and the remaining `unanswered` are what the practice reads next.  `SUPERSEDE` puts the staged zorgtraject back on the queue; applying it closes the existing one on the imported zorgtraject\'s start date and re-resolves the cliënt\'s afspraken onto the imported one, and **a revert undoes neither**. `SKIP` takes the staged zorgtraject out of the run with a Dutch reason on it and leaves the practice\'s own zorgtraject untouched; its afspraken, vragenlijsten and declaratieregels are still imported, without the link (#3880).  **Both directions are available for as long as the run is awaiting approval.** Answering the same zorgtraject again replaces the previous answer. Once the run has been approved or rejected the answers are history and this answers `409` — the rows it would move are the apply worker\'s by then.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Beantwoord één overlappend zorgtraject
     * @param {string} runId The run the zorgtraject belongs to
     * @param {ResolveImportTrajectoryOverlapRequest} resolveImportTrajectoryOverlapRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveImportTrajectoryOverlap(runId: string, resolveImportTrajectoryOverlapRequest: ResolveImportTrajectoryOverlapRequest, options?: RawAxiosRequestConfig) {
        return ImportTrajectoryOverlapsApiFp(this.configuration).resolveImportTrajectoryOverlap(runId, resolveImportTrajectoryOverlapRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The same answer, given to every overlap nobody has answered yet — and the reason this endpoint exists rather than a default on the column.  A merge of a delivery this size overlaps in bulk: on a FIER-shaped one, 124 of 329 zorgtrajecten already existed. One question per row would re-create exactly the problem `acceptedSkips` was filed for (#4051), and a *default* would answer a clinical question without anybody reading it. So the list carries a sensible batch answer — normally `SUPERSEDE`, since a delivery is the practice\'s authoritative history — which the practice gives once, having been told what it means, and overrides per cliënt where they disagree.  Answers already given are left exactly as they are, in both directions: this only reaches the rows that are still unanswered. A row reported as a `heraanmelding` is not reached either: it stays unanswered, and so keeps refusing the approval, until it is answered on its own.  Requires the permission that would have committed this import: `imports.agenda.commit` for an agenda planning workbook, `imports.epd` for an EPD dump. 
     * @summary Beantwoord in één keer elk overlappend zorgtraject dat nog openstaat
     * @param {string} runId The run whose remaining overlaps are being answered
     * @param {ResolveRemainingImportTrajectoryOverlapsRequest} resolveRemainingImportTrajectoryOverlapsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveRemainingImportTrajectoryOverlaps(runId: string, resolveRemainingImportTrajectoryOverlapsRequest: ResolveRemainingImportTrajectoryOverlapsRequest, options?: RawAxiosRequestConfig) {
        return ImportTrajectoryOverlapsApiFp(this.configuration).resolveRemainingImportTrajectoryOverlaps(runId, resolveRemainingImportTrajectoryOverlapsRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportUploadsApi - axios parameter creator
 */
export const ImportUploadsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates an import run for the caller\'s own organization and returns a short-lived URL that authorises uploading its archive **directly to Cloud Storage**. The archive never passes through this API: an EPD export is one file of several gigabytes, well past both the multipart limit and what a request may carry.  The URL starts a **resumable** upload; it is not the URL the bytes go to. The exchange is:  1. `POST` to `uploadUrl` with an empty body and the header `x-goog-resumable: start`. That    header is part of what the URL is signed for — without it, or with a different value, the    request is refused. 2. Cloud Storage answers `201` with a session URI in the `Location` header. 3. `PUT` the archive to that session URI, in one request or in ranges. A transfer that is    interrupted is resumed against the same URI rather than started again, which is the whole    reason the upload is not a plain signed `PUT`.  `expiresAt` bounds step 1 only. The session opened in step 2 outlives it, so an upload that has started is not cut off mid-transfer.  The run comes back in the `AWAITING_APPROVAL` state and stays there: it has staged nothing, and nothing is written to any business table until it has been previewed and approved. A run whose upload never happens simply stays empty.  `vendor` is the practice\'s own answer to which EPD produced the archive, and it selects which recogniser is offered the delivery. It does **not** decide `source`: the run still opens at `EPD_DUMP`, and only a recogniser that has read the delivery narrows that. A mis-declared archive therefore fails with findings naming what was missing, rather than being parsed as something it is not.  Requires the `imports.epd` permission. 
         * @summary Open an import and get an upload URL for its archive
         * @param {InitiateImportUploadRequest} initiateImportUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        initiateImportUpload: async (initiateImportUploadRequest: InitiateImportUploadRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'initiateImportUploadRequest' is not null or undefined
            assertParamExists('initiateImportUpload', 'initiateImportUploadRequest', initiateImportUploadRequest)
            const localVarPath = `/api/v1/imports/initiate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(initiateImportUploadRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportUploadsApi - functional programming interface
 */
export const ImportUploadsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportUploadsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates an import run for the caller\'s own organization and returns a short-lived URL that authorises uploading its archive **directly to Cloud Storage**. The archive never passes through this API: an EPD export is one file of several gigabytes, well past both the multipart limit and what a request may carry.  The URL starts a **resumable** upload; it is not the URL the bytes go to. The exchange is:  1. `POST` to `uploadUrl` with an empty body and the header `x-goog-resumable: start`. That    header is part of what the URL is signed for — without it, or with a different value, the    request is refused. 2. Cloud Storage answers `201` with a session URI in the `Location` header. 3. `PUT` the archive to that session URI, in one request or in ranges. A transfer that is    interrupted is resumed against the same URI rather than started again, which is the whole    reason the upload is not a plain signed `PUT`.  `expiresAt` bounds step 1 only. The session opened in step 2 outlives it, so an upload that has started is not cut off mid-transfer.  The run comes back in the `AWAITING_APPROVAL` state and stays there: it has staged nothing, and nothing is written to any business table until it has been previewed and approved. A run whose upload never happens simply stays empty.  `vendor` is the practice\'s own answer to which EPD produced the archive, and it selects which recogniser is offered the delivery. It does **not** decide `source`: the run still opens at `EPD_DUMP`, and only a recogniser that has read the delivery narrows that. A mis-declared archive therefore fails with findings naming what was missing, rather than being parsed as something it is not.  Requires the `imports.epd` permission. 
         * @summary Open an import and get an upload URL for its archive
         * @param {InitiateImportUploadRequest} initiateImportUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async initiateImportUpload(initiateImportUploadRequest: InitiateImportUploadRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportUploadTicket>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.initiateImportUpload(initiateImportUploadRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportUploadsApi.initiateImportUpload']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportUploadsApi - factory interface
 */
export const ImportUploadsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportUploadsApiFp(configuration)
    return {
        /**
         * Creates an import run for the caller\'s own organization and returns a short-lived URL that authorises uploading its archive **directly to Cloud Storage**. The archive never passes through this API: an EPD export is one file of several gigabytes, well past both the multipart limit and what a request may carry.  The URL starts a **resumable** upload; it is not the URL the bytes go to. The exchange is:  1. `POST` to `uploadUrl` with an empty body and the header `x-goog-resumable: start`. That    header is part of what the URL is signed for — without it, or with a different value, the    request is refused. 2. Cloud Storage answers `201` with a session URI in the `Location` header. 3. `PUT` the archive to that session URI, in one request or in ranges. A transfer that is    interrupted is resumed against the same URI rather than started again, which is the whole    reason the upload is not a plain signed `PUT`.  `expiresAt` bounds step 1 only. The session opened in step 2 outlives it, so an upload that has started is not cut off mid-transfer.  The run comes back in the `AWAITING_APPROVAL` state and stays there: it has staged nothing, and nothing is written to any business table until it has been previewed and approved. A run whose upload never happens simply stays empty.  `vendor` is the practice\'s own answer to which EPD produced the archive, and it selects which recogniser is offered the delivery. It does **not** decide `source`: the run still opens at `EPD_DUMP`, and only a recogniser that has read the delivery narrows that. A mis-declared archive therefore fails with findings naming what was missing, rather than being parsed as something it is not.  Requires the `imports.epd` permission. 
         * @summary Open an import and get an upload URL for its archive
         * @param {InitiateImportUploadRequest} initiateImportUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        initiateImportUpload(initiateImportUploadRequest: InitiateImportUploadRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImportUploadTicket> {
            return localVarFp.initiateImportUpload(initiateImportUploadRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportUploadsApi - object-oriented interface
 */
export class ImportUploadsApi extends BaseAPI {
    /**
     * Creates an import run for the caller\'s own organization and returns a short-lived URL that authorises uploading its archive **directly to Cloud Storage**. The archive never passes through this API: an EPD export is one file of several gigabytes, well past both the multipart limit and what a request may carry.  The URL starts a **resumable** upload; it is not the URL the bytes go to. The exchange is:  1. `POST` to `uploadUrl` with an empty body and the header `x-goog-resumable: start`. That    header is part of what the URL is signed for — without it, or with a different value, the    request is refused. 2. Cloud Storage answers `201` with a session URI in the `Location` header. 3. `PUT` the archive to that session URI, in one request or in ranges. A transfer that is    interrupted is resumed against the same URI rather than started again, which is the whole    reason the upload is not a plain signed `PUT`.  `expiresAt` bounds step 1 only. The session opened in step 2 outlives it, so an upload that has started is not cut off mid-transfer.  The run comes back in the `AWAITING_APPROVAL` state and stays there: it has staged nothing, and nothing is written to any business table until it has been previewed and approved. A run whose upload never happens simply stays empty.  `vendor` is the practice\'s own answer to which EPD produced the archive, and it selects which recogniser is offered the delivery. It does **not** decide `source`: the run still opens at `EPD_DUMP`, and only a recogniser that has read the delivery narrows that. A mis-declared archive therefore fails with findings naming what was missing, rather than being parsed as something it is not.  Requires the `imports.epd` permission. 
     * @summary Open an import and get an upload URL for its archive
     * @param {InitiateImportUploadRequest} initiateImportUploadRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public initiateImportUpload(initiateImportUploadRequest: InitiateImportUploadRequest, options?: RawAxiosRequestConfig) {
        return ImportUploadsApiFp(this.configuration).initiateImportUpload(initiateImportUploadRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ImportsApi - axios parameter creator
 */
export const ImportsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  Requires the `imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitZpmImport: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('commitZpmImport', 'file', file)
            const localVarPath = `/api/v1/imports/zpm/commit`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to. The report is scoped to the caller\'s own organization.  Requires the `imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        validateZpmImport: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('validateZpmImport', 'file', file)
            const localVarPath = `/api/v1/imports/zpm/validate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ImportsApi - functional programming interface
 */
export const ImportsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ImportsApiAxiosParamCreator(configuration)
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  Requires the `imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async commitZpmImport(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZpmImportResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.commitZpmImport(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportsApi.commitZpmImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to. The report is scoped to the caller\'s own organization.  Requires the `imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async validateZpmImport(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZpmValidationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.validateZpmImport(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ImportsApi.validateZpmImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ImportsApi - factory interface
 */
export const ImportsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ImportsApiFp(configuration)
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  Requires the `imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        commitZpmImport(file: File, options?: RawAxiosRequestConfig): AxiosPromise<ZpmImportResult> {
            return localVarFp.commitZpmImport(file, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to. The report is scoped to the caller\'s own organization.  Requires the `imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        validateZpmImport(file: File, options?: RawAxiosRequestConfig): AxiosPromise<ZpmValidationReport> {
            return localVarFp.validateZpmImport(file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ImportsApi - object-oriented interface
 */
export class ImportsApi extends BaseAPI {
    /**
     * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the caller\'s own organization.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  Requires the `imports.zpm.commit` permission. 
     * @summary Commit a ZPM GGZ import workbook
     * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public commitZpmImport(file: File, options?: RawAxiosRequestConfig) {
        return ImportsApiFp(this.configuration).commitZpmImport(file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to. The report is scoped to the caller\'s own organization.  Requires the `imports.zpm` permission. 
     * @summary Validate a ZPM GGZ import workbook
     * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public validateZpmImport(file: File, options?: RawAxiosRequestConfig) {
        return ImportsApiFp(this.configuration).validateZpmImport(file, options).then((request) => request(this.axios, this.basePath));
    }
}



